import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useAnnoScolastico } from "@/lib/annoScolastico";
import AlunnoLogModal from "@/components/AlunnoLogModal";
import MailConfermaModal from "@/components/MailConfermaModal";
import MovimentiCollegatiModal from "@/components/MovimentiCollegatiModal";
import DieteManagerModal from "@/components/DieteManagerModal";
import AnnoAttivoSelector from "@/components/AnnoAttivoSelector";
import { openPrintWindow } from "@/lib/printWindow";
import * as XLSX from "xlsx";

const DAYS = ["LUN", "MAR", "MER", "GIO", "VEN"];
const DAY_NAMES = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì"];
const ICONS = { s: "🎓", x: "🎨", f: "👨‍👩‍👧" };
const COLORS = { s: "#2e7d32", x: "#0288d1", f: "#ef6c00" };
const BG_CELL = { s: "#c8e6c9", x: "#b3e5fc", f: "#ffe0b2" };
const LABELS = { s: "SCUOLA", x: "EXTRA", f: "SPOT" };

// Sfondo per le celle della matrice in base ai motivi (verde=scuola / azzurro=extra / arancio=famiglia)
const cellBg = (motivi) => {
  if (!motivi || motivi.length === 0) return "#fafafa";
  if (motivi.length === 1) return BG_CELL[motivi[0]];
  const colors = motivi.map(m => BG_CELL[m]);
  const step = 100 / colors.length;
  const stops = colors.map((c, i) => `${c} ${i * step}%, ${c} ${(i + 1) * step}%`).join(", ");
  return `linear-gradient(135deg, ${stops})`;
};

// Formatta l'anno scolastico come "YYYY/YY+1" (es. 2025 → "2025/26")
const fmtAnno = (a) => {
  const n = Number(a);
  if (!n) return "";
  return `${n}/${String(n + 1).slice(-2)}`;
};

export default function MensaMatricePage({ initialTab = "matrice" } = {}) {
  const [tab, setTab] = useState(initialTab);
  return (
    <div>
      <div style={{ background: "#f3e5f5", padding: "6px 20px 0", borderBottom: "1px solid #ce93d8", display: "flex", gap: 4, alignItems: "flex-end" }}>
        <TabBtn active={tab === "matrice"} onClick={() => setTab("matrice")} testid="mm-tab-matrice">📊 Matrice Mensa</TabBtn>
        <TabBtn active={tab === "intolleranze"} onClick={() => setTab("intolleranze")} testid="mm-tab-intoll">🍴 Intolleranze</TabBtn>
      </div>
      {tab === "matrice" && <MatriceView />}
      {tab === "intolleranze" && <IntolleranzeListView />}
      {tab === "plessi" && <PlessiConfigView />}
    </div>
  );
}

// 🆕 Feb 2026: vista "Intolleranze" — elenca tutti gli alunni con dieta valorizzata
// (senza griglia giorni). Riusa lo stesso endpoint /mensa-matrice/matrice.
function IntolleranzeListView() {
  const { annoCorrente } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [search, setSearch] = useState("");
  const [filterDieta, setFilterDieta] = useState("");
  const [filterSettore, setFilterSettore] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true); setErr(null);
      try {
        const params = {};
        if (annoCorrente) params.anno = annoCorrente;
        const r = await api.get("/mensa-matrice/matrice", { params });
        setData(r.data);
      } catch (e) {
        setErr(e.response?.data?.detail || "Errore caricamento");
      } finally { setLoading(false); }
    })();
  }, [annoCorrente]);

  const rows = useMemo(() => {
    const all = (data?.rows || []).filter(r => (r.dieta || "").trim());
    const s = search.trim().toLowerCase();
    return all.filter(r => {
      if (filterDieta && (r.dieta || "") !== filterDieta) return false;
      if (filterSettore && r.settore !== filterSettore) return false;
      if (s && !`${r.cognome} ${r.nome} ${r.cf}`.toLowerCase().includes(s)) return false;
      return true;
    }).sort((a, b) => (a.cognome || "").localeCompare(b.cognome || "", "it"));
  }, [data, search, filterDieta, filterSettore]);

  const dieteOpts = useMemo(() => Array.from(new Set((data?.rows || []).map(r => (r.dieta || "").trim()).filter(Boolean))).sort(), [data]);
  const settoriOpts = useMemo(() => Array.from(new Set((data?.rows || []).map(r => r.settore).filter(Boolean))).sort(), [data]);
  const perDieta = useMemo(() => {
    const m = {};
    for (const r of rows) {
      const d = r.dieta || "—";
      m[d] = (m[d] || 0) + 1;
    }
    return m;
  }, [rows]);

  return (
    <div>
      <div style={{ margin: "8px 20px", background: "white", border: "1px solid #ccc", padding: 10, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <AnnoAttivoSelector />
        <input placeholder="Cerca alunno..." value={search} onChange={e => setSearch(e.target.value)}
          data-testid="mm-intoll-search"
          style={{ padding: "5px 10px", border: "1px solid #ccc", fontSize: 12, width: 220, marginLeft: 8 }} />
        <select value={filterSettore} onChange={e => setFilterSettore(e.target.value)} data-testid="mm-intoll-filter-settore"
          style={{ padding: "5px 8px", border: "1px solid #ccc", fontSize: 12 }}>
          <option value="">Tutti i settori</option>
          {settoriOpts.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filterDieta} onChange={e => setFilterDieta(e.target.value)} data-testid="mm-intoll-filter-dieta"
          style={{ padding: "5px 8px", border: "1px solid #ccc", fontSize: 12 }}>
          <option value="">Tutte le diete</option>
          {dieteOpts.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <div style={{ marginLeft: "auto", fontSize: 12, color: "#444" }}>
          <b>{rows.length}</b> alunni · {Object.entries(perDieta).map(([k, v]) => `${k}: ${v}`).join(" · ") || "—"}
        </div>
      </div>

      {err && <div style={{ margin: "0 20px 8px", padding: 10, background: "#fff0f0", border: "1px solid #fcc", color: "#c00", fontSize: 12 }}>{err}</div>}

      <div style={{ margin: "0 20px 20px", background: "white", border: "1px solid #ccc", overflow: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }} data-testid="mm-intoll-table">
          <thead style={{ background: "#8e24aa", color: "white" }}>
            <tr>
              <th style={{ padding: "6px 8px", textAlign: "center", width: 30 }}>#</th>
              <th style={{ padding: "6px 8px", textAlign: "left", width: 60 }}>SETT.</th>
              <th style={{ padding: "6px 8px", textAlign: "left", width: 80 }}>CLASSE</th>
              <th style={{ padding: "6px 8px", textAlign: "left" }}>ALUNNO</th>
              <th style={{ padding: "6px 8px", textAlign: "left", width: 140 }}>C.F.</th>
              <th style={{ padding: "6px 8px", textAlign: "left", width: 200, background: "#ad1457" }}>🍴 DIETA</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} style={{ padding: 20, textAlign: "center", color: "#888" }}>Caricamento…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={6} style={{ padding: 30, textAlign: "center", color: "#888" }}>Nessun alunno con intolleranza registrata.</td></tr>}
            {rows.map((r, i) => (
              <tr key={r.cf || i} data-testid={`mm-intoll-row-${i}`} style={{ borderBottom: "1px solid #eee" }}>
                <td style={{ padding: "5px 8px", textAlign: "center", color: "#888", fontFamily: "monospace" }}>{i + 1}</td>
                <td style={{ padding: "5px 8px", fontWeight: 700 }}>{r.settore || "—"}</td>
                <td style={{ padding: "5px 8px", fontFamily: "monospace" }}>{r.classe || "—"}</td>
                <td style={{ padding: "5px 8px", fontWeight: 600, textTransform: "uppercase" }}>{r.cognome} {r.nome}</td>
                <td style={{ padding: "5px 8px", fontFamily: "monospace", fontSize: 10, color: "#666" }}>{r.cf || "—"}</td>
                <td style={{ padding: "5px 8px", fontWeight: 700, color: "#ad1457" }} data-testid={`mm-intoll-dieta-${i}`}>{r.dieta}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      style={{ padding: "8px 18px", border: "none", background: active ? "#8e24aa" : "white", color: active ? "white" : "#8e24aa",
        fontWeight: 700, fontSize: 12, cursor: "pointer", borderRadius: "4px 4px 0 0", borderTop: "1px solid #ccc", borderLeft: "1px solid #ccc", borderRight: "1px solid #ccc" }}>
      {children}
    </button>
  );
}

export function MatriceView() {
  const { annoCorrente } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [search, setSearch] = useState("");
  const [filterClasse, setFilterClasse] = useState("");
  const [filterSettore, setFilterSettore] = useState("");
  const [soloMensa, setSoloMensa] = useState(false);
  const [soloIntoll, setSoloIntoll] = useState(false);
  // 🆕 Diete inline: lista config + modale gestione
  const [dieteList, setDieteList] = useState([]);
  const [dieteModalOpen, setDieteModalOpen] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/mensa/giorno/diete");
        setDieteList(r.data?.items || []);
      } catch { /* ignore */ }
    })();
  }, []);
  const saveDieta = async (row, nuovaDieta) => {
    if ((row.dieta || "") === (nuovaDieta || "")) return;
    if (!row.cf) { setErr("Alunno senza codice fiscale, impossibile salvare la dieta"); return; }
    try {
      await api.put("/mensa-matrice/intolleranze/by-cf", {
        cf: row.cf,
        dieta: nuovaDieta || "",
        studente: `${row.cognome || ""} ${row.nome || ""}`.trim(),
        anno: data?.anno,  // 🆕 Feb 2026: salva legato all'anno corrente della matrice
      });
      // Aggiorna localmente (senza refetch completo). Reset del flag "prev_year" perché
      // il salvataggio è ora dell'anno corrente.
      setData(d => d ? { ...d, rows: (d.rows || []).map(r => r.cf === row.cf ? { ...r, dieta: nuovaDieta, dieta_prev_year: false } : r) } : d);
    } catch (e) {
      setErr(`Errore salvataggio dieta: ${e.response?.data?.detail || e.message}`);
    }
  };
  const [anni, setAnni] = useState([]);
  const [anno, setAnno] = useState(null);
  const [giornoSel, setGiornoSel] = useState(null); // null = settimana intera; 0-4 = singolo giorno

  const caricaAnni = async () => {
    try {
      const r = await api.get("/mensa-matrice/anni");
      setAnni(r.data.anni || []);
    } catch { /* ignore */ }
  };

  const carica = async (a) => {
    setLoading(true); setErr(null);
    try {
      const params = {};
      if (a != null) params.anno = a;
      const r = await api.get("/mensa-matrice/matrice", { params });
      setData(r.data);
      if (anno == null && r.data?.anno != null) setAnno(r.data.anno);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento");
    } finally { setLoading(false); }
  };
  useEffect(() => {
    caricaAnni();
    // Usa annoCorrente come default se disponibile, altrimenti lascia al backend la scelta
    carica(annoCorrente || undefined);
    if (annoCorrente) setAnno(annoCorrente);
    /* eslint-disable-next-line */
  }, [annoCorrente]);

  // Attiva/disattiva un giorno "scuola" di default per il singolo alunno (eccezione manuale).
  const toggleScuolaEscluso = async (r, j) => {
    try {
      const res = await api.post("/mensa-matrice/scuola-eccezione/toggle", {
        alunno_cod: r.alunno_cod, anno: data?.anno, giorno: j,
      });
      const esclusi = res.data.giorni_esclusi || [];
      setData(prev => {
        if (!prev) return prev;
        const rows = (prev.rows || []).map(row => {
          if (row.alunno_cod !== r.alunno_cod) return row;
          const plesso = new Set(row.plesso_giorni || []);
          const esclSet = new Set(esclusi);
          const cells = row.cells.map((m, idx) => {
            if (!plesso.has(idx)) return m;
            const others = m.filter(x => x !== "s");
            return esclSet.has(idx) ? others : ["s", ...others];
          });
          return { ...row, cells, scuola_esclusi: esclusi.filter(d => plesso.has(d)) };
        });
        return { ...prev, rows };
      });
    } catch (e) {
      alert(e.response?.data?.detail || "Errore aggiornamento eccezione mensa scuola");
    }
  };

  const classiOpts = useMemo(() => Array.from(new Set((data?.rows || []).map(r => r.classe).filter(Boolean))).sort(), [data]);
  const settoriOpts = useMemo(() => Array.from(new Set((data?.rows || []).map(r => r.settore).filter(Boolean))).sort(), [data]);

  const filtered = useMemo(() => {
    const rows = data?.rows || [];
    const s = search.trim().toLowerCase();
    return rows.filter(r => {
      if (filterClasse && r.classe !== filterClasse) return false;
      if (filterSettore && r.settore !== filterSettore) return false;
      if (giornoSel != null) {
        // In modalità singolo giorno: mostro solo chi ha almeno un motivo quel giorno
        if (!r.cells[giornoSel] || r.cells[giornoSel].length === 0) return false;
      } else if (soloMensa && !r.cells.some(c => c.length)) return false;
      if (soloIntoll && !(r.dieta || "").trim()) return false;
      if (s && !`${r.cognome} ${r.nome} ${r.cf}`.toLowerCase().includes(s)) return false;
      return true;
    });
  }, [data, search, filterClasse, filterSettore, soloMensa, soloIntoll, giornoSel]);

  // Totali per colonna (alunni con almeno un motivo quel giorno)
  const totCol = useMemo(() => {
    const t = [0, 0, 0, 0, 0];
    for (const r of filtered) {
      r.cells.forEach((motivi, i) => { if (motivi.length) t[i]++; });
    }
    return t;
  }, [filtered]);

  // 🆕 Giu 2026: raggruppa gli alunni del GIORNO selezionato per REFETTORIO (dato dal backend
  // in base a plesso/classe + gruppo giorno). Ordinati per nome refettorio; "Senza refettorio" in coda.
  const giornoRefGroups = useMemo(() => {
    if (giornoSel == null) return [];
    const map = new Map();
    for (const r of filtered) {
      const ref = (r.refettori && r.refettori[giornoSel]) || "";
      const key = ref || "— Senza refettorio —";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    const keys = Array.from(map.keys()).sort((a, b) => {
      const aNo = a === "— Senza refettorio —"; const bNo = b === "— Senza refettorio —";
      if (aNo && !bNo) return 1;
      if (bNo && !aNo) return -1;
      return a.localeCompare(b, "it");
    });
    return keys.map(k => ({
      refettorio: k,
      rows: map.get(k),
      intoll: map.get(k).filter(r => (r.dieta || "").trim()).length,
    }));
  }, [filtered, giornoSel]);

  // 🆕 Giu 2026: selezione dei refettori da includere nella stampa (vista giorno).
  const [selectedRefs, setSelectedRefs] = useState(new Set());
  const [expandedRefs, setExpandedRefs] = useState(new Set()); // vista giorno: refettori espansi (default: tutti compressi)
  const refNamesKey = giornoRefGroups.map(g => g.refettorio).join("|");
  useEffect(() => {
    // Di default seleziona tutti i refettori del giorno corrente e li mostra COMPRESSI (solo totali).
    setSelectedRefs(new Set(giornoRefGroups.map(g => g.refettorio)));
    setExpandedRefs(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [giornoSel, refNamesKey]);
  const toggleRef = (name) => setSelectedRefs(prev => {
    const n = new Set(prev);
    if (n.has(name)) n.delete(name); else n.add(name);
    return n;
  });
  const toggleExpand = (name) => setExpandedRefs(prev => {
    const n = new Set(prev);
    if (n.has(name)) n.delete(name); else n.add(name);
    return n;
  });


  const detailFor = (r, wd) => {
    const d = { scuola: false, extra: [], fam: null };
    if ((r.plesso_giorni || []).includes(wd)) d.scuola = true;
    for (const ex of (r.extra_details || [])) {
      if ((ex.giorni || []).includes(wd)) d.extra.push(ex);
    }
    if ((r.fam_giorni || []).includes(wd)) d.fam = { note: r.fam_note || "" };
    return d;
  };

  const stampaIntollRefettori = async () => {
    try {
      const r = await api.get("/mensa-matrice/intolleranti-refettori-pdf", {
        params: data?.anno ? { anno: data.anno } : {},
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const w = window.open(url, "_blank");
      if (!w) {
        const a = document.createElement("a");
        a.href = url;
        a.download = `intolleranti_refettori_${data?.anno || ""}.pdf`;
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      alert(e.response?.data?.detail || "Errore generazione PDF intolleranti per refettorio");
    }
  };

  const stampaMatrice = () => {
    if (filtered.length === 0) { alert("Nessun dato da stampare."); return; }
    const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const annoLabel = data?.anno ? `${data.anno}/${String(data.anno + 1).slice(-2)}` : "";
    const oggi = new Date().toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" });
    const filtriDesc = [
      filterSettore ? `Settore: ${filterSettore}` : null,
      filterClasse ? `Classe: ${filterClasse}` : null,
      search.trim() ? `Ricerca: "${search.trim()}"` : null,
      giornoSel == null && soloMensa ? "Solo chi va a mensa" : null,
      soloIntoll ? "Solo con intolleranze" : null,
    ].filter(Boolean).join(" · ");
    const cellLabels = (motivi) => motivi.map(m => LABELS[m]).join(" + ");
    const dayThead = `<tr><th>#</th><th>Sett.</th><th>Classe</th><th>Alunno</th><th>Dieta</th><th>Tipo</th><th>Dettaglio</th><th>Delegati al ritiro</th></tr>`;
    let titolo, mainHtml;
    if (giornoSel == null) {
      titolo = "Matrice Mensa — Settimana";
      const thead = `<tr><th>#</th><th>Sett.</th><th>Classe</th><th>Alunno</th>${DAY_NAMES.map(d => `<th>${d}</th>`).join("")}<th>Delegati al ritiro</th></tr>`;
      const tbody = filtered.map((r, i) => `<tr>
        <td class="num">${i + 1}</td>
        <td class="c strong">${esc(r.settore)}</td>
        <td class="mono">${esc(r.classe)}</td>
        <td class="strong">${esc(`${r.cognome || ""} ${r.nome || ""}`.trim())}</td>
        ${r.cells.map(m => `<td class="c day ${m.length ? ("m-" + m[0]) : ""}">${esc(cellLabels(m))}</td>`).join("")}
        <td class="deleg">${esc((r.delegati || []).join(" · "))}</td>
      </tr>`).join("");
      const tfoot = `<tr class="tot"><td colspan="4">TOTALE per giorno</td>${totCol.map(t => `<td class="c">${t}</td>`).join("")}<td></td></tr>`;
      mainHtml = `<table><thead>${thead}</thead><tbody>${tbody}</tbody><tfoot>${tfoot}</tfoot></table>`;
    } else {
      titolo = `Matrice Mensa — ${DAY_NAMES[giornoSel]} (per refettorio)`;
      // Un refettorio per pagina: solo i refettori SELEZIONATI (checkbox nella vista giorno).
      const grpsToPrint = giornoRefGroups.filter(g => selectedRefs.has(g.refettorio));
      if (grpsToPrint.length === 0) { alert("Seleziona almeno un refettorio da stampare (spunta la casella nell'intestazione del refettorio)."); return; }
      mainHtml = grpsToPrint.map((grp, gidx) => {
        const rowsHtml = grp.rows.map((r, k) => {
          const det = detailFor(r, giornoSel);
          const motivi = r.cells[giornoSel] || [];
          const parts = [];
          if (det.scuola) parts.push(`SCUOLA: Rientro pomeridiano (${esc(r.plesso || r.settore)})`);
          det.extra.forEach(ex => parts.push(`EXTRA: ${esc((ex.codice ? ex.codice + " " : "") + (ex.descrizione || ""))}`));
          if (det.fam) parts.push(`SPOT: ${esc(det.fam.note || "Richiesta mensa SPOT")}`);
          return `<tr>
            <td class="num">${k + 1}</td>
            <td class="c strong">${esc(r.settore)}</td>
            <td class="mono">${esc(r.classe)}</td>
            <td class="strong">${esc(`${r.cognome || ""} ${r.nome || ""}`.trim())}</td>
            <td class="dieta">${esc((r.dieta || "").toUpperCase())}</td>
            <td class="c">${esc(cellLabels(motivi))}</td>
            <td>${parts.join("<br/>")}</td>
            <td class="deleg">${esc((r.delegati || []).join(" · "))}</td>
          </tr>`;
        }).join("");
        const sub = `<tr class="refsub"><td colspan="8">Totale ${esc(grp.refettorio)}: ${grp.rows.length} alunni${grp.intoll > 0 ? ` · ${grp.intoll} con intolleranza` : ""}</td></tr>`;
        return `<section class="refpage${gidx > 0 ? " pb" : ""}">
          <div class="reftitle">🍽 ${esc(grp.refettorio)} — ${DAY_NAMES[giornoSel]} · ${grp.rows.length} alunni${grp.intoll > 0 ? ` · 🍴 ${grp.intoll} intoll.` : ""}</div>
          <table><thead>${dayThead}</thead><tbody>${rowsHtml}</tbody><tfoot>${sub}</tfoot></table>
        </section>`;
      }).join("");
    }
    const html = `<!doctype html><html lang="it"><head><meta charset="utf-8"><title>${esc(titolo)} · ${esc(annoLabel)}</title>
<style>
  @page { size: A4 landscape; margin: 10mm; }
  * { box-sizing: border-box; }
  body { margin: 0; font-family: Arial, Helvetica, sans-serif; color: #222; }
  .hdr { border-bottom: 2px solid #8e24aa; padding-bottom: 6px; margin-bottom: 8px; }
  .title { font-size: 17px; font-weight: 800; color: #6a1b9a; }
  .meta { margin-top: 3px; font-size: 12px; color: #1f3864; font-weight: 600; }
  .filters { margin-top: 2px; font-size: 11px; color: #555; }
  .legend { margin-top: 4px; font-size: 10px; }
  .legend span { padding: 1px 6px; border-radius: 2px; color: #fff; font-weight: 700; margin-right: 6px; }
  table { width: 100%; border-collapse: collapse; font-size: 10px; margin-top: 6px; }
  thead tr { background: #1f3864; color: #fff; }
  th, td { border: 1px solid #cfd8dc; padding: 3px 5px; text-align: left; }
  thead { display: table-header-group; }
  tr { page-break-inside: avoid; }
  td.c { text-align: center; }
  td.num { text-align: right; color: #666; width: 26px; }
  td.mono { font-family: Consolas, "Courier New", monospace; color: #555; }
  td.strong { font-weight: 700; }
  td.dieta { background: #fce4ec; color: #880e4f; font-weight: 700; text-align: center; }
  td.deleg { color: #1b5e20; font-weight: 700; font-size: 9px; }
  td.day { font-weight: 700; }
  td.m-s { background: #e8f5e9; color: #1b5e20; }
  td.m-x { background: #e1f5fe; color: #01579b; }
  td.m-f { background: #fff3e0; color: #e65100; }
  tr.tot td { background: #263238; color: #fff; font-weight: 800; text-align: center; }
  tr.refsub td { background: #f3e5f5; color: #6a1b9a; font-weight: 700; text-align: right; }
  section.refpage { margin-bottom: 6px; }
  section.pb { page-break-before: always; }
  .reftitle { margin-top: 8px; font-size: 14px; font-weight: 800; color: #4527a0; background: #ede7f6; padding: 6px 10px; border-left: 5px solid #7e57c2; }
  .ft { margin-top: 8px; font-size: 9px; color: #888; text-align: right; }
</style></head><body>
  <div class="hdr">
    <div class="title">📊 ${esc(titolo)}</div>
    <div class="meta">A.S. ${esc(annoLabel)} · ${filtered.length} alunni${filtriDesc ? "" : ""}</div>
    <div class="filters">${esc(filtriDesc || "Nessun filtro applicato")}</div>
    <div class="legend"><span style="background:#2e7d32">SCUOLA</span><span style="background:#0288d1">EXTRA</span><span style="background:#ef6c00">SPOT</span></div>
  </div>
  ${mainHtml}
  <div class="ft">canossaHUB · ${esc(titolo)} · A.S. ${esc(annoLabel)} · stampato il ${esc(oggi)}</div>
  <script>window.onload=function(){setTimeout(function(){window.print();},250);};<\/script>
</body></html>`;
    const w = window.open("", "_blank");
    if (!w) { alert("Popup bloccato dal browser. Abilita i popup per stampare."); return; }
    w.document.open(); w.document.write(html); w.document.close();
  };

  const stampaDoppie = () => {
    const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const annoLabel = data?.anno ? `${data.anno}/${String(data.anno + 1).slice(-2)}` : "";
    const oggi = new Date().toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" });
    // Raccogli alunno/giorno con 2+ motivi (es. EXTRA + SPOT lo stesso giorno)
    const righe = [];
    for (const r of filtered) {
      r.cells.forEach((motivi, wd) => {
        if ((motivi || []).length >= 2) {
          const det = detailFor(r, wd);
          const parts = [];
          if (det.scuola) parts.push(`SCUOLA (${esc(r.plesso || r.settore)})`);
          det.extra.forEach(ex => parts.push(`EXTRA: ${esc((ex.codice ? ex.codice + " " : "") + (ex.descrizione || ""))}`));
          if (det.fam) parts.push(`SPOT: ${esc(det.fam.note || "Richiesta mensa SPOT")}`);
          righe.push({ r, wd, motivi, parts });
        }
      });
    }
    if (righe.length === 0) { alert("Nessun alunno con due prenotazioni nello stesso giorno (con i filtri attuali)."); return; }
    const tbody = righe.map((x, i) => `<tr>
      <td class="num">${i + 1}</td>
      <td class="c strong">${esc(x.r.settore)}</td>
      <td class="mono">${esc(x.r.classe)}</td>
      <td class="strong">${esc(`${x.r.cognome || ""} ${x.r.nome || ""}`.trim())}</td>
      <td class="c strong" style="background:#ede7f6;color:#4527a0">${esc(DAY_NAMES[x.wd])}</td>
      <td class="c">${esc(x.motivi.map(m => LABELS[m]).join(" + "))}</td>
      <td>${x.parts.join("<br/>")}</td>
    </tr>`).join("");
    const html = `<!doctype html><html lang="it"><head><meta charset="utf-8"><title>Doppie prenotazioni · ${esc(annoLabel)}</title>
<style>
  @page { size: A4 landscape; margin: 10mm; }
  body { margin: 0; font-family: Arial, Helvetica, sans-serif; color: #222; }
  .hdr { border-bottom: 2px solid #5e35b1; padding-bottom: 6px; margin-bottom: 8px; }
  .title { font-size: 17px; font-weight: 800; color: #4527a0; }
  .meta { margin-top: 3px; font-size: 12px; color: #1f3864; font-weight: 600; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; margin-top: 6px; }
  thead tr { background: #4527a0; color: #fff; }
  th, td { border: 1px solid #cfd8dc; padding: 4px 6px; text-align: left; }
  thead { display: table-header-group; }
  tr { page-break-inside: avoid; }
  td.c { text-align: center; }
  td.num { text-align: right; color: #666; width: 26px; }
  td.mono { font-family: Consolas, "Courier New", monospace; color: #555; }
  td.strong { font-weight: 700; }
  .ft { margin-top: 8px; font-size: 9px; color: #888; text-align: right; }
</style></head><body>
  <div class="hdr">
    <div class="title">⚠ Doppie prenotazioni mensa (stesso giorno)</div>
    <div class="meta">A.S. ${esc(annoLabel)} · ${righe.length} casi (alunno + giorno con 2+ prenotazioni, es. EXTRA + SPOT)</div>
  </div>
  <table><thead><tr><th>#</th><th>Sett.</th><th>Classe</th><th>Alunno</th><th>Giorno</th><th>Prenotazioni</th><th>Dettaglio</th></tr></thead><tbody>${tbody}</tbody></table>
  <div class="ft">canossaHUB · Doppie prenotazioni · A.S. ${esc(annoLabel)} · stampato il ${esc(oggi)}</div>
  <script>window.onload=function(){setTimeout(function(){window.print();},250);};<\/script>
</body></html>`;
    const w = window.open("", "_blank");
    if (!w) { alert("Popup bloccato dal browser. Abilita i popup per stampare."); return; }
    w.document.open(); w.document.write(html); w.document.close();
  };


  return (
    <div>
      <div style={{ margin: "8px 20px", background: "white", border: "1px solid #ccc", padding: 10, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        {/* 🆕 Selettore globale AS al primo posto (feb 2026) */}
        <AnnoAttivoSelector />
        {/* Selettore giorno */}
        <div style={{ display: "flex", gap: 2, marginLeft: 6 }}>
          <button onClick={() => setGiornoSel(null)} data-testid="mm-day-all"
            style={{ padding: "5px 10px", fontSize: 11, fontWeight: 700, border: "1px solid #8e24aa",
              background: giornoSel == null ? "#8e24aa" : "white", color: giornoSel == null ? "white" : "#8e24aa", cursor: "pointer" }}>
            Settimana
          </button>
          {DAYS.map((d, i) => (
            <button key={i} onClick={() => setGiornoSel(i)} data-testid={`mm-day-${i}`}
              style={{ padding: "5px 10px", fontSize: 11, fontWeight: 700, border: "1px solid #8e24aa",
                background: giornoSel === i ? "#8e24aa" : "white", color: giornoSel === i ? "white" : "#8e24aa", cursor: "pointer" }}>
              {d}{totCol[i] > 0 ? ` (${totCol[i]})` : ""}
            </button>
          ))}
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 6, alignItems: "center" }}>
          {giornoSel == null && (
            <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, cursor: "pointer", padding: "4px 10px", border: "1px solid #ccc", background: soloMensa ? "#e8f5e9" : "white" }}>
              <input type="checkbox" checked={soloMensa} onChange={e => setSoloMensa(e.target.checked)} data-testid="mm-solo-mensa" />
              Solo chi va a mensa
            </label>
          )}
          <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, cursor: "pointer", padding: "4px 10px", border: "1px solid #ef6c00", background: soloIntoll ? "#fff3e0" : "white", color: soloIntoll ? "#ef6c00" : "#333", fontWeight: soloIntoll ? 700 : 400 }}
              title="Mostra solo gli alunni con una dieta/intolleranza registrata">
            <input type="checkbox" checked={soloIntoll} onChange={e => setSoloIntoll(e.target.checked)} data-testid="mm-solo-intoll" />
            🍴 Solo con intolleranze
          </label>
          <input placeholder="Cerca alunno..." value={search} onChange={e => setSearch(e.target.value)}
            data-testid="mm-search"
            style={{ padding: "5px 10px", border: "1px solid #ccc", fontSize: 12, width: 200 }} />
          <select value={filterSettore} onChange={e => setFilterSettore(e.target.value)} data-testid="mm-filter-settore"
            style={{ padding: "5px 8px", border: "1px solid #ccc", fontSize: 12 }}>
            <option value="">Tutti i settori</option>
            {settoriOpts.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filterClasse} onChange={e => setFilterClasse(e.target.value)} data-testid="mm-filter-classe"
            style={{ padding: "5px 8px", border: "1px solid #ccc", fontSize: 12 }}>
            <option value="">Tutte le classi</option>
            {classiOpts.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <button onClick={() => carica(anno)} data-testid="mm-refresh"
            style={{ padding: "5px 12px", background: "#8e24aa", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
            🔄 Aggiorna
          </button>
          <button onClick={stampaMatrice} data-testid="mm-print"
            title="Stampa la matrice. In vista giorno stampa solo i refettori selezionati (una pagina per refettorio)."
            style={{ padding: "5px 12px", background: "#6a1b9a", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
            🖨 Stampa matrice
          </button>
          <button onClick={stampaDoppie} data-testid="mm-print-doppie"
            title="Stampa chi ha due prenotazioni nello stesso giorno (es. EXTRA + SPOT)"
            style={{ padding: "5px 12px", background: "#4527a0", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
            ⚠ Doppie prenotazioni
          </button>
          <button onClick={stampaIntollRefettori} data-testid="mm-print-intoll-refettori"
            title="Stampa gli alunni con intolleranza divisi per refettorio, per Gruppo 1 (Lun/Mer/Ven) e Gruppo 2 (Mar/Gio). Dati dalla Matrice."
            style={{ padding: "5px 12px", background: "#c62828", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
            🍽 Stampa intolleranti per refettorio
          </button>
        </div>
      </div>

      <div style={{ margin: "0 20px 8px", padding: "6px 10px", background: "#f3e5f5", fontSize: 11, display: "flex", gap: 18, alignItems: "center" }}>
        <LegendItem k="s" /> <LegendItem k="x" /> <LegendItem k="f" />
        {data?.refettori_fonte && (
          <span data-testid="mm-refettori-version" style={{ marginLeft: "auto", padding: "2px 8px", background: "#ede7f6", border: "1px solid #7e57c2", borderRadius: 4, color: "#4527a0", fontWeight: 700 }}
            title="Versione della lista refettori usata per dividere gli alunni per refettorio (vista giorno)">
            🍽 Refettori: {data.refettori_fonte === "tabella_import" ? "Tabella import (legacy)" : (data.refettori_versione ? `Dal ${data.refettori_versione.split("-").reverse().join("/")}` : "Attuale (base)")}
          </span>
        )}
      </div>

      {giornoSel != null && giornoRefGroups.length > 0 && (
        <div style={{ margin: "0 20px 8px", padding: "6px 10px", background: "#ede7f6", border: "1px solid #d1c4e9", borderRadius: 4, fontSize: 11, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <b style={{ color: "#4527a0" }}>🖨 Refettori da stampare:</b>
          <span data-testid="mm-ref-sel-count" style={{ background: "#4527a0", color: "white", padding: "1px 8px", borderRadius: 10, fontWeight: 700 }}>
            {selectedRefs.size}/{giornoRefGroups.length} selezionati
          </span>
          <button onClick={() => setSelectedRefs(new Set(giornoRefGroups.map(g => g.refettorio)))} data-testid="mm-ref-sel-all"
            style={{ padding: "3px 10px", background: "white", color: "#6a1b9a", border: "1px solid #6a1b9a", borderRadius: 3, fontWeight: 700, cursor: "pointer" }}>
            ✓ Tutti
          </button>
          <button onClick={() => setSelectedRefs(new Set())} data-testid="mm-ref-sel-none"
            style={{ padding: "3px 10px", background: "white", color: "#6a1b9a", border: "1px solid #6a1b9a", borderRadius: 3, fontWeight: 700, cursor: "pointer" }}>
            ✗ Nessuno
          </button>
          <span style={{ borderLeft: "1px solid #b39ddb", height: 16, margin: "0 2px" }} />
          <button onClick={() => setExpandedRefs(new Set(giornoRefGroups.map(g => g.refettorio)))} data-testid="mm-ref-expand-all"
            title="Mostra l'elenco alunni di tutti i refettori"
            style={{ padding: "3px 10px", background: "white", color: "#00695c", border: "1px solid #00695c", borderRadius: 3, fontWeight: 700, cursor: "pointer" }}>
            ▼ Espandi tutti
          </button>
          <button onClick={() => setExpandedRefs(new Set())} data-testid="mm-ref-collapse-all"
            title="Mostra solo i totali (nascondi gli elenchi)"
            style={{ padding: "3px 10px", background: "white", color: "#00695c", border: "1px solid #00695c", borderRadius: 3, fontWeight: 700, cursor: "pointer" }}>
            ▶ Comprimi tutti
          </button>
          <span style={{ color: "#7e57c2" }}>Clicca un refettorio per aprire/chiudere l'elenco alunni. Spunta i refettori da stampare, poi <b>🖨 Stampa matrice</b>.</span>
        </div>
      )}

      {err && <div style={{ margin: "0 20px 8px", padding: 10, background: "#fff0f0", border: "1px solid #fcc", color: "#c00", fontSize: 12 }}>{err}</div>}

      <div style={{ margin: "0 20px 20px", background: "white", border: "1px solid #ccc" }}>
        <div style={{ maxHeight: "70vh", overflow: "auto" }}>
          {giornoSel == null ? (
            <table className="sw-table sw-table-compact" style={{ fontSize: 11, width: "100%" }}>
              <thead style={{ position: "sticky", top: 0, background: "#1f3864", color: "white", zIndex: 2 }}>
                <tr>
                  <th style={{ width: 40, textAlign: "center" }}>#</th>
                  <th style={{ width: 70, textAlign: "center" }}>SETTORE</th>
                  <th style={{ width: 80 }}>CLASSE</th>
                  <th style={{ minWidth: 220 }}>ALUNNO</th>
                  <th style={{ width: 110, textAlign: "center", background: "#ad1457" }}>🍴 DIETA</th>
                  {DAYS.map(d => <th key={d} style={{ width: 80, textAlign: "center" }}>{d}</th>)}
                  <th style={{ width: 50, textAlign: "center", background: "#2e7d32", color: "white" }}>TOT</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={10} style={{ padding: 30, textAlign: "center", color: "#888" }}>Caricamento...</td></tr>}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={10} style={{ padding: 30, textAlign: "center", color: "#888" }}>
                    Nessun alunno a mensa. Configura prima <b>Plessi pomeriggio</b>, <b>Iscrizioni Extra</b> o <b>Richieste Famiglie</b>.
                  </td></tr>
                )}
                {!loading && (() => {
                  const out = [];
                  let curSet = null;
                  let curCls = null;
                  let setStart = 0;
                  let clsStart = 0;
                  const sumRange = (start, end) => {
                    const cellSums = [0, 0, 0, 0, 0];
                    let totSum = 0;
                    for (let k = start; k < end; k++) {
                      const r = filtered[k];
                      let rowTot = 0;
                      r.cells.forEach((m, j) => { if (m.length) { cellSums[j] += 1; rowTot += 1; } });
                      totSum += rowTot;
                    }
                    return { cellSums, totSum, count: end - start };
                  };
                  const flushClasse = (endIdx) => {
                    if (curCls == null) return;
                    const { cellSums, totSum, count } = sumRange(clsStart, endIdx);
                    out.push(
                      <tr key={`cs-${clsStart}`} style={{ background: "#fff8e1", fontWeight: 700, color: "#8d6e00", borderTop: "1px solid #ffd54f" }}>
                        <td colSpan={3} style={{ textAlign: "right", padding: "4px 8px", fontSize: 11 }}>Subtotale classe {curCls}</td>
                        <td style={{ textAlign: "right", fontSize: 11 }}>{count} alunni</td>
                        <td></td>
                        {cellSums.map((n, j) => <td key={j} style={{ textAlign: "center", fontFamily: "Consolas, monospace" }}>{n || ""}</td>)}
                        <td style={{ textAlign: "center", fontFamily: "Consolas, monospace", background: "#ffe082" }}>{totSum}</td>
                      </tr>
                    );
                  };
                  const flushSettore = (endIdx) => {
                    if (curSet == null) return;
                    const { cellSums, totSum, count } = sumRange(setStart, endIdx);
                    out.push(
                      <tr key={`ss-${setStart}`} style={{ background: "#e3f2fd", fontWeight: 800, color: "#0d47a1", borderTop: "2px solid #1565c0", borderBottom: "2px solid #1565c0" }}>
                        <td colSpan={3} style={{ textAlign: "right", padding: "5px 8px", fontSize: 12 }}>SUBTOTALE SETTORE {curSet}</td>
                        <td style={{ textAlign: "right", fontSize: 12 }}>{count} alunni</td>
                        <td></td>
                        {cellSums.map((n, j) => <td key={j} style={{ textAlign: "center", fontFamily: "Consolas, monospace", fontSize: 13 }}>{n || ""}</td>)}
                        <td style={{ textAlign: "center", fontFamily: "Consolas, monospace", fontSize: 13, background: "#bbdefb" }}>{totSum}</td>
                      </tr>
                    );
                  };
                  filtered.forEach((r, i) => {
                    if (curSet !== r.settore) {
                      flushClasse(i);
                      flushSettore(i);
                      curSet = r.settore;
                      setStart = i;
                      curCls = null;
                    }
                    if (curCls !== r.classe) {
                      flushClasse(i);
                      curCls = r.classe;
                      clsStart = i;
                    }
                    const tot = r.cells.reduce((a, c) => a + (c.length ? 1 : 0), 0);
                    out.push(
                      <tr key={r.alunno_cod} data-testid={`mm-row-${i}`}>
                        <td style={{ textAlign: "center", color: "#888", fontFamily: "Consolas, monospace" }}>{i + 1}</td>
                        <td style={{ textAlign: "center", fontWeight: 700 }}>{r.settore}</td>
                        <td>{r.classe}</td>
                        <td style={{ fontWeight: 600 }}>{r.cognome} {r.nome}</td>
                        <td style={{ textAlign: "center", background: r.dieta ? (r.dieta_prev_year ? "#fff3e0" : "#fce4ec") : "transparent", fontSize: 10, padding: "2px 4px" }}
                            title={r.dieta_prev_year ? `⚠ Dieta dell'anno scolastico precedente (${(data?.anno ?? 0) - 1}/${String((data?.anno ?? 0)).slice(-2)}) — Certificato non ancora rinnovato per l'anno corrente` : undefined}>
                          <div style={{ display: "flex", gap: 2, alignItems: "center" }}>
                            <select value={r.dieta || ""} onChange={e => saveDieta(r, e.target.value)}
                              data-testid={`mm-mat-dieta-${r.cf || r.alunno_cod}`}
                              disabled={!r.cf}
                              title={r.cf ? "Cambia dieta/intolleranza inline" : "CF mancante, impossibile modificare"}
                              style={{ flex: 1, padding: "2px 3px", fontSize: 10, fontWeight: r.dieta ? 700 : 400,
                                       color: r.dieta ? "white" : "#888",
                                       border: r.dieta ? (r.dieta_prev_year ? "1px solid #ef6c00" : "1px solid #ad1457") : "1px solid #ccc",
                                       background: r.dieta ? (r.dieta_prev_year ? "#ef6c00" : "#ad1457") : "white",
                                       borderRadius: 2, cursor: r.cf ? "pointer" : "not-allowed" }}>
                              <option value="" style={{ background: "white", color: "#333" }}>— nessuna —</option>
                              {dieteList.map(d => <option key={d} value={d} style={{ background: "white", color: "#333" }}>{d}</option>)}
                              {r.dieta && !dieteList.includes(r.dieta) && (
                                <option value={r.dieta} style={{ background: "white", color: "#333" }}>{r.dieta} (custom)</option>
                              )}
                            </select>
                            {r.dieta_prev_year && (
                              <span data-testid={`mm-mat-prev-badge-${r.cf}`}
                                title="Dieta dell'anno precedente — certificato non ancora aggiornato"
                                style={{ background: "#ef6c00", color: "white", fontSize: 8, fontWeight: 900, padding: "1px 3px", borderRadius: 2, whiteSpace: "nowrap" }}>
                                ⚠ {String((data?.anno ?? 0) - 1).slice(-2)}/{String(data?.anno ?? 0).slice(-2)}
                              </span>
                            )}
                            <button onClick={() => setDieteModalOpen(true)} title="Gestisci elenco diete"
                              data-testid={`mm-mat-dieta-mng-${r.cf || r.alunno_cod}`}
                              style={{ background: "#37474f", color: "white", border: "none", padding: "1px 3px", fontSize: 9, cursor: "pointer", borderRadius: 2 }}>⚙</button>
                          </div>
                        </td>
                        {r.cells.map((motivi, j) => {
                          const isScuolaDay = (r.plesso_giorni || []).includes(j);
                          const escluso = (r.scuola_esclusi || []).includes(j);
                          return (
                          <td key={j}
                            onClick={isScuolaDay ? () => toggleScuolaEscluso(r, j) : undefined}
                            data-testid={isScuolaDay ? `mm-cell-scuola-${r.alunno_cod}-${j}` : undefined}
                            title={isScuolaDay ? (escluso ? "Escluso manualmente dalla mensa scuola — clic per riattivare" : "A mensa scuola — clic per escludere questo giorno") : undefined}
                            style={{ textAlign: "center", background: cellBg(motivi), padding: "3px 4px", cursor: isScuolaDay ? "pointer" : "default" }}>
                            {escluso && (
                              <span title="Escluso manualmente dalla mensa scuola"
                                style={{ color: "#9e9e9e", fontWeight: 700, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.3, textDecoration: "line-through", marginRight: motivi.length ? 4 : 0 }}>
                                {LABELS.s}
                              </span>
                            )}
                            {motivi.map((m, k) => (
                              <span key={m} style={{ color: COLORS[m], fontWeight: 700, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.3, marginRight: k < motivi.length - 1 ? 4 : 0 }}>
                                {LABELS[m]}
                              </span>
                            ))}
                          </td>
                          );
                        })}
                        <td style={{ textAlign: "center", background: "#e8f5e9", fontWeight: 700, fontFamily: "Consolas, monospace" }}>{tot}</td>
                      </tr>
                    );
                  });
                  flushClasse(filtered.length);
                  flushSettore(filtered.length);
                  return out;
                })()}
              </tbody>
              {filtered.length > 0 && (
                <tfoot>
                  <tr style={{ background: "#263238", color: "white", fontWeight: 800 }}>
                    <td colSpan={4} style={{ textAlign: "right", padding: "6px 8px" }}>TOTALE A MENSA</td>
                    <td></td>
                    {totCol.map((n, i) => <td key={i} style={{ textAlign: "center", fontFamily: "Consolas, monospace", fontSize: 13 }}>{n}</td>)}
                    <td style={{ textAlign: "center", background: "#1b5e20", fontFamily: "Consolas, monospace", fontSize: 13 }}>{filtered.length}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          ) : (
            <table className="sw-table sw-table-compact" style={{ fontSize: 11, width: "100%" }}>
              <thead style={{ position: "sticky", top: 0, background: "#1f3864", color: "white", zIndex: 2 }}>
                <tr>
                  <th style={{ width: 40, textAlign: "center" }}>#</th>
                  <th style={{ width: 70, textAlign: "center" }}>SETTORE</th>
                  <th style={{ width: 80 }}>CLASSE</th>
                  <th style={{ minWidth: 220 }}>ALUNNO</th>
                  <th style={{ width: 110, textAlign: "center", background: "#ad1457" }}>🍴 DIETA</th>
                  <th style={{ width: 110, textAlign: "center" }}>MOTIVI</th>
                  <th style={{ minWidth: 320 }}>DETTAGLIO</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={7} style={{ padding: 30, textAlign: "center", color: "#888" }}>Caricamento...</td></tr>}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={7} style={{ padding: 30, textAlign: "center", color: "#888" }}>
                    Nessun alunno potenzialmente in mensa {DAY_NAMES[giornoSel]}.
                  </td></tr>
                )}
                {!loading && (() => {
                  const out = [];
                  let gi = 0;
                  for (const grp of giornoRefGroups) {
                    out.push(
                      <tr key={`rh-${grp.refettorio}`} style={{ background: "#ede7f6", color: "#4527a0", fontWeight: 800, borderTop: "2px solid #7e57c2", cursor: "pointer" }}
                        onClick={() => toggleExpand(grp.refettorio)}>
                        <td colSpan={7} style={{ padding: "6px 10px", fontSize: 12 }} data-testid={`mm-day-ref-hdr-${grp.refettorio}`}>
                          <span data-testid={`mm-ref-expand-${grp.refettorio}`} style={{ display: "inline-block", width: 14, color: "#7e57c2" }}>{expandedRefs.has(grp.refettorio) ? "▼" : "▶"}</span>
                          <label style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }} title="Includi questo refettorio nella stampa" onClick={(e) => e.stopPropagation()}>
                            <input type="checkbox" checked={selectedRefs.has(grp.refettorio)} onChange={() => toggleRef(grp.refettorio)}
                              data-testid={`mm-ref-check-${grp.refettorio}`} style={{ width: 15, height: 15, cursor: "pointer" }} />
                            🍽 {grp.refettorio}
                          </label>
                          <span style={{ marginLeft: 10, background: "#4527a0", color: "white", padding: "1px 8px", borderRadius: 10, fontSize: 11 }}>{grp.rows.length} alunni</span>
                          {grp.intoll > 0 && (
                            <span style={{ marginLeft: 6, background: "#ad1457", color: "white", padding: "1px 8px", borderRadius: 10, fontSize: 11 }}>🍴 {grp.intoll} intoll.</span>
                          )}
                          <span style={{ marginLeft: 10, fontWeight: 600, color: "#7e57c2", fontSize: 10 }}>{expandedRefs.has(grp.refettorio) ? "(clicca per comprimere)" : "(clicca per vedere l'elenco)"}</span>
                        </td>
                      </tr>
                    );
                    if (!expandedRefs.has(grp.refettorio)) { continue; }
                    grp.rows.forEach((r, k) => {
                      const i = gi++;
                      const det = detailFor(r, giornoSel);
                      const motivi = r.cells[giornoSel] || [];
                      out.push(
                        <tr key={r.alunno_cod} data-testid={`mm-day-row-${i}`}>
                      <td style={{ textAlign: "center", color: "#888", fontFamily: "Consolas, monospace" }}>{k + 1}</td>
                      <td style={{ textAlign: "center", fontWeight: 700 }}>{r.settore}</td>
                      <td>{r.classe}</td>
                      <td style={{ fontWeight: 600 }}>{r.cognome} {r.nome}</td>
                      <td style={{ textAlign: "center", background: r.dieta ? (r.dieta_prev_year ? "#fff3e0" : "#fce4ec") : "transparent", fontSize: 10, padding: "2px 4px" }}
                          title={r.dieta_prev_year ? `⚠ Dieta dell'anno scolastico precedente (${(data?.anno ?? 0) - 1}/${String((data?.anno ?? 0)).slice(-2)}) — Certificato non ancora rinnovato per l'anno corrente` : undefined}>
                        <div style={{ display: "flex", gap: 2, alignItems: "center" }}>
                          <select value={r.dieta || ""} onChange={e => saveDieta(r, e.target.value)}
                            data-testid={`mm-day-dieta-${r.cf || r.alunno_cod}`}
                            disabled={!r.cf}
                            title={r.cf ? "Cambia dieta/intolleranza inline" : "CF mancante"}
                            style={{ flex: 1, padding: "2px 3px", fontSize: 10, fontWeight: r.dieta ? 700 : 400,
                                     color: r.dieta ? "white" : "#888",
                                     border: r.dieta ? (r.dieta_prev_year ? "1px solid #ef6c00" : "1px solid #ad1457") : "1px solid #ccc",
                                     background: r.dieta ? (r.dieta_prev_year ? "#ef6c00" : "#ad1457") : "white",
                                     borderRadius: 2, cursor: r.cf ? "pointer" : "not-allowed" }}>
                            <option value="" style={{ background: "white", color: "#333" }}>— nessuna —</option>
                            {dieteList.map(d => <option key={d} value={d} style={{ background: "white", color: "#333" }}>{d}</option>)}
                            {r.dieta && !dieteList.includes(r.dieta) && (
                              <option value={r.dieta} style={{ background: "white", color: "#333" }}>{r.dieta} (custom)</option>
                            )}
                          </select>
                          {r.dieta_prev_year && (
                            <span data-testid={`mm-day-prev-badge-${r.cf}`}
                              title="Dieta dell'anno precedente — certificato non ancora aggiornato"
                              style={{ background: "#ef6c00", color: "white", fontSize: 8, fontWeight: 900, padding: "1px 3px", borderRadius: 2, whiteSpace: "nowrap" }}>
                              ⚠ {String((data?.anno ?? 0) - 1).slice(-2)}/{String(data?.anno ?? 0).slice(-2)}
                            </span>
                          )}
                          <button onClick={() => setDieteModalOpen(true)} title="Gestisci elenco diete"
                            style={{ background: "#37474f", color: "white", border: "none", padding: "1px 3px", fontSize: 9, cursor: "pointer", borderRadius: 2 }}>⚙</button>
                        </div>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {motivi.map((m, k) => (
                          <span key={m}
                            style={{ display: "inline-block", background: BG_CELL[m], color: COLORS[m], padding: "2px 8px", margin: "0 2px", fontSize: 10, fontWeight: 700, textTransform: "uppercase", borderRadius: 2 }}>
                            {LABELS[m]}
                          </span>
                        ))}
                      </td>
                      <td style={{ fontSize: 11 }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          {det.scuola && (
                            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                              <span style={{ background: COLORS.s, color: "white", padding: "1px 6px", fontWeight: 700, borderRadius: 2, fontSize: 10 }}>SCUOLA</span>
                              <span style={{ color: "#555" }}>Rientro pomeridiano ({r.plesso || r.settore})</span>
                            </div>
                          )}
                          {det.extra.map((ex, j) => (
                            <div key={j} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                              <span style={{ background: COLORS.x, color: "white", padding: "1px 6px", fontWeight: 700, borderRadius: 2, fontSize: 10 }}>EXTRA</span>
                              <span style={{ color: "#555" }}>{ex.codice ? <b style={{ fontFamily: "Consolas, monospace", color: "#0d47a1" }}>{ex.codice}</b> : null} {ex.descrizione}</span>
                            </div>
                          ))}
                          {det.fam && (
                            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                              <span style={{ background: COLORS.f, color: "white", padding: "1px 6px", fontWeight: 700, borderRadius: 2, fontSize: 10 }}>SPOT</span>
                              <span style={{ color: "#555" }}>{det.fam.note || "Richiesta mensa SPOT"}</span>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                      );
                    });
                    out.push(
                      <tr key={`rs-${grp.refettorio}`} style={{ background: "#f3e5f5", color: "#6a1b9a", fontWeight: 700 }}>
                        <td colSpan={7} style={{ textAlign: "right", padding: "4px 10px", fontSize: 11 }}>
                          Subtotale {grp.refettorio}: <b>{grp.rows.length}</b> alunni{grp.intoll > 0 ? ` · ${grp.intoll} con intolleranza` : ""}
                        </td>
                      </tr>
                    );
                  }
                  return out;
                })()}
              </tbody>
              {filtered.length > 0 && (
                <tfoot>
                  <tr style={{ background: "#263238", color: "white", fontWeight: 800 }}>
                    <td colSpan={5} style={{ textAlign: "right", padding: "6px 8px" }}>TOTALE {DAY_NAMES[giornoSel].toUpperCase()}</td>
                    <td colSpan={2} style={{ textAlign: "center", background: "#1b5e20", fontFamily: "Consolas, monospace", fontSize: 13 }}>{filtered.length} alunni potenziali</td>
                  </tr>
                </tfoot>
              )}
            </table>
          )}
        </div>
      </div>
      {dieteModalOpen && (
        <DieteManagerModal items={dieteList}
          onSaved={(newList) => setDieteList(newList)}
          onClose={() => setDieteModalOpen(false)} />
      )}
    </div>
  );
}

function LegendItem({ k }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span style={{ display: "inline-block", width: 14, height: 14, background: BG_CELL[k], border: `1px solid ${COLORS[k]}` }} />
      <span style={{ color: COLORS[k], fontWeight: 700, textTransform: "uppercase", fontSize: 11 }}>{LABELS[k]}</span>
    </span>
  );
}

function IntolleranzeImportButton({ onDone }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState(null);

  const loadStats = async () => {
    try {
      const r = await api.get("/mensa-matrice/intolleranze");
      setStats({ total: r.data?.total || 0, per: r.data?.per_dieta || {} });
    } catch {/* ignore */}
  };
  useEffect(() => { loadStats(); }, []);

  const handle = async (file) => {
    if (!file) return;
    if (!window.confirm(`Importare la lista intolleranze da "${file.name}"?\n\nModalità: REPLACE (sostituisce l'elenco esistente, link via codice fiscale)`)) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/mensa-matrice/intolleranze/import?mode=replace", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const d = r.data || {};
      alert(`Import completato\n• Inseriti: ${d.inserted}\n• Aggiornati: ${d.updated}\n• Scartati: ${d.skipped}\n• Totale in archivio: ${d.total_records}`);
      await loadStats();
      onDone && onDone();
    } catch (e) {
      alert(e.response?.data?.detail || "Errore import");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <>
      <label data-testid="mm-intoll-import"
             title={stats ? `Diete caricate: ${stats.total}\n${Object.entries(stats.per).map(([k,v]) => `· ${k}: ${v}`).join("\n")}` : "Carica lista intolleranze (Excel)"}
             style={{ padding: "5px 10px", background: busy ? "#888" : "#ad1457", color: "white", fontSize: 11, fontWeight: 700, cursor: busy ? "wait" : "pointer", border: "none", display: "inline-flex", alignItems: "center", gap: 4 }}>
        🍴 {busy ? "Carico..." : "Intolleranze"}{stats && stats.total > 0 ? ` (${stats.total})` : ""}
        <input type="file" ref={fileRef} accept=".xls,.xlsx,.xml" disabled={busy}
               onChange={(e) => handle(e.target.files?.[0])} style={{ display: "none" }} />
      </label>
    </>
  );
}

// ========================= PLESSI CONFIG =========================

function PlessiConfigView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newPlesso, setNewPlesso] = useState("");
  const [err, setErr] = useState(null);

  const carica = async () => {
    setLoading(true);
    try {
      const r = await api.get("/mensa-matrice/plessi-config");
      setItems(r.data.items || []);
    } catch (e) { setErr(e.response?.data?.detail || "Errore"); }
    finally { setLoading(false); }
  };
  useEffect(() => { carica(); }, []);

  const toggleDay = async (row, wd) => {
    const giorni = row.giorni.includes(wd) ? row.giorni.filter(d => d !== wd) : [...row.giorni, wd].sort();
    try {
      await api.post("/mensa-matrice/plessi-config", { plesso: row.plesso, giorni });
      await carica();
    } catch (e) { setErr(e.response?.data?.detail || "Errore salvataggio"); }
  };

  const aggiungi = async () => {
    const p = newPlesso.trim();
    if (!p) return;
    try {
      await api.post("/mensa-matrice/plessi-config", { plesso: p, giorni: [] });
      setNewPlesso("");
      await carica();
    } catch (e) { setErr(e.response?.data?.detail || "Errore"); }
  };

  const elimina = async (row) => {
    if (!window.confirm(`Eliminare il plesso "${row.plesso}" dalla configurazione?`)) return;
    try {
      await api.delete(`/mensa-matrice/plessi-config/${encodeURIComponent(row.plesso)}`);
      await carica();
    } catch (e) { setErr(e.response?.data?.detail || "Errore eliminazione"); }
  };

  return (
    <div>
      <div style={{ margin: "8px 20px", background: "white", border: "1px solid #ccc", padding: 12 }}>
        <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
          Definisci per ogni <b>plesso</b> (o settore A/E/F/M/...) in quali giorni della settimana gli alunni fanno
          <b> rientro pomeridiano</b> e quindi restano a mensa.
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <input placeholder="Codice plesso o settore (es. BS1M01400R oppure M)"
            value={newPlesso} onChange={e => setNewPlesso(e.target.value.toUpperCase())}
            onKeyDown={e => e.key === "Enter" && aggiungi()}
            data-testid="mm-plessi-new"
            style={{ flex: 1, padding: 6, border: "1px solid #ccc", fontSize: 12, fontFamily: "Consolas, monospace" }} />
          <button onClick={aggiungi} data-testid="mm-plessi-add"
            style={{ padding: "6px 14px", background: "#8e24aa", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>+ Aggiungi</button>
        </div>
      </div>

      {err && <div style={{ margin: "0 20px 8px", padding: 10, background: "#fff0f0", color: "#c00", fontSize: 12 }}>{err}</div>}

      <div style={{ margin: "0 20px 20px", background: "white", border: "1px solid #ccc" }}>
        <table className="sw-table sw-table-compact" style={{ fontSize: 12 }}>
          <thead style={{ background: "#1f3864", color: "white" }}>
            <tr>
              <th style={{ minWidth: 250 }}>PLESSO / SETTORE</th>
              {DAYS.map(d => <th key={d} style={{ textAlign: "center", width: 90 }}>{d}</th>)}
              <th style={{ textAlign: "center", width: 70 }}>AZIONI</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} style={{ textAlign: "center", padding: 20, color: "#888" }}>Caricamento...</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} style={{ textAlign: "center", padding: 20, color: "#888" }}>Nessun plesso configurato. Aggiungine uno sopra.</td></tr>
            )}
            {!loading && items.map((r, i) => (
              <tr key={r.plesso} data-testid={`mm-plessi-row-${i}`}>
                <td style={{ fontWeight: 700, fontFamily: "Consolas, monospace", color: "#0d47a1" }}>{r.plesso}</td>
                {[0, 1, 2, 3, 4].map(wd => (
                  <td key={wd} style={{ textAlign: "center" }}>
                    <input type="checkbox" checked={r.giorni.includes(wd)} onChange={() => toggleDay(r, wd)}
                      data-testid={`mm-plessi-${r.plesso}-${wd}`}
                      style={{ width: 18, height: 18, cursor: "pointer" }} />
                  </td>
                ))}
                <td style={{ textAlign: "center" }}>
                  <button onClick={() => elimina(r)} data-testid={`mm-plessi-del-${r.plesso}`} title="Elimina"
                    style={{ background: "#c62828", color: "white", border: "none", padding: "3px 8px", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>🗑</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ========================= CORSI =========================

export function CorsiView() {
  const { annoCorrente } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [anni, setAnni] = useState([]);
  const [anno, setAnno] = useState(null);
  const [filterSettore, setFilterSettore] = useState("");
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState(null);
  const [slotEditing, setSlotEditing] = useState(null); // corso row being edited for max_per_slot
  const [iscrizioniCfg, setIscrizioniCfg] = useState({ anno_iscrizioni: null, updated_at: null, updated_by: null });

  const caricaIscrizioniCfg = async () => {
    try {
      const r = await api.get("/mensa-matrice/iscrizioni-config");
      setIscrizioniCfg(r.data || { anno_iscrizioni: null });
    } catch { /* ignore */ }
  };

  const salvaIscrizioniCfg = async (newAnno) => {
    try {
      await api.put("/mensa-matrice/iscrizioni-config", { anno_iscrizioni: newAnno });
      await caricaIscrizioniCfg();
      if (newAnno != null) setAnno(newAnno);
    } catch (e) { setErr(e.response?.data?.detail || "Errore salvataggio config iscrizioni"); }
  };

  const caricaAnni = async () => {
    try {
      const r = await api.get("/mensa-matrice/anni");
      const arr = r.data.anni || [];
      setAnni(arr);
      if (anno == null && arr.length) {
        setAnno(annoCorrente && arr.includes(annoCorrente) ? annoCorrente : arr[0]);
      }
    } catch { /* ignore */ }
  };

  const carica = async (a) => {
    if (a == null) return;
    setLoading(true); setErr(null);
    try {
      const params = { anno: a };
      if (filterSettore) params.settore = filterSettore;
      const r = await api.get("/mensa-matrice/corsi", { params });
      setItems(r.data.items || []);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore");
    } finally { setLoading(false); }
  };

  useEffect(() => { caricaAnni(); caricaIscrizioniCfg(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (anno != null) carica(anno); /* eslint-disable-next-line */ }, [anno, filterSettore]);

  const salvaMaxInline = async (r, newMax) => {
    const m = Math.max(0, parseInt(newMax || 0, 10) || 0);
    if (m === (r.max_iscritti ?? 0)) return;
    try {
      await api.put(`/mensa-matrice/corsi/${r.codice}`, { anno: r.anno, max_iscritti: m });
      await carica(anno);
    } catch (e) { setErr(e.response?.data?.detail || "Errore aggiornamento max"); }
  };
  const salvaGrpInline = async (r, newGrp) => {
    const g = Math.max(1, Math.min(5, parseInt(newGrp || 1, 10) || 1));
    if (g === (r.num_gruppi ?? 1)) return;
    try {
      await api.put(`/mensa-matrice/corsi/${r.codice}`, { anno: r.anno, num_gruppi: g });
      await carica(anno);
    } catch (e) { setErr(e.response?.data?.detail || "Errore aggiornamento gruppi"); }
  };

  const salvaOrdInline = async (r, newOrd) => {
    const o = parseInt(newOrd || 0, 10) || 0;
    if (o === (r.ordinamento ?? 0)) return;
    try {
      await api.put(`/mensa-matrice/corsi/${r.codice}`, { anno: r.anno, ordinamento: o });
      await carica(anno);
    } catch (e) { setErr(e.response?.data?.detail || "Errore aggiornamento ordinamento"); }
  };

  // 🆕 Salva inline la causale custom del corso (max 200 char). Vuoto = usa la
  // causale_default globale. Optimistic update + rollback su errore.
  const salvaCausaleInline = async (r, newCausale) => {
    const c = String(newCausale || "").trim();
    if (c === (r.causale || "")) return;
    const prev = items;
    setItems(items.map(x => x.codice === r.codice && x.anno === r.anno ? { ...x, causale: c } : x));
    try {
      await api.put(`/mensa-matrice/corsi/${r.codice}`, { anno: r.anno, causale: c });
    } catch (e) {
      setItems(prev);
      setErr(e.response?.data?.detail || "Errore aggiornamento causale");
    }
  };

  const svuotaTuttiCorsi = async () => {
    // Conta iscrizioni totali dell'anno
    try {
      const r0 = await api.get("/mensa-matrice/extra-iscrizioni", { params: { anno } });
      const totIsc = (r0.data.items || []).length;
      if (!totIsc) { alert(`Nessuna iscrizione extra per l'anno ${fmtAnno(anno)}`); return; }
      if (!window.confirm(`⚠ Cancellare TUTTE le ${totIsc} iscrizioni extra dell'anno ${fmtAnno(anno)}?\n\n(I corsi NON verranno toccati, solo gli iscritti.)\nOperazione IRREVERSIBILE.`)) return;
      const s = window.prompt(`Per confermare digita: ELIMINA ${totIsc}`);
      if (s !== `ELIMINA ${totIsc}`) { alert("Operazione annullata"); return; }
      const r = await api.delete("/mensa-matrice/extra-iscrizioni", { params: { anno, confirm: "DELETE" } });
      alert(`✓ Eliminate ${r.data.deleted} iscrizioni`);
      await carica(anno);
    } catch (e) { setErr(e.response?.data?.detail || "Errore eliminazione"); }
  };

  const importInputRef = useRef(null);
  const exportaJson = async () => {
    try {
      const r = await api.get("/mensa-matrice/corsi/export-json", { params: { anno } });
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `corsi_${fmtAnno(anno)}_${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setErr(e.response?.data?.detail || "Errore export"); }
  };
  const importaJson = async (file) => {
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const items = data.items || data;
      if (!Array.isArray(items)) { setErr("File non valido: 'items' mancante"); return; }
      const mode = window.confirm(
        `Importare ${items.length} corsi?\n\n` +
        `• OK = UPSERT (aggiunge nuovi, aggiorna esistenti)\n` +
        `• Annulla = operazione abortita\n\n` +
        `⚠ Per REPLACE (cancella tutti i corsi dell'anno e reinserisce) contattare lo sviluppatore.`
      );
      if (!mode) return;
      const r = await api.post("/mensa-matrice/corsi/import-json", { items, mode: "upsert" });
      alert(`✓ Import completato\nInseriti: ${r.data.inserted}\nAggiornati: ${r.data.updated}\nErrori: ${r.data.errors?.length || 0}`);
      await carica(anno);
    } catch (e) { setErr(e.response?.data?.detail || e.message || "Errore import"); }
    finally {
      if (importInputRef.current) importInputRef.current.value = "";
    }
  };
  const salvaMaxPerSlot = async (r, matrix) => {
    try {
      await api.put(`/mensa-matrice/corsi/${r.codice}`, { anno: r.anno, max_per_slot: matrix });
      await carica(anno);
      setSlotEditing(null);
    } catch (e) { setErr(e.response?.data?.detail || "Errore salvataggio"); }
  };

  const nuovo = () => setEditing({ _isNew: true, codice: "", descrizione: "", settori: ["E", "F"], settore_curriculum: "", cadenza: "aaa", giorno_fisso: null, giorni_ammessi: [], prezzi: {}, tariffe: [], rate: [], classi_ammesse: [], crea_mensa: true, tutti_giorni_obbligatori: false, max_iscritti: 0, orario: "", ordinamento: 0, anno });
  const modifica = (r) => {
    const settoriNorm = Array.isArray(r.settori) && r.settori.length ? r.settori : (r.settore ? [r.settore] : []);
    setEditing({
      ...r,
      settori: settoriNorm,
      settore_curriculum: r.settore_curriculum || "",
      cadenza: r.cadenza || "aaa",
      giorno_fisso: r.giorno_fisso ?? null,
      crea_mensa: r.crea_mensa !== false,
      tutti_giorni_obbligatori: !!r.tutti_giorni_obbligatori,
      max_iscritti: r.max_iscritti ?? 0,
      orario: r.orario || "",
      ordinamento: r.ordinamento ?? 0,
      prezzi: { ...(r.prezzi || {}) },
      tariffe: Array.isArray(r.tariffe) ? r.tariffe.map(t => ({ ...t })) : [],
      rate: Array.isArray(r.rate) ? r.rate.map(t => ({ ...t })) : [],
      classi_ammesse: Array.isArray(r.classi_ammesse) ? [...r.classi_ammesse] : [],
      _orig_codice: r.codice,
    });
  };

  const salva = async () => {
    if (!editing) return;
    const payload = {
      codice: editing.codice, descrizione: editing.descrizione,
      settori: editing.settori, cadenza: editing.cadenza,
      settore_curriculum: editing.settore_curriculum || null,
      giorno_fisso: null,
      giorni_ammessi: editing.giorni_ammessi,
      prezzi: editing.prezzi || {}, anno: editing.anno,
      crea_mensa: editing.crea_mensa !== false,
      tutti_giorni_obbligatori: !!editing.tutti_giorni_obbligatori,
      max_iscritti: parseInt(editing.max_iscritti || 0, 10),
      classi_ammesse: editing.classi_ammesse || [],
      orario: editing.orario || "",
      ordinamento: parseInt(editing.ordinamento || 0, 10),
      tariffe: (editing.tariffe || []).filter(t => t.etichetta).map(t => ({
        etichetta: String(t.etichetta || "").toUpperCase(),
        nr_giorni: Number(t.nr_giorni || 1),
        importo: Number(t.importo || 0),
      })),
      rate: (editing.rate || []).filter(r => r.nome).map(r => ({
        nome: String(r.nome || "").toUpperCase(),
        scadenza: r.scadenza || null,
      })),
    };
    try {
      if (editing._isNew) {
        await api.post("/mensa-matrice/corsi", payload);
      } else {
        await api.put(`/mensa-matrice/corsi/${editing._orig_codice}`, payload);
      }
      setEditing(null);
      await carica(anno);
    } catch (e) { setErr(e.response?.data?.detail || "Errore salvataggio"); }
  };

  const elimina = async (r) => {
    if (!window.confirm(`Eliminare il corso "${r.descrizione}" (${r.codice})?`)) return;
    try {
      await api.delete(`/mensa-matrice/corsi/${r.codice}`, { params: { anno: r.anno } });
      await carica(anno);
    } catch (e) { setErr(e.response?.data?.detail || "Errore"); }
  };

  const toggleCreaMensa = async (r, nuovo) => {
    // Ottimistico: aggiorno subito la UI
    setItems(items.map(x => x.codice === r.codice ? { ...x, crea_mensa: nuovo } : x));
    try {
      await api.put(`/mensa-matrice/corsi/${r.codice}`, { anno: r.anno, crea_mensa: nuovo });
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore aggiornamento");
      // rollback
      setItems(items);
    }
  };

  const duplicaDaSorgente = async (annoSrcN) => {
    if (!annoSrcN || annoSrcN === anno) return;
    if (!window.confirm(`Duplicare TUTTI i corsi dell'anno ${fmtAnno(annoSrcN)} nell'anno ${fmtAnno(anno)}?\n\nLe scadenze delle rate verranno avanzate automaticamente di ${anno - annoSrcN > 0 ? "+" : ""}${anno - annoSrcN} ann${Math.abs(anno - annoSrcN) === 1 ? "o" : "i"} (es. "10/07/${annoSrcN}" → "10/07/${anno}").\n\nLe iscrizioni NON vengono toccate — solo la struttura dei corsi.`)) return;
    try {
      const r = await api.post("/mensa-matrice/corsi/duplica-anno", {
        anno_sorgente: annoSrcN, anno_destinazione: anno, sovrascrivi: false, avanza_date: true,
      });
      alert(`✓ Duplicazione completata!\n· Creati: ${r.data.creati}\n· Saltati (già esistenti): ${r.data.saltati}\n· Date scadenza avanzate di ${r.data.delta_anni} ann${Math.abs(r.data.delta_anni) === 1 ? "o" : "i"}`);
      await carica(anno);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore duplicazione");
    }
  };




  // Anno precedente disponibile (in lista anni e con corsi) per offrire la replica automatica
  const annoPrecDisponibile = useMemo(() => {
    if (anno == null) return null;
    const candidati = (anni || []).filter(a => a < anno);
    return candidati.length ? Math.max(...candidati) : null;
  }, [anni, anno]);

  return (
    <div>
      <div style={{ margin: "8px 20px", background: "white", border: "1px solid #ccc", padding: 10, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: "#4a148c" }}>📚 Corsi · {items.length}</div>
        <select value={anno ?? ""} onChange={e => setAnno(Number(e.target.value))}
          data-testid="mm-corsi-anno"
          style={{ padding: "5px 10px", border: "2px solid #8e24aa", fontSize: 12, fontWeight: 700, color: "#4a148c" }}>
          {anni.map(a => <option key={a} value={a}>Anno {fmtAnno(a)}</option>)}
        </select>
        <select value={filterSettore} onChange={e => setFilterSettore(e.target.value)}
          data-testid="mm-corsi-settore"
          style={{ padding: "5px 8px", border: "1px solid #ccc", fontSize: 12 }}>
          <option value="">Tutti i settori</option>
          {["A", "E", "F", "M"].map(s => <option key={s} value={s}>Settore {s}</option>)}
        </select>
        <button onClick={nuovo} data-testid="mm-corsi-new"
          style={{ marginLeft: "auto", padding: "6px 14px", background: "#8e24aa", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>+ Nuovo corso</button>
      </div>

      {/* ⚙️ ISCRIZIONI EXTRA — anno aperto per il portale alunni */}
      <div style={{ margin: "0 20px 8px", padding: "10px 14px", background: "linear-gradient(90deg,#e8f5e9,#c8e6c9)", border: "2px solid #2e7d32", borderRadius: 4, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{ fontSize: 22 }}>🎓</div>
        <div style={{ flex: "1 1 280px" }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#1b5e20", marginBottom: 2 }}>
            Anno aperto per le iscrizioni dal Portale Alunni
          </div>
          <div style={{ fontSize: 10, color: "#33691e", lineHeight: 1.4 }}>
            {iscrizioniCfg.anno_iscrizioni
              ? <>Gli alunni vedono i corsi dell'anno <b>{fmtAnno(iscrizioniCfg.anno_iscrizioni)}</b> (override globale attivo){iscrizioniCfg.updated_by ? <> · ultimo cambio {iscrizioniCfg.updated_by}</> : null}</>
              : <>Override <b>disattivato</b>: ogni alunno vede i corsi del proprio anno scolastico corrente (comportamento automatico).</>
            }
          </div>
        </div>
        <select value={iscrizioniCfg.anno_iscrizioni ?? ""}
          onChange={e => salvaIscrizioniCfg(e.target.value === "" ? null : Number(e.target.value))}
          data-testid="mm-iscr-config-anno"
          style={{ padding: "6px 10px", border: "2px solid #2e7d32", fontSize: 12, fontWeight: 700, color: "#1b5e20", background: "white", cursor: "pointer" }}>
          <option value="">⟲ Automatico (anno alunno)</option>
          {anni.map(a => <option key={a} value={a}>Forza anno {fmtAnno(a)}</option>)}
        </select>
        {iscrizioniCfg.anno_iscrizioni && (
          <button onClick={() => salvaIscrizioniCfg(null)} data-testid="mm-iscr-config-reset"
            title="Disattiva l'override e torna al comportamento automatico"
            style={{ padding: "5px 10px", background: "white", color: "#c62828", border: "1px solid #c62828", fontSize: 10, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
            ✕ Reset
          </button>
        )}
      </div>


      {err && <div style={{ margin: "0 20px 8px", padding: 10, background: "#fff0f0", color: "#c00", fontSize: 12 }}>{err} <button onClick={() => setErr(null)} style={{ marginLeft: 10, border: "none", background: "none", color: "#c00", cursor: "pointer", fontWeight: 700 }}>✕</button></div>}

      {/* Banner replica automatica: anno selezionato vuoto + esiste un anno precedente in lista */}
      {!loading && items.length === 0 && annoPrecDisponibile != null && (
        <div data-testid="mm-corsi-replica-banner"
          style={{ margin: "0 20px 12px", padding: "14px 18px", background: "linear-gradient(90deg,#fff3e0,#ffe0b2)", border: "2px solid #ef6c00", borderRadius: 6, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <div style={{ fontSize: 28 }}>📋</div>
          <div style={{ flex: "1 1 280px" }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#bf360c", marginBottom: 3 }}>
              Nessun corso configurato per l'anno {fmtAnno(anno)}
            </div>
            <div style={{ fontSize: 12, color: "#666" }}>
              È disponibile l'anno scolastico precedente <b>{fmtAnno(annoPrecDisponibile)}</b>. Vuoi replicare automaticamente tutti i corsi (con tariffe, rate, classi ammesse, giorni)?
              <br/><span style={{ fontSize: 10, fontStyle: "italic", color: "#888" }}>Le scadenze delle rate verranno avanzate di <b>{anno - annoPrecDisponibile > 0 ? "+" : ""}{anno - annoPrecDisponibile}</b> ann{Math.abs(anno - annoPrecDisponibile) === 1 ? "o" : "i"} · le iscrizioni NON vengono copiate.</span>
            </div>
          </div>
          <button onClick={() => duplicaDaSorgente(annoPrecDisponibile)} data-testid="mm-corsi-replica-auto"
            style={{ padding: "10px 20px", background: "#ef6c00", color: "white", border: "none", borderRadius: 4, fontSize: 13, fontWeight: 800, cursor: "pointer", boxShadow: "0 2px 6px rgba(0,0,0,0.15)" }}>
            ✓ Replica da {fmtAnno(annoPrecDisponibile)}
          </button>
        </div>
      )}


      <div style={{ margin: "0 20px 20px", background: "white", border: "1px solid #ccc" }}>
        <table className="sw-table sw-table-compact" style={{ fontSize: 12 }}>
          <thead style={{ background: "#1f3864", color: "white" }}>
            <tr>
              <th style={{ width: 60, textAlign: "center" }} title="Ordinamento nel portale alunni (0 = ultimo, asc)">ORD</th>
              <th style={{ width: 110 }}>CODICE</th>
              <th style={{ minWidth: 260 }}>DESCRIZIONE</th>
              <th style={{ minWidth: 180 }} title="Causale custom del bonifico per questo corso. Vuoto = usa il default globale. Usata per matchare i movimenti contabili.">CAUSALE</th>
              <th style={{ width: 90, textAlign: "center" }}>SETTORI</th>
              <th style={{ width: 60, textAlign: "center" }} title="Numero di tariffe disponibili">TARIFFE</th>
              <th style={{ width: 60, textAlign: "center" }} title="Numero di rate di pagamento">RATE</th>
              <th style={{ width: 90, textAlign: "center" }} title="Classi (1-5) ammesse al corso. Vuoto = tutte le classi">CLASSI</th>
              <th style={{ width: 70, textAlign: "center" }}>MENSA</th>
              <th style={{ width: 90, textAlign: "center" }} title="Iscritti / Max (+ lista attesa)">MAX</th>
              <th style={{ width: 60, textAlign: "center" }} title="Numero di gruppi del corso (1=unico, 2=classi 1-2 vs 3-4-5, ecc.)">GRP</th>
              {DAYS.map(d => <th key={d} style={{ width: 50, textAlign: "center" }}>{d}</th>)}
              <th style={{ width: 100, textAlign: "center" }}>AZIONI</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={17} style={{ textAlign: "center", padding: 20, color: "#888" }}>Caricamento...</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={17} style={{ textAlign: "center", padding: 20, color: "#888" }}>
                Nessun corso per l'anno {fmtAnno(anno)} {filterSettore ? `settore ${filterSettore}` : ""}. Clicca <b>+ Nuovo corso</b>.
              </td></tr>
            )}
            {!loading && items.map((r, i) => {
              const settoriList = Array.isArray(r.settori) && r.settori.length ? r.settori : (r.settore ? [r.settore] : []);
              const creaMensa = r.crea_mensa !== false;
              return (
              <tr key={`${r.codice}-${r.anno}`} data-testid={`mm-corsi-row-${i}`}>
                <td style={{ textAlign: "center", padding: "4px 6px" }} title="Ordinamento nel portale alunni (0 = ultimo, asc) — modifica e premi Tab/Enter">
                  <input type="number" min={0} defaultValue={r.ordinamento ?? 0}
                    onBlur={(e) => salvaOrdInline(r, e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                    data-testid={`mm-corsi-ord-inline-${i}`}
                    style={{ width: 46, padding: "3px 4px", border: "1px solid #ccc",
                             fontSize: 12, fontFamily: "Consolas, monospace", fontWeight: 700,
                             textAlign: "center", background: (r.ordinamento || 0) > 0 ? "#e3f2fd" : "#f5f5f5",
                             color: (r.ordinamento || 0) > 0 ? "#0d47a1" : "#888" }} />
                </td>
                <td style={{ fontFamily: "Consolas, monospace", fontWeight: 700, color: "#0d47a1" }}>{r.codice}</td>
                <td style={{ fontWeight: 600 }}>{r.descrizione}</td>
                <td style={{ padding: "3px 6px" }}
                    title="Causale per il bonifico — usata anche per matchare i movimenti contabili. Vuoto = usa il default globale.">
                  <input type="text" defaultValue={r.causale ?? ""}
                    onBlur={(e) => salvaCausaleInline(r, e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                    data-testid={`mm-corsi-causale-inline-${i}`}
                    maxLength={200}
                    placeholder="(default globale)"
                    style={{ width: "100%", padding: "3px 6px", border: "1px solid #ccc",
                             fontSize: 11, fontFamily: "Consolas, monospace",
                             background: (r.causale || "").trim() ? "#fff9c4" : "#fafafa",
                             color: (r.causale || "").trim() ? "#5d4037" : "#999",
                             fontStyle: (r.causale || "").trim() ? "normal" : "italic" }} />
                </td>
                <td style={{ textAlign: "center", fontWeight: 700 }}>
                  {settoriList.join(" ")}
                  {r.settore_curriculum ? (
                    <span title="Settore Curriculum (P/M)" style={{ marginLeft: 6, padding: "1px 6px", background: "#1565c0", color: "white", fontSize: 10, fontWeight: 700, borderRadius: 2 }}>{r.settore_curriculum}</span>
                  ) : (
                    <span title="Settore Curriculum NON impostato — il corso non sarà visibile a nessun alunno nel portale" style={{ marginLeft: 6, padding: "1px 6px", background: "#fff3cd", color: "#856404", border: "1px solid #ffe69c", fontSize: 10, fontWeight: 700, borderRadius: 2 }}>?</span>
                  )}
                </td>
                <td style={{ textAlign: "center", fontFamily: "Consolas, monospace", fontWeight: 700, color: (r.tariffe || []).length > 0 ? "#1565c0" : "#c62828" }}
                    title={(r.tariffe || []).map(t => `${t.etichetta}: €${t.importo}`).join("\n") || "Nessuna tariffa configurata"}>
                  {(r.tariffe || []).length}
                </td>
                <td style={{ textAlign: "center", fontFamily: "Consolas, monospace", fontWeight: 700, color: (r.rate || []).length > 0 ? "#558b2f" : "#888" }}
                    title={(r.rate || []).map(rt => `${rt.nome}${rt.scadenza ? " (" + rt.scadenza + ")" : ""}`).join("\n") || "Nessuna rata configurata"}>
                  {(r.rate || []).length}
                </td>
                <td style={{ textAlign: "center", fontFamily: "Consolas, monospace", fontWeight: 700 }}
                    title={(r.classi_ammesse || []).length ? `Classi ammesse: ${(r.classi_ammesse || []).join(", ")}` : "Tutte le classi"}>
                  {(r.classi_ammesse || []).length === 0
                    ? <span style={{ color: "#888", fontStyle: "italic" }}>tutte</span>
                    : <span style={{ color: "#0d47a1" }}>{(r.classi_ammesse || []).join(" ")}</span>}
                </td>
                <td style={{ textAlign: "center", fontSize: 13, background: creaMensa ? "#e8f5e9" : "#ffebee" }}
                  title={creaMensa ? "L'iscrizione genera presenza in mensa (click per disattivare)" : "L'iscrizione NON genera presenza in mensa (click per attivare)"}>
                  <label style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer", fontWeight: 700, color: creaMensa ? "#2e7d32" : "#c62828" }}>
                    <input type="checkbox" checked={creaMensa}
                      onChange={e => toggleCreaMensa(r, e.target.checked)}
                      data-testid={`mm-corsi-cm-${i}`}
                      style={{ width: 16, height: 16, cursor: "pointer", accentColor: creaMensa ? "#2e7d32" : "#c62828" }} />
                    <span style={{ fontSize: 11 }}>{creaMensa ? "🍽️" : "✗"}</span>
                  </label>
                </td>
                <td style={{ textAlign: "center", fontSize: 11, fontFamily: "Consolas, monospace", fontWeight: 700, padding: "4px 6px" }}
                    title={`${r.iscritti_count || 0} iscritti attivi · ${r.lista_attesa_count || 0} in lista d'attesa · ${(r.giorni_ammessi || []).length > 1 ? "max per slot" : (r.max_iscritti ? `max ${r.max_iscritti}` : "illimitato")}${r.max_per_slot && Object.keys(r.max_per_slot).length ? " · slot personalizzati attivi" : ""}`}>
                  <div style={{ display: "flex", gap: 4, alignItems: "center", justifyContent: "center" }}>
                    <span style={{ color: "#0d47a1" }}>{r.iscritti_count || 0}</span>
                    {(r.giorni_ammessi || []).length <= 1 ? (
                      <>
                        <span style={{ color: "#888" }}>/</span>
                        <input type="number" min={0} defaultValue={r.max_iscritti ?? 0}
                          onBlur={(e) => salvaMaxInline(r, e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                          data-testid={`mm-corsi-max-inline-${i}`}
                          style={{ width: 52, padding: "2px 4px", border: "1px solid #ccc", fontSize: 11, fontFamily: "Consolas, monospace", fontWeight: 700, textAlign: "center", color: (r.iscritti_count || 0) >= (r.max_iscritti || 0) && r.max_iscritti ? "#c62828" : "#2e7d32", background: "#fffde7" }} />
                        {(r.num_gruppi || 1) > 1 && (
                          <button onClick={() => setSlotEditing(r)} title="Max per slot (giorno × gruppo)"
                            data-testid={`mm-corsi-slot-${i}`}
                            style={{ padding: "1px 6px", background: (r.max_per_slot && Object.keys(r.max_per_slot).length) ? "#ef6c00" : "#1565c0", color: "white", border: "none", fontSize: 10, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}>⚙</button>
                        )}
                      </>
                    ) : (
                      <button onClick={() => setSlotEditing(r)} title="Max per slot (giorno × gruppo) — click per modificare"
                        data-testid={`mm-corsi-slot-${i}`}
                        style={{ marginLeft: 4, padding: "3px 10px", background: (r.max_per_slot && Object.keys(r.max_per_slot).length) ? "#ef6c00" : "#1565c0", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}>
                        ⚙ Slot
                      </button>
                    )}
                  </div>
                  {r.lista_attesa_count ? <div style={{ color: "#ef6c00", fontSize: 9, marginTop: 2 }}>+{r.lista_attesa_count} attesa</div> : null}
                </td>
                <td style={{ textAlign: "center", padding: "4px 6px" }} title="Numero di gruppi — click per modificare">
                  <input type="number" min={1} max={5} defaultValue={r.num_gruppi ?? 1}
                    onBlur={(e) => salvaGrpInline(r, e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                    data-testid={`mm-corsi-grp-${i}`}
                    style={{ width: 44, padding: "3px 4px", border: "1px solid #ccc", fontSize: 12, fontFamily: "Consolas, monospace", fontWeight: 700, textAlign: "center", background: "#f3e5f5", color: "#6a1b9a" }} />
                </td>
                {[0, 1, 2, 3, 4].map(wd => (
                  <td key={wd} style={{ textAlign: "center", background: r.giorni_ammessi.includes(wd) ? "#e8f5e9" : "transparent" }}>
                    {r.giorni_ammessi.includes(wd) ? <span style={{ color: "#2e7d32", fontWeight: 800 }}>✓</span> : <span style={{ color: "#ccc" }}>—</span>}
                  </td>
                ))}
                <td style={{ textAlign: "center" }}>
                  <button onClick={() => modifica(r)} data-testid={`mm-corsi-edit-${i}`}
                    style={{ background: "#1565c0", color: "white", border: "none", padding: "3px 8px", fontSize: 10, fontWeight: 700, cursor: "pointer", marginRight: 4 }}>✏</button>
                  <button onClick={() => elimina(r)} data-testid={`mm-corsi-del-${i}`}
                    style={{ background: "#c62828", color: "white", border: "none", padding: "3px 8px", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>🗑</button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && <CorsoEditModal editing={editing} setEditing={setEditing} onSave={salva} />}
      {slotEditing && (
        <SlotMatrixModal corso={slotEditing} onClose={() => setSlotEditing(null)} onSave={(m) => salvaMaxPerSlot(slotEditing, m)} />
      )}
    </div>
  );
}

function SlotMatrixModal({ corso, onClose, onSave }) {
  const DAY_LABELS = ["Lun", "Mar", "Mer", "Gio", "Ven"];
  const giorni = corso.giorni_ammessi || [];
  const numGruppi = Math.max(1, Math.min(5, corso.num_gruppi || 1));
  const gruppi = Array.from({ length: numGruppi }, (_, i) => i + 1);
  const [matrix, setMatrix] = useState(() => {
    const m = { ...(corso.max_per_slot || {}) };
    for (const g of giorni) {
      for (const gr of gruppi) {
        const k = `${g}_${gr}`;
        if (!(k in m)) m[k] = 0;
      }
    }
    return m;
  });
  const set = (g, gr, v) => {
    const n = Math.max(0, parseInt(v || 0, 10) || 0);
    setMatrix(prev => ({ ...prev, [`${g}_${gr}`]: n }));
  };
  const gruppoLabels = {
    1: numGruppi === 1 ? "UNICO" : "cl. 1-2",
    2: "cl. 3-4-5",
    3: "Gr. 3",
    4: "Gr. 4",
    5: "Gr. 5",
  };
  const gruppoBg = (gr) => gr === 1 ? "#fff3e0" : gr === 2 ? "#e8eaf6" : "#f5f5f5";
  const gruppoFg = (gr) => gr === 1 ? "#e65100" : gr === 2 ? "#1a237e" : "#555";
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} data-testid="mm-corsi-slot-modal"
        style={{ background: "white", width: "100%", maxWidth: 620, borderRadius: 4, overflow: "hidden" }}>
        <div style={{ background: "#ef6c00", color: "white", padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>⚙ Max iscritti per slot — {corso.codice} · {corso.descrizione}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "white", fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ padding: 18 }}>
          <div style={{ fontSize: 12, color: "#555", marginBottom: 12, padding: 10, background: "#fff3e0", border: "1px solid #ffcc80" }}>
            Imposta un limite separato per ogni combinazione <b>Giorno × Gruppo</b>. Il corso ha <b>{numGruppi} gruppo/i</b>{numGruppi === 2 ? " (Gruppo 1 = classi 1-2, Gruppo 2 = classi 3-4-5)" : numGruppi === 1 ? " (tutti gli iscritti nel gruppo unico)" : ""}.
            Imposta 0 per nessun limite su quella cella.
          </div>
          <table className="sw-table sw-table-compact" style={{ fontSize: 12, width: "100%" }}>
            <thead style={{ background: "#1f3864", color: "white" }}>
              <tr>
                <th style={{ textAlign: "left", padding: "6px 10px" }}>GIORNO</th>
                {gruppi.map(gr => (
                  <th key={gr} style={{ textAlign: "center", padding: "6px 10px", background: gruppoBg(gr), color: gruppoFg(gr) }}>
                    GRUPPO {gr} ({gruppoLabels[gr]})
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {giorni.map(g => (
                <tr key={g}>
                  <td style={{ padding: "6px 10px", fontWeight: 700, color: "#0d47a1" }}>{DAY_LABELS[g]}</td>
                  {gruppi.map(gr => (
                    <td key={gr} style={{ padding: "4px 8px", textAlign: "center" }}>
                      <input type="number" min={0} value={matrix[`${g}_${gr}`] ?? 0}
                        onChange={e => set(g, gr, e.target.value)}
                        data-testid={`mm-corsi-slot-${g}-${gr}`}
                        style={{ width: 80, padding: 6, border: "1px solid #ccc", fontSize: 13, fontWeight: 700, textAlign: "center", fontFamily: "Consolas, monospace" }} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ padding: "10px 18px", borderTop: "1px solid #eee", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={{ padding: "7px 14px", background: "white", color: "#666", border: "1px solid #ccc", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Annulla</button>
          <button onClick={() => onSave(matrix)} data-testid="mm-corsi-slot-save"
            style={{ padding: "7px 18px", background: "#ef6c00", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>💾 Salva slot</button>
        </div>
      </div>
    </div>
  );
}

function CorsoEditModal({ editing, setEditing, onSave }) {
  const toggleDay = (wd) => {
    if (editing.giorno_fisso != null) return; // bloccato se giorno fisso
    const next = editing.giorni_ammessi.includes(wd) ? editing.giorni_ammessi.filter(d => d !== wd) : [...editing.giorni_ammessi, wd].sort();
    setEditing({ ...editing, giorni_ammessi: next });
  };
  const toggleSettore = (s) => {
    const cur = Array.isArray(editing.settori) ? editing.settori : [];
    const next = cur.includes(s) ? cur.filter(x => x !== s) : [...cur, s].sort();
    setEditing({ ...editing, settori: next });
  };
  const setPrezzo = (n, val) => {
    const prezzi = { ...(editing.prezzi || {}) };
    if (val === "" || val === null) delete prezzi[String(n)];
    else prezzi[String(n)] = val;
    setEditing({ ...editing, prezzi });
  };
  const setGiornoFisso = (val) => {
    if (val === "" || val === null) {
      setEditing({ ...editing, giorno_fisso: null });
    } else {
      const n = Number(val);
      setEditing({ ...editing, giorno_fisso: n, giorni_ammessi: [n] });
    }
  };
  // Auto-suggerimento settori dalla 1ª lettera della descrizione (solo se settori vuoti)
  const onDescChange = (v) => {
    const settoriCur = Array.isArray(editing.settori) ? editing.settori : [];
    let settori = settoriCur;
    if (settoriCur.length === 0 && v) {
      const first = v.trim().charAt(0).toUpperCase();
      if (first === "P") settori = ["E", "F"];
      else if (first === "S") settori = ["M"];
    }
    setEditing({ ...editing, descrizione: v, settori });
  };
  const settoriSel = Array.isArray(editing.settori) ? editing.settori : [];
  const nGiorni = editing.giorno_fisso != null ? 1 : editing.giorni_ammessi.length;
  return (
    <div onClick={() => setEditing(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 600, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "white", width: 680, borderRadius: 4, overflow: "hidden", maxHeight: "92vh", display: "flex", flexDirection: "column" }}>
        <div style={{ background: "#8e24aa", color: "white", padding: "12px 18px", fontWeight: 700, fontSize: 14, display: "flex", justifyContent: "space-between" }}>
          {editing._isNew ? "➕ Nuovo Corso" : `✏ Modifica Corso ${editing._orig_codice}`}
          <button onClick={() => setEditing(null)} style={{ background: "none", border: "none", color: "white", fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ padding: 18, overflowY: "auto", flex: 1 }}>
          <div style={{ display: "grid", gridTemplateColumns: "120px 1fr 90px", gap: 10, marginBottom: 14 }}>
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: "#666" }}>CODICE *</label>
              <input value={editing.codice || ""} onChange={e => setEditing({ ...editing, codice: e.target.value.toUpperCase() })}
                disabled={!editing._isNew}
                data-testid="mm-corso-codice"
                style={{ width: "100%", padding: 6, border: "1px solid #ccc", fontSize: 12, fontFamily: "Consolas, monospace", fontWeight: 700, textTransform: "uppercase" }} />
            </div>
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: "#666" }}>DESCRIZIONE *</label>
              <input value={editing.descrizione || ""} onChange={e => onDescChange(e.target.value)}
                data-testid="mm-corso-descr"
                style={{ width: "100%", padding: 6, border: "1px solid #ccc", fontSize: 12 }} />
            </div>
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: "#666" }}>ANNO *</label>
              <input type="number" value={editing.anno ?? ""} onChange={e => setEditing({ ...editing, anno: Number(e.target.value) })}
                style={{ width: "100%", padding: 6, border: "1px solid #ccc", fontSize: 12, fontFamily: "Consolas, monospace" }} />
            </div>
          </div>

          {/* ORARIO + ORDINAMENTO: visualizzati nel portale alunni */}
          <div style={{ marginBottom: 14, display: "grid", gridTemplateColumns: "1fr 130px", gap: 10 }}>
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: "#666", display: "block", marginBottom: 4 }}>
                🕐 ORARIO <span style={{ color: "#999", fontWeight: 400, textTransform: "none" }}>(testo libero — apparirà sul bottone iscrizione nel portale alunni)</span>
              </label>
              <input value={editing.orario || ""}
                onChange={e => setEditing({ ...editing, orario: e.target.value })}
                placeholder="Es. MAR 14:00–16:00 + GIO 14:00–16:00"
                data-testid="mm-corso-orario"
                style={{ width: "100%", padding: 7, border: "1px solid #ccc", fontSize: 13, fontWeight: 600, color: "#0d47a1", background: "#f5fbff" }} />
            </div>
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: "#666", display: "block", marginBottom: 4 }}>
                🔢 ORDINAMENTO <span style={{ color: "#999", fontWeight: 400, textTransform: "none" }}>(0 = primo)</span>
              </label>
              <input type="number" value={editing.ordinamento ?? 0}
                onChange={e => setEditing({ ...editing, ordinamento: Number(e.target.value) })}
                placeholder="0"
                data-testid="mm-corso-ordinamento"
                title="Posizione del corso nel portale alunni (asc). 0 = primo. Pareggio → ordine alfabetico per codice."
                style={{ width: "100%", padding: 7, border: "1px solid #ccc", fontSize: 13, fontWeight: 700, color: "#0d47a1", background: "#f5fbff", textAlign: "center", fontFamily: "Consolas, monospace" }} />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 14, marginBottom: 14 }}>
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: "#666", display: "block", marginBottom: 4 }}>SETTORI * (multipli)</label>
              <div style={{ display: "flex", gap: 6 }}>
                {["A", "E", "F", "M"].map(s => (
                  <button key={s} onClick={() => toggleSettore(s)}
                    data-testid={`mm-corso-sett-${s}`}
                    style={{ flex: 1, padding: "6px 0", fontSize: 12, fontWeight: 700,
                      border: `2px solid ${settoriSel.includes(s) ? "#8e24aa" : "#ccc"}`,
                      background: settoriSel.includes(s) ? "#8e24aa" : "white",
                      color: settoriSel.includes(s) ? "white" : "#666", cursor: "pointer" }}>{s}</button>
                ))}
              </div>
              <div style={{ fontSize: 9, color: "#888", marginTop: 3, fontStyle: "italic" }}>
                Suggerimento: descrizione "P…" → E+F · "S…" → M
              </div>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 14, marginBottom: 14 }}>
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: "#666", display: "block", marginBottom: 4 }}>
                SETTORE CURRICULUM * (P = Primaria · M = Medie)
              </label>
              <div style={{ display: "flex", gap: 6 }}>
                {[{ k: "", l: "—" }, { k: "P", l: "P (Primaria)" }, { k: "M", l: "M (Medie)" }].map(opt => (
                  <button key={opt.k || "none"} onClick={() => setEditing({ ...editing, settore_curriculum: opt.k })}
                    data-testid={`mm-corso-sett-curr-${opt.k || "none"}`}
                    style={{ flex: 1, padding: "6px 0", fontSize: 12, fontWeight: 700,
                      border: `2px solid ${(editing.settore_curriculum || "") === opt.k ? "#1565c0" : "#ccc"}`,
                      background: (editing.settore_curriculum || "") === opt.k ? "#1565c0" : "white",
                      color: (editing.settore_curriculum || "") === opt.k ? "white" : "#666", cursor: "pointer" }}>{opt.l}</button>
                ))}
              </div>
              <div style={{ fontSize: 9, color: "#888", marginTop: 3, fontStyle: "italic" }}>
                Filtra il portale iscrizioni in base al Curriculum dell&apos;alunno (I/P/M/U). Senza valore, il corso NON è visibile a nessun alunno.
              </div>
            </div>
          </div>

          <div style={{ marginBottom: 14, display: "grid", gridTemplateColumns: "1fr 1fr 140px", gap: 14 }}>
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: "#666", display: "block", marginBottom: 4 }}>CREA PRESENZA MENSA?</label>
              <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", border: `2px solid ${editing.crea_mensa !== false ? "#2e7d32" : "#c62828"}`, background: editing.crea_mensa !== false ? "#e8f5e9" : "#ffebee", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                <input type="checkbox" checked={editing.crea_mensa !== false}
                  onChange={e => setEditing({ ...editing, crea_mensa: e.target.checked })}
                  data-testid="mm-corso-crea-mensa" />
                <span>{editing.crea_mensa !== false ? "🍽️ Sì, iscriverò in mensa" : "✗ No, solo registrazione"}</span>
              </label>
              <div style={{ fontSize: 9, color: "#888", marginTop: 3, fontStyle: "italic" }}>
                Disattiva per corsi che non generano mensa (es. <b>Prescuola</b>).
              </div>
            </div>
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: "#666", display: "block", marginBottom: 4 }}>SELEZIONE GIORNI</label>
              <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", border: `2px solid ${editing.tutti_giorni_obbligatori ? "#7b1fa2" : "#1565c0"}`, background: editing.tutti_giorni_obbligatori ? "#f3e5f5" : "#e3f2fd", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                <input type="checkbox" checked={!!editing.tutti_giorni_obbligatori}
                  onChange={e => setEditing({ ...editing, tutti_giorni_obbligatori: e.target.checked })}
                  data-testid="mm-corso-tutti-giorni" />
                <span>{editing.tutti_giorni_obbligatori ? "📅 Tutti i giorni obbligatori" : "🎯 Alunno sceglie i giorni"}</span>
              </label>
              <div style={{ fontSize: 9, color: "#888", marginTop: 3, fontStyle: "italic" }}>
                Se attivo, all&apos;iscrizione l&apos;alunno riceverà <b>tutti i giorni</b> ammessi senza poter scegliere (es. <b>Prescuola</b>).
              </div>
            </div>
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: "#666", display: "block", marginBottom: 4 }}>MAX ISCRITTI</label>
              <input type="number" min="0" step="1"
                value={editing.max_iscritti ?? 0}
                onChange={e => setEditing({ ...editing, max_iscritti: Math.max(0, parseInt(e.target.value || "0", 10)) })}
                data-testid="mm-corso-max"
                placeholder="0 = illimitato"
                style={{ width: "100%", padding: 6, border: "1px solid #ccc", fontSize: 12, fontFamily: "Consolas, monospace", fontWeight: 700, textAlign: "right" }} />
              <div style={{ fontSize: 9, color: "#888", marginTop: 3, fontStyle: "italic" }}>
                Oltre al max → iscrizione in lista d'attesa. 0 = illimitato.
              </div>
            </div>
          </div>

          <div style={{ marginBottom: 6, fontSize: 11, fontWeight: 700, color: "#666" }}>GIORNI AMMESSI *</div>
          <div style={{ display: "flex", gap: 6, marginBottom: 18 }}>
            {DAYS.map((d, wd) => (
              <button key={wd} onClick={() => toggleDay(wd)}
                data-testid={`mm-corso-day-${wd}`}
                style={{ padding: "10px 14px", border: `2px solid ${editing.giorni_ammessi.includes(wd) ? "#8e24aa" : "#ccc"}`,
                  background: editing.giorni_ammessi.includes(wd) ? "#8e24aa" : "white",
                  color: editing.giorni_ammessi.includes(wd) ? "white" : "#666",
                  cursor: "pointer",
                  fontSize: 12, fontWeight: 700, flex: 1 }}>{d}</button>
            ))}
          </div>
          <div style={{ fontSize: 9, color: "#888", marginTop: -10, marginBottom: 14, fontStyle: "italic" }}>
            Per un corso a giorno unico, spunta UN solo giorno. Per corsi multi-giorno, spuntali tutti.
          </div>

          {/* ============== CLASSI AMMESSE ============== */}
          <div style={{ marginBottom: 14, padding: 10, background: "#fff3e0", border: "1px solid #ffb74d", borderRadius: 4 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: "#bf360c", marginBottom: 6, letterSpacing: 0.3 }}>🎓 CLASSI AMMESSE</div>
            <div style={{ fontSize: 10, color: "#666", marginBottom: 8, fontStyle: "italic" }}>
              Seleziona le classi (1-5) ammesse al corso. <b>Nessuna selezione = tutte le classi del settore.</b><br/>
              Esempio: English Club 3-4 → seleziona 3 e 4 · English Club 5 → seleziona solo 5.
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
              {[1, 2, 3, 4, 5].map(n => {
                const sel = (editing.classi_ammesse || []).includes(n);
                return (
                  <button key={n} onClick={() => {
                    const cur = editing.classi_ammesse || [];
                    setEditing({ ...editing, classi_ammesse: sel ? cur.filter(x => x !== n) : [...cur, n].sort((a, b) => a - b) });
                  }}
                    data-testid={`mm-corso-cls-${n}`}
                    style={{ width: 44, padding: "8px 0", border: `2px solid ${sel ? "#ef6c00" : "#ccc"}`,
                      background: sel ? "#ef6c00" : "white", color: sel ? "white" : "#666",
                      fontSize: 13, fontWeight: 800, cursor: "pointer" }}>{n}</button>
                );
              })}
              <button onClick={() => setEditing({ ...editing, classi_ammesse: [] })}
                data-testid="mm-corso-cls-clear"
                style={{ marginLeft: 8, padding: "5px 10px", background: "white", color: "#666", border: "1px solid #ccc", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>Tutte</button>
              <span style={{ fontSize: 11, color: "#bf360c", fontWeight: 700, marginLeft: 8 }}>
                {(editing.classi_ammesse || []).length === 0 ? "→ Tutte le classi" : `→ Solo classi ${(editing.classi_ammesse || []).join(", ")}`}
              </span>
            </div>
          </div>

          {/* ============== TARIFFE (varianti come da listino Excel) ============== */}
          <div style={{ marginTop: 8, paddingTop: 14, borderTop: "2px solid #1565c0" }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: "#0d47a1", marginBottom: 8, letterSpacing: 0.4 }}>💰 TARIFFE</div>
            <div style={{ fontSize: 10, color: "#666", marginBottom: 8, fontStyle: "italic" }}>
              Una riga per ogni opzione disponibile in base al <b>numero di giorni</b> che l'alunno sceglie.<br/>
              Esempi: corso a giorno fisso → 1 sola riga con <b>NR GG = 1</b>. Doposcuola Primaria → 2 righe (1gg / 2gg). Doposcuola Secondaria → fino a 5 righe.
            </div>
            <div style={{ display: "flex", gap: 6, fontSize: 9, fontWeight: 800, color: "#1565c0", marginBottom: 4, letterSpacing: 0.3 }}>
              <div style={{ flex: 3 }}>ETICHETTA</div>
              <div style={{ width: 60, textAlign: "center" }}>NR GG</div>
              <div style={{ width: 100, textAlign: "right" }}>IMPORTO RATA €</div>
              <div style={{ width: 40 }} />
            </div>
            {(editing.tariffe || []).map((t, i) => (
              <div key={i} style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6 }}>
                <input type="text" value={t.etichetta || ""} placeholder="Etichetta (es. 2 GG MAR E GIO)"
                  onChange={e => { const a = [...editing.tariffe]; a[i] = { ...a[i], etichetta: e.target.value.toUpperCase() }; setEditing({ ...editing, tariffe: a }); }}
                  style={{ flex: 3, padding: 6, border: "1px solid #ccc", fontSize: 12 }} />
                <input type="number" value={t.nr_giorni ?? 1} placeholder="GG"
                  onChange={e => { const a = [...editing.tariffe]; a[i] = { ...a[i], nr_giorni: e.target.value }; setEditing({ ...editing, tariffe: a }); }}
                  style={{ width: 60, padding: 6, border: "1px solid #ccc", fontSize: 12, fontFamily: "monospace", textAlign: "center" }} />
                <input type="number" step="0.01" value={t.importo ?? 0} placeholder="Importo rata €"
                  onChange={e => { const a = [...editing.tariffe]; a[i] = { ...a[i], importo: e.target.value }; setEditing({ ...editing, tariffe: a }); }}
                  style={{ width: 100, padding: 6, border: "1px solid #ccc", fontSize: 13, fontFamily: "monospace", textAlign: "right", fontWeight: 700, color: "#1b5e20" }} />
                <button onClick={() => setEditing({ ...editing, tariffe: editing.tariffe.filter((_, j) => j !== i) })}
                  style={{ padding: "4px 10px", background: "white", color: "#c62828", border: "1px solid #c62828", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>✕</button>
              </div>
            ))}
            <button onClick={() => setEditing({ ...editing, tariffe: [...(editing.tariffe || []), { etichetta: "", nr_giorni: 1, importo: 0 }] })}
              style={{ padding: "5px 12px", background: "#1565c0", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
              + Aggiungi tariffa
            </button>
          </div>

          {/* ============== RATE (scadenze di pagamento) ============== */}
          <div style={{ marginTop: 18, paddingTop: 14, borderTop: "2px solid #558b2f" }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: "#33691e", marginBottom: 8, letterSpacing: 0.4 }}>📅 RATE DI PAGAMENTO</div>
            <div style={{ fontSize: 10, color: "#666", marginBottom: 8, fontStyle: "italic" }}>
              Es. "ISCRIZIONE" (nessuna scadenza) · "2 RATA" scadenza "28/02/2026". Schema condiviso da tutte le tariffe.
            </div>
            {(editing.rate || []).map((r, i) => (
              <div key={i} style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6 }}>
                <input type="text" value={r.nome || ""} placeholder="Nome rata (es. ISCRIZIONE)"
                  onChange={e => { const a = [...editing.rate]; a[i] = { ...a[i], nome: e.target.value.toUpperCase() }; setEditing({ ...editing, rate: a }); }}
                  style={{ flex: 2, padding: 6, border: "1px solid #ccc", fontSize: 12 }} />
                <input type="text" value={r.scadenza || ""} placeholder="DD/MM/YYYY o vuoto"
                  onChange={e => { const a = [...editing.rate]; a[i] = { ...a[i], scadenza: e.target.value }; setEditing({ ...editing, rate: a }); }}
                  style={{ flex: 1, padding: 6, border: "1px solid #ccc", fontSize: 12, fontFamily: "monospace" }} />
                <button onClick={() => setEditing({ ...editing, rate: editing.rate.filter((_, j) => j !== i) })}
                  style={{ padding: "4px 10px", background: "white", color: "#c62828", border: "1px solid #c62828", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>✕</button>
              </div>
            ))}
            <button onClick={() => setEditing({ ...editing, rate: [...(editing.rate || []), { nome: "", scadenza: "" }] })}
              style={{ padding: "5px 12px", background: "#558b2f", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
              + Aggiungi rata
            </button>
          </div>
        </div>
        <div style={{ padding: "10px 18px", borderTop: "1px solid #eee", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={() => setEditing(null)} style={{ padding: "6px 14px", background: "white", color: "#666", border: "1px solid #ccc", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Annulla</button>
          <button onClick={onSave} data-testid="mm-corso-save"
            style={{ padding: "6px 18px", background: "#8e24aa", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>💾 Salva</button>
        </div>
      </div>
    </div>
  );
}

// ========================= ISCRIZIONI (extra / famiglia) =========================

export function IscrizioniView({ kind, initialFocus }) {
  const prefix = kind === "extra" ? "extra-iscrizioni" : "famiglia-ricorrenti";
  const title = kind === "extra" ? "🎨 Iscrizioni Extracurriculari" : "👨‍👩‍👧 Richieste Famiglie";
  const color = kind === "extra" ? "#ef6c00" : "#8e24aa";
  const { annoCorrente } = useAuth();
  // 🆕 Anno Scolastico globale attivo (context)
  const { anno: annoGlobal, setAnno: setAnnoGlobal } = useAnnoScolastico();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editErr, setEditErr] = useState(null);
  const [err, setErr] = useState(null);
  const [tableSearch, setTableSearch] = useState("");
  const [filterCorso, setFilterCorso] = useState(""); // filtro per corso (solo extra)
  const [filterAttesa, setFilterAttesa] = useState("all"); // all | attivi | attesa
  const [filterGiorno, setFilterGiorno] = useState(""); // "" | "0".."4"
  const [filterGruppo, setFilterGruppo] = useState(""); // "" | "1" | "2"
  const [sortMode, setSortMode] = useState("created"); // "created" | "alpha"
  const [corsiLookup, setCorsiLookup] = useState([]); // per dropdown filtro corso
  const [anno, setAnno] = useState(annoGlobal);
  const [anni, setAnni] = useState([]);
  const [wizardOpen, setWizardOpen] = useState(false);
  // 🆕 Sync anno LOCALE ← GLOBALE: quando cambio A.S. dal selettore header, il filtro locale segue
  useEffect(() => {
    if (annoGlobal != null && annoGlobal !== anno) {
      setAnno(annoGlobal);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annoGlobal]);
  const [importOpen, setImportOpen] = useState(false);
  const [logAlunno, setLogAlunno] = useState(null);
  const [mailConfermaIscr, setMailConfermaIscr] = useState(null);
  // Anno aperto per iscrizioni (override globale impostato dall'admin)
  const [iscrizioniCfg, setIscrizioniCfg] = useState({ anno_iscrizioni: null, updated_at: null, updated_by: null });
  // Stato modal invio email
  const [sendModal, setSendModal] = useState(null);
  const [sendTemplates, setSendTemplates] = useState([]);
  const [emailCfgState, setEmailCfgState] = useState({ resend_configured: false });
  // 🆕 Movimenti collegati (badge tricolore + modal)
  const [movimentiCounts, setMovimentiCounts] = useState({}); // { iid: {total, paid} }
  const [movimentiModal, setMovimentiModal] = useState(null); // { iscr, data } | null
  // 🆕 Popup dedicato per impostare la data recesso (colonna rimossa)
  const [recessoModal, setRecessoModal] = useState(null); // { it, value } | null

  // 🆕 Apre il modal con i movimenti contabili collegati all'iscrizione
  const apriMovimenti = async (it) => {
    try {
      const r = await api.get(`/mensa-matrice/extra-iscrizioni/${it._id}/movimenti-collegati`);
      setMovimentiModal({ iscr: it, data: r.data });
    } catch (e) { setErr(e.response?.data?.detail || "Errore caricamento movimenti"); }
  };

  const apriInvioEmail = async (it) => {
    try {
      const [r, rcfg] = await Promise.all([
        api.get("/mensa-matrice/email-templates"),
        api.get("/mensa-matrice/email-config"),
      ]);
      setSendTemplates(r.data.items || []);
      setEmailCfgState({ resend_configured: !!rcfg.data.resend_configured });
      // 🆕 Supporta preselect template (es. "sollecito_pagamento" dal bottone Sollecito).
      const wantedId = it?._preselect_template;
      const defTpl = (wantedId && (r.data.items || []).find(t => t.id === wantedId))
        || (r.data.items || []).find(t => t.id === "accettazione_pagamento")
        || (r.data.items || [])[0];
      setSendModal({
        iscrizione: it, selectedTpl: defTpl?.id || "",
        toOverride: it.email || "", subject: defTpl?.subject || "", body: defTpl?.body || "",
        anteprima: null, sending: false,
      });
      if (defTpl) await caricaAnteprima(defTpl.id, it._id);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento modelli");
    }
  };
  const caricaAnteprima = async (tid, iscr_id) => {
    try {
      const r = await api.post(`/mensa-matrice/email-templates/${tid}/anteprima`, { iscrizione_id: iscr_id });
      setSendModal(prev => prev ? ({ ...prev, anteprima: r.data, toOverride: r.data.to || prev.toOverride, subject: r.data.subject, body: r.data.body }) : prev);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore anteprima email");
    }
  };
  const inviaEmail = async () => {
    if (!sendModal) return;
    setSendModal({ ...sendModal, sending: true });
    try {
      const r = await api.post(`/mensa-matrice/email-templates/${sendModal.selectedTpl}/invia`, {
        iscrizione_id: sendModal.iscrizione._id,
        to_override: sendModal.toOverride,
        subject_override: sendModal.subject,
        body_override: sendModal.body,
      });
      setSendModal(null);
      if (r.data.status === "sent") {
        alert(`✓ Email inviata via Resend!\n\nDestinatario: ${r.data.to}\nMessage ID: ${r.data.provider_message_id || "—"}`);
      } else if (r.data.mocked || r.data.status === "sent_mock") {
        alert(`📧 Email registrata in outbox (MOCK)\n\n⚠ Resend non configurato — l'invio reale non è avvenuto.\n\nDestinatario: ${r.data.to}`);
      } else if (r.data.status === "error") {
        alert(`❌ Errore Resend:\n${r.data.error}`);
      } else {
        alert(r.data.message || "Operazione completata");
      }
      await carica();
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore invio email");
      setSendModal(prev => prev ? ({ ...prev, sending: false }) : prev);
    }
  };

  const caricaIscrizioniCfg = async () => {
    try {
      const r = await api.get("/mensa-matrice/iscrizioni-config");
      setIscrizioniCfg(r.data || { anno_iscrizioni: null });
    } catch { /* ignore */ }
  };
  const salvaIscrizioniCfg = async (newAnno) => {
    try {
      await api.put("/mensa-matrice/iscrizioni-config", { anno_iscrizioni: newAnno });
      await caricaIscrizioniCfg();
      // Allinea il filtro anno della tabella al nuovo override (solo se valorizzato)
      if (newAnno != null) setAnno(newAnno);
    } catch (e) { setErr(e.response?.data?.detail || "Errore salvataggio config iscrizioni"); }
  };

  // Gruppo doposcuola primaria: dipende dal num_gruppi del corso
  // num_gruppi=1 → tutti in gruppo 1; num_gruppi=2 → cl.1-2=1, cl.3-4-5=2; 3+ → mapping per classe
  const gruppoPerIscrizione = (classe, numGruppi) => {
    if (!numGruppi || numGruppi < 2) return 1;
    if (!classe) return null;
    const m = String(classe).match(/^(\d)/);
    if (!m) return null;
    const n = Number(m[1]);
    if (numGruppi === 2) {
      if (n === 1 || n === 2) return 1;
      if (n === 3 || n === 4 || n === 5) return 2;
      return null;
    }
    // 3+: mappa lineare (fallback)
    return Math.min(numGruppi, Math.max(1, n));
  };
  const numGruppiByCorso = useMemo(() => {
    const m = {};
    for (const c of corsiLookup) m[c.codice] = Number(c.num_gruppi || 1);
    return m;
  }, [corsiLookup]);
  // Gruppo "naturale" per visualizzazione in tabella, derivato sempre dalla classe
  // (1-2 → 1, 3-4-5 → 2). Indipendente dal num_gruppi del corso. Serve solo per
  // mostrare a colpo d'occhio in che fascia rientra l'alunno della scuola primaria.
  const gruppoVisualOf = (it) => {
    if (!it || !it.classe) return null;
    const mm = String(it.classe).match(/^(\d)/);
    if (!mm) return null;
    const n = Number(mm[1]);
    if (n === 1 || n === 2) return 1;
    if (n === 3 || n === 4 || n === 5) return 2;
    return null;
  };
  const gruppoOf = (it) => gruppoPerIscrizione(it.classe, numGruppiByCorso[it.corso_codice] || 1);

  // Giorni e gruppi effettivamente presenti nel dataset corrente
  const availableGiorni = useMemo(() => {
    const s = new Set();
    for (const it of items) {
      for (const g of (it.giorni || [])) s.add(g);
    }
    return Array.from(s).sort();
  }, [items]);
  const availableGruppi = useMemo(() => {
    const s = new Set();
    for (const it of items) {
      const g = gruppoOf(it);
      if (g != null) s.add(g);
    }
    return Array.from(s).sort();
  }, [items, numGruppiByCorso]);

  // Graduatoria per corso (in base a created_at asc): dopo max_iscritti → lista attesa
  const maxByCorso = useMemo(() => {
    const m = {};
    for (const c of corsiLookup) m[c.codice] = Number(c.max_iscritti || 0);
    return m;
  }, [corsiLookup]);
  const maxPerSlotByCorso = useMemo(() => {
    const m = {};
    for (const c of corsiLookup) m[c.codice] = c.max_per_slot || {};
    return m;
  }, [corsiLookup]);
  const waitlistByIdComputed = useMemo(() => {
    const out = {};
    if (kind !== "extra") return out;
    // Raggruppa per (corso_codice, anno): le graduatorie sono per ANNO scolastico,
    // perché lo stesso corso può ripetersi in più anni con max_iscritti diverso.
    const byCorso = {};
    for (const it of items) {
      if ((it.data_recesso || "").trim()) { out[it._id] = false; continue; } // recessi esclusi dalla graduatoria
      const k = `${it.corso_codice || ""}__${it.anno ?? ""}`;
      if (!byCorso[k]) byCorso[k] = [];
      byCorso[k].push(it);
    }
    for (const [keyFull, arr] of Object.entries(byCorso)) {
      const corso = keyFull.split("__")[0];
      const perSlot = maxPerSlotByCorso[corso] || {};
      const hasPerSlot = Object.values(perSlot).some(v => Number(v) > 0);
      const sorted = [...arr].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
      if (hasPerSlot) {
        // Conta per slot (giorno_gruppo). Un item è waitlist se almeno uno dei suoi slot è pieno.
        const counts = {};
        for (const it of sorted) {
          const gr = gruppoOf(it);
          let isWait = false;
          for (const g of (it.giorni || [])) {
            if (gr == null) continue;
            const key = `${g}_${gr}`;
            const max = Number(perSlot[key] || 0);
            counts[key] = (counts[key] || 0) + 1;
            if (max > 0 && counts[key] > max) isWait = true;
          }
          out[it._id] = isWait;
        }
      } else {
        const max = maxByCorso[corso] || 0;
        sorted.forEach((it, idx) => { out[it._id] = max > 0 && idx >= max; });
      }
    }
    return out;
  }, [items, maxByCorso, maxPerSlotByCorso, numGruppiByCorso, kind]);

  const caricaAnni = async () => {
    try {
      const r = await api.get("/mensa-matrice/anni");
      const arr = r.data.anni || [];
      setAnni(arr);
      if (anno === null && arr.length) {
        // Priorità:
        // 1) `annoGlobal` — A.S. attivo condiviso dal header (VINCE SEMPRE se impostato)
        // 2) `iscrizioniCfg.anno_iscrizioni` — anno forzato in Impostazioni Extra
        // 3) anno corrente sistema
        // 4) primo anno disponibile in lista
        const cfg = iscrizioniCfg?.anno_iscrizioni;
        if (annoGlobal != null && arr.includes(annoGlobal)) setAnno(annoGlobal);
        else if (cfg && arr.includes(cfg)) setAnno(cfg);
        else if (annoCorrente && arr.includes(annoCorrente)) setAnno(annoCorrente);
        else setAnno(arr[0]);
      }
    } catch { /* ignore */ }
  };

  const carica = async () => {
    setLoading(true);
    try {
      const params = {};
      if (anno != null) params.anno = anno;
      const r = await api.get(`/mensa-matrice/${prefix}`, { params });
      setItems(r.data.items || []);
      // 🚀 Bulk fetch movimenti collegati (badge tricolore) — parallelo, non blocca
      // il render dei prezzi/stato. La query è ora batch ottimizzata (~10x più veloce).
      if (kind === "extra") {
        const ids = (r.data.items || []).map(x => x._id).filter(Boolean);
        if (ids.length > 0) {
          // Fire-and-forget: non attendo qui, così la tabella appare subito.
          // Il badge apparirà quando arriva la risposta.
          setMovimentiCounts({});  // reset immediato
          api.post("/mensa-matrice/extra-iscrizioni/movimenti-counts", { ids })
            .then(rmc => setMovimentiCounts(rmc.data?.counts || {}))
            .catch(() => { /* ignore */ });
        } else {
          setMovimentiCounts({});
        }
      }
      // Lookup corsi: ora gestito in useEffect dedicato (ascolta [kind, anno, anni])
      // per evitare race condition quando `anni` arriva dopo il primo carica().
    } catch (e) { setErr(e.response?.data?.detail || "Errore"); }
    finally { setLoading(false); }
  };
  useEffect(() => { caricaIscrizioniCfg(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { caricaAnni(); /* eslint-disable-next-line */ }, [iscrizioniCfg]);
  useEffect(() => { carica(); /* eslint-disable-next-line */ }, [kind, anno]);

  // 🆕 Focus corso da tab "Log iscrizioni" (redirect con filtro pre-impostato)
  useEffect(() => {
    if (kind !== "extra") return;
    // 1) Prop diretto (preferenza)
    if (initialFocus && initialFocus.corso) {
      setFilterCorso(initialFocus.corso);
      if (initialFocus.anno != null && Number.isFinite(Number(initialFocus.anno))) {
        setAnno(Number(initialFocus.anno));
        setAnnoGlobal(Number(initialFocus.anno));
      }
      return;
    }
    // 2) Fallback sessionStorage (redirect da altre pagine)
    try {
      const c = sessionStorage.getItem("extra_focus_corso");
      const a = sessionStorage.getItem("extra_focus_anno");
      if (c) {
        setFilterCorso(c);
        sessionStorage.removeItem("extra_focus_corso");
      }
      if (a != null && a !== "") {
        const an = Number(a);
        if (Number.isFinite(an)) {
          setAnno(an);
          setAnnoGlobal(an);
        }
        sessionStorage.removeItem("extra_focus_anno");
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line
  }, [initialFocus, kind]);

  // 🆕 useEffect dedicato per il lookup corsi: ascolta [kind, anno, anni] così
  // si ri-esegue automaticamente quando `anni` viene caricato dopo (evita race
  // condition in cui il dropdown corsi resta vuoto in produzione).
  useEffect(() => {
    if (kind !== "extra") return;
    let cancelled = false;
    (async () => {
      try {
        if (anno != null) {
          const rc = await api.get("/mensa-matrice/corsi", { params: { anno } });
          if (!cancelled) setCorsiLookup(rc.data.items || []);
        } else if ((anni || []).length > 0) {
          const allCorsi = new Map();
          await Promise.all(anni.map(async (a) => {
            try {
              const rc = await api.get("/mensa-matrice/corsi", { params: { anno: a } });
              (rc.data.items || []).forEach(c => { if (c.codice && !allCorsi.has(c.codice)) allCorsi.set(c.codice, c); });
            } catch { /* ignore */ }
          }));
          if (!cancelled) setCorsiLookup([...allCorsi.values()].sort((a, b) => (a.codice || "").localeCompare(b.codice || "")));
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [kind, anno, anni]);

  // Apertura wizard via sessionStorage (proviene dal pulsante "+ Nuova iscrizione" in Log iscrizioni)
  useEffect(() => {
    if (kind !== "extra") return;
    try {
      if (sessionStorage.getItem("extra_open_wizard") === "1") {
        sessionStorage.removeItem("extra_open_wizard");
        setWizardOpen(true);
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line
  }, [kind]);

  const salva = async () => {
    if (!editing) return;
    const body = { alunno_cod: editing.alunno_cod, giorni: editing.giorni };
    if (editing.anno != null) body.anno = editing.anno;
    if (kind === "extra") body.corso_codice = editing.corso_codice;
    if (kind === "fam") {
      body.note = editing.note;
      if (editing.dal) body.dal = editing.dal;
      if (editing.data_recesso !== undefined) body.data_recesso = editing.data_recesso || "";
    }
    setEditErr(null);
    try {
      if (editing._isNew) {
        await api.post(`/mensa-matrice/${prefix}`, body);
      } else {
        await api.put(`/mensa-matrice/${prefix}/${editing._id}`, body);
      }
      const annoSalvato = editing.anno;
      setEditing(null);
      setWizardOpen(false);
      // Se l'iscrizione è stata creata su un anno diverso da quello visualizzato, allineo la tabella
      if (annoSalvato != null && annoSalvato !== anno) {
        setAnno(annoSalvato); // triggera la useEffect che richiama carica()
      } else {
        await carica();
      }
    } catch (e) { setEditErr(e.response?.data?.detail || "Errore salvataggio"); }
  };

  const elimina = async (it) => {
    if (!window.confirm("Eliminare questa iscrizione?")) return;
    try {
      await api.delete(`/mensa-matrice/${prefix}/${it._id}`);
      await carica();
    } catch (e) { setErr(e.response?.data?.detail || "Errore"); }
  };

  const svuotaTutteFamiglia = async () => {
    if (kind !== "fam") return;
    const n = items.length;
    if (!n) { alert(`Nessuna richiesta famiglia per l'anno ${anno}`); return; }
    if (!window.confirm(`⚠ Cancellare TUTTE le ${n} richieste famiglia dell'anno ${anno}?\n\nOperazione IRREVERSIBILE.`)) return;
    const s = window.prompt(`Per confermare digita: ELIMINA ${n}`);
    if (s !== `ELIMINA ${n}`) { alert("Operazione annullata"); return; }
    try {
      const r = await api.delete("/mensa-matrice/famiglia-ricorrenti", { params: { anno, confirm: "DELETE" } });
      alert(`✓ Eliminate ${r.data.deleted} richieste`);
      await carica();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "errore sconosciuto";
      const status = e.response?.status || "?";
      alert(`❌ Errore cancellazione\nStatus: ${status}\nDettaglio: ${detail}\n\nSe lo Status è 404 o 405, devi fare il deploy per attivare il nuovo endpoint.`);
      setErr(detail);
    }
  };

  const filtered = useMemo(() => {
    let out = items;
    // 🆕 Difesa: filtra sempre lato client per anno quando un anno è selezionato,
    // così evitiamo che race condition fra cambio anno/cambio corso facciano
    // comparire iscrizioni di anni diversi sotto il corso selezionato.
    if (anno != null) {
      out = out.filter(it => Number(it.anno) === Number(anno));
    }
    // Per "extra": mostrare la lista solo dopo aver impostato almeno un filtro
    if (kind === "extra") {
      const hasAnyFilter = !!filterCorso || filterAttesa !== "all" || filterGiorno !== "" || filterGruppo !== "" || !!tableSearch.trim();
      if (!hasAnyFilter) return [];
    }
    if (kind === "extra" && filterCorso) {
      out = out.filter(it => (it.corso_codice || "") === filterCorso);
    }
    if (kind === "extra" && filterAttesa !== "all") {
      const wantAttesa = filterAttesa === "attesa";
      out = out.filter(it => !!waitlistByIdComputed[it._id] === wantAttesa);
    }
    if (filterGiorno !== "") {
      const wd = Number(filterGiorno);
      out = out.filter(it => (it.giorni || []).includes(wd));
    }
    if (kind === "extra" && filterGruppo !== "") {
      const gWant = Number(filterGruppo);
      out = out.filter(it => gruppoOf(it) === gWant);
    }
    const s = tableSearch.trim().toLowerCase();
    if (s) {
      out = out.filter(it => `${it.cognome} ${it.nome} ${it.attivita || ""} ${it.corso_codice || ""} ${it.corso_descrizione || ""}`.toLowerCase().includes(s));
    }
    // Sort
    out = [...out];
    if (sortMode === "created") {
      out.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    } else {
      out.sort((a, b) => (`${a.cognome} ${a.nome}`).localeCompare(`${b.cognome} ${b.nome}`, "it"));
    }
    return out;
  }, [items, tableSearch, filterCorso, filterAttesa, filterGiorno, filterGruppo, sortMode, kind, waitlistByIdComputed, anno]);

  // "Posizione per giorno": per ogni giorno della settimana (0=LUN ... 4=VEN), calcola
  // la posizione di ciascun iscritto rispetto agli altri iscritti che hanno scelto QUEL giorno,
  // ordinati per data/ora iscrizione (più vecchia prima). Esclude solo i recessi.
  // (Include anche quelli in lista d'attesa, così posso confrontarli con max_per_slot e capire
  //  visivamente chi è "fuori graduatoria" giorno per giorno.)
  // Calcolato sull'insieme `filtered` (rispecchia i filtri attivi).
  const giornoRanks = useMemo(() => {
    if (kind !== "extra") return {};
    const byDay = {};
    for (let wd = 0; wd < 5; wd++) {
      const arr = filtered
        .filter(it => Array.isArray(it.giorni) && it.giorni.includes(wd)
          && !(it.data_recesso || "").trim())
        .slice()
        .sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
      const m = {};
      arr.forEach((it, idx) => { m[it._id] = idx + 1; });
      byDay[wd] = m;
    }
    return byDay;
  }, [filtered, kind]);

  // 🆕 Numerazione progressiva che ESCLUDE i recessi: chi si è ritirato non prende un numero.
  const rowNumbers = useMemo(() => {
    const m = {};
    let n = 0;
    filtered.forEach((it) => {
      if ((it.data_recesso || "").trim()) return; // recesso → nessun numero
      n += 1;
      m[it._id] = n;
    });
    return m;
  }, [filtered]);

  // Massimo posti per (corso, giorno, gruppo) o fallback al max globale del corso.
  // Restituisce il limite numerico per la coppia (corso, giorno) e gruppo dell'alunno; 0 = illimitato.
  const slotMaxFor = (it, wd) => {
    if (kind !== "extra") return 0;
    const corso = it.corso_codice || "";
    const perSlot = maxPerSlotByCorso[corso] || {};
    const gr = gruppoOf(it);
    if (gr != null) {
      const key = `${wd}_${gr}`;
      const m = Number(perSlot[key] || 0);
      if (m > 0) return m;
    }
    return Number(maxByCorso[corso] || 0);
  };

  // 🆕 Esportazione Excel della tabella FILTRATA (rispecchia esattamente i filtri attivi)
  const exportExcel = () => {
    if (filtered.length === 0) { alert("Nessuna riga da esportare con i filtri attuali."); return; }
    const rows = filtered.map((it, i) => {
      const isRecesso = !!(it.data_recesso || "").trim();
      const isWait = kind === "extra" ? (waitlistByIdComputed[it._id] ?? !!it.in_lista_attesa) : !!it.in_lista_attesa;
      const stato = isRecesso ? "Recesso" : (it.stato_iscrizione === "pagata" ? "Pagata" : (isWait ? "Lista attesa" : "Iscritto"));
      let dataOra = "";
      try { if (it.created_at) dataOra = new Date(it.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { /* ignore */ }
      const giorni = (it.giorni || []).map(w => DAY_NAMES[w] || "").filter(Boolean).join(", ");
      return {
        "N.": i + 1,
        "Data/Ora richiesta": dataOra,
        "Anno": fmtAnno(it.anno),
        "Cognome": it.cognome || "",
        "Nome": it.nome || "",
        "Cod. Alunno": it.alunno_cod || "",
        "Settore": it.settore || "",
        "Classe": it.classe || "",
        "Gruppo": gruppoVisualOf(it) || "",
        "Corso": it.corso_codice || "",
        "Descrizione": it.corso_descrizione || it.attivita || "",
        "Prezzo €": it.prezzo != null && it.prezzo !== "" ? Number(it.prezzo) : "",
        "Stato": stato,
        "Giorni": giorni,
        "Telefono": it.telefono || "",
        "Email": it.email || "",
        "Email Canossa": it.email_canossa || "",
        "Data recesso": it.data_recesso || "",
      };
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [
      { wch: 5 }, { wch: 18 }, { wch: 9 }, { wch: 18 }, { wch: 16 }, { wch: 10 },
      { wch: 7 }, { wch: 8 }, { wch: 7 }, { wch: 12 }, { wch: 28 }, { wch: 9 },
      { wch: 13 }, { wch: 22 }, { wch: 14 }, { wch: 26 }, { wch: 26 }, { wch: 12 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Corsi Alunni");
    const corsoTag = filterCorso ? `_${filterCorso}` : "";
    XLSX.writeFile(wb, `corsi_alunni_${fmtAnno(anno)}${corsoTag}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <div>
      {kind === "extra" ? (
        <>
          {/* Bottoni per corso con contatori (iscritti/max) — visione immediata.
              Visualizzati solo quando è selezionato uno specifico anno scolastico:
              i contatori sono SEMPRE riferiti a quell'anno (corsi + iscrizioni). */}
          {anno != null && corsiLookup.length > 0 && (
            <div style={{ margin: "12px 20px 0", padding: 10, background: "#f5f5f5", borderRadius: "4px 4px 0 0", border: "1px solid #e0e0e0", borderBottom: "none", display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}
                 data-testid="mm-corso-chips">
              <span style={{ fontSize: 10, fontWeight: 800, color: "#0d47a1", letterSpacing: 0.4, marginRight: 4 }}>
                {fmtAnno(anno)}
              </span>
              <button onClick={() => { setFilterCorso(""); setFilterAttesa("all"); setFilterGiorno(""); setFilterGruppo(""); setTableSearch(""); }}
                data-testid="mm-corso-chip-all"
                style={{ padding: "4px 12px",
                         background: !filterCorso ? "#0d47a1" : "white",
                         color: !filterCorso ? "white" : "#0d47a1",
                         border: "1px solid #0d47a1", fontSize: 11, fontWeight: 800,
                         cursor: "pointer", borderRadius: 14, whiteSpace: "nowrap" }}>
                Tutti ({items.filter(it => it.anno === anno).length})
              </button>
              {(() => {
                // Conta iscritti attivi / lista attesa SOLO per l'anno selezionato.
                // (items è già filtrato dal backend, ma facciamo doppia sicurezza.)
                const stats = {};
                for (const it of items) {
                  if (it.anno !== anno) continue;
                  const k = it.corso_codice || "";
                  if (!stats[k]) stats[k] = { tot: 0, att: 0, wait: 0, rec: 0 };
                  stats[k].tot += 1;
                  if ((it.data_recesso || "").trim()) { stats[k].rec += 1; continue; }
                  const isWait = waitlistByIdComputed[it._id] ?? !!it.in_lista_attesa;
                  if (isWait) stats[k].wait += 1; else stats[k].att += 1;
                }
                // Mostra i corsi: se anno è selezionato → solo quel'anno; altrimenti TUTTI i corsi
                const corsiAnno = (anno == null)
                  ? corsiLookup
                  : corsiLookup.filter(c => c.anno == null || c.anno === anno);
                return corsiAnno.map((c) => {
                  const s = stats[c.codice] || { tot: 0, att: 0, wait: 0, rec: 0 };
                  const max = Number(c.max_iscritti || 0);
                  const active = filterCorso === c.codice;
                  // Capacity colorscale (su iscritti attivi vs max)
                  let bg = "white", fg = "#0d47a1", border = "#90caf9";
                  if (max > 0) {
                    const pct = s.att / max;
                    if (pct >= 1)      { bg = "#ffebee"; fg = "#b71c1c"; border = "#ef9a9a"; }
                    else if (pct >= 0.85) { bg = "#fff3e0"; fg = "#e65100"; border = "#ffb74d"; }
                    else if (pct >= 0.5)  { bg = "#e8f5e9"; fg = "#1b5e20"; border = "#a5d6a7"; }
                    else if (pct > 0)     { bg = "#e3f2fd"; fg = "#0d47a1"; border = "#90caf9"; }
                  } else if (s.att > 0) { bg = "#e3f2fd"; fg = "#0d47a1"; border = "#90caf9"; }
                  if (active) { bg = fg; fg = "white"; }
                  return (
                    <button key={c.codice}
                      data-testid={`mm-corso-chip-${c.codice}`}
                      onClick={() => {
                        if (filterCorso === c.codice) setFilterCorso("");
                        else { setFilterCorso(c.codice); setFilterAttesa("all"); setFilterGiorno(""); setFilterGruppo(""); setTableSearch(""); }
                      }}
                      title={`${c.codice} — ${c.descrizione || ""}\nAttivi: ${s.att}${max ? "/" + max : ""}${s.wait ? " · Attesa: " + s.wait : ""}${s.rec ? " · Recessi: " + s.rec : ""}`}
                      style={{ padding: "4px 10px", background: bg, color: fg,
                               border: `1px solid ${active ? bg : border}`,
                               fontSize: 11, fontWeight: 700, cursor: "pointer",
                               borderRadius: 14, whiteSpace: "nowrap",
                               display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontWeight: 700 }}>{c.descrizione || c.codice}</span>
                      <span style={{ fontFamily: "Consolas, monospace", fontWeight: 800 }}>
                        {s.att}{max > 0 ? `/${max}` : ""}
                      </span>
                      {s.wait > 0 && (
                        <span style={{
                          background: active ? "rgba(255,255,255,0.25)" : "#ef6c00",
                          color: "white", padding: "0 5px", borderRadius: 8,
                          fontSize: 9, fontWeight: 800,
                        }} title={`${s.wait} in lista d'attesa`}>+{s.wait}</span>
                      )}
                    </button>
                  );
                });
              })()}
            </div>
          )}
          {/* Toolbar filtri stile ExtraAlunniView */}
          <div style={{ margin: "0 20px 8px", background: "#f5f5f5", padding: 10, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", borderRadius: "0 0 4px 4px", border: "1px solid #e0e0e0", borderTop: corsiLookup.length > 0 ? "1px solid #e0e0e0" : "1px solid #e0e0e0" }}>
            <select value={anno ?? ""} onChange={e => {
                const newAnno = e.target.value === "" ? null : Number(e.target.value);
                setAnno(newAnno);
                // 🆕 Sync locale → globale (aggiorna A.S. attivo condiviso)
                if (newAnno != null) setAnnoGlobal(newAnno);
              }}
              data-testid="mm-isc-anno" title="Anno scolastico (sincronizzato con A.S. attivo del header)"
              style={{ padding: "6px 10px", fontSize: 12, border: "1px solid #1565c0", color: "#0d47a1", fontWeight: 700, borderRadius: 3 }}>
              <option value="">Tutti gli anni</option>
              {anni.map(a => <option key={a} value={a}>{fmtAnno(a)}</option>)}
            </select>

            <select value={filterCorso} onChange={e => {
                setFilterCorso(e.target.value);
                setFilterAttesa("all"); setFilterGiorno(""); setFilterGruppo(""); setTableSearch("");
              }}
              data-testid="mm-isc-filter-corso"
              style={{ padding: "6px 10px", fontSize: 12, border: "1px solid #bbb", borderRadius: 3, maxWidth: 260 }}>
              <option value="">Tutti i corsi</option>
              {corsiLookup.map(c => (
                <option key={c.codice} value={c.codice}>{c.codice} — {c.descrizione}</option>
              ))}
            </select>

            <select value={filterGiorno} onChange={e => setFilterGiorno(e.target.value)}
              data-testid="mm-isc-filter-giorno" title="Giorno"
              style={{ padding: "6px 10px", fontSize: 12, border: "1px solid #bbb", borderRadius: 3 }}>
              <option value="">Tutti i giorni</option>
              {availableGiorni.map(wd => (
                <option key={wd} value={String(wd)}>{["Lunedì","Martedì","Mercoledì","Giovedì","Venerdì"][wd]}</option>
              ))}
            </select>

            <select value={filterGruppo} onChange={e => setFilterGruppo(e.target.value)}
              data-testid="mm-isc-filter-gruppo" title="Gruppo doposcuola primaria: 1 = classi 1-2, 2 = classi 3-4-5"
              style={{ padding: "6px 10px", fontSize: 12, border: "1px solid #bbb", borderRadius: 3 }}>
              <option value="">Tutti i gruppi</option>
              {availableGruppi.map(g => (
                <option key={g} value={String(g)}>{g === 1 ? "Gruppo 1 (cl.1-2)" : "Gruppo 2 (cl.3-4-5)"}</option>
              ))}
            </select>

            <select value={filterAttesa} onChange={e => setFilterAttesa(e.target.value)}
              data-testid="mm-isc-filter-attesa"
              style={{ padding: "6px 10px", fontSize: 12, border: "1px solid #bbb", borderRadius: 3 }}>
              <option value="all">Tutti gli stati</option>
              <option value="attivi">Solo attivi</option>
              <option value="attesa">Solo lista d'attesa</option>
            </select>

            <select value={sortMode} onChange={e => setSortMode(e.target.value)}
              data-testid="mm-isc-sort" title="Ordinamento"
              style={{ padding: "6px 10px", fontSize: 12, border: "1px solid #bbb", borderRadius: 3 }}>
              <option value="created">Ord. iscrizione (graduatoria) ↑</option>
              <option value="alpha">Ord. Cognome ↑</option>
            </select>

            {(filterCorso || filterGiorno || filterGruppo || filterAttesa !== "all" || tableSearch) && (
              <button onClick={() => { setFilterCorso(""); setFilterGiorno(""); setFilterGruppo(""); setFilterAttesa("all"); setTableSearch(""); }}
                data-testid="mm-isc-filter-clear"
                title="Reset di tutti i filtri"
                style={{ padding: "6px 10px", background: "white", color: "#c62828", border: "1px solid #c62828", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
                ✕ Pulisci
              </button>
            )}

            <div style={{ marginLeft: "auto", fontSize: 11, color: "#555" }} data-testid="mm-isc-counter">
              <b>{filtered.length}</b> iscrizion{filtered.length === 1 ? "e" : "i"}
            </div>

            <input placeholder="🔎 Cerca alunno, corso, classe..." value={tableSearch} onChange={e => setTableSearch(e.target.value)}
              data-testid="mm-isc-filter"
              style={{ width: 240, padding: "6px 10px", border: "1px solid #bbb", fontSize: 12, borderRadius: 3 }} />

            <button onClick={() => stampaTabella({ items: filtered, kind, color, filterCorso, filterGiorno, filterGruppo, filterAttesa, anno, corsiLookup })}
              data-testid="mm-isc-print"
              disabled={filtered.length === 0}
              title={filtered.length === 0 ? "Imposta almeno un filtro per stampare" : `Stampa ${filtered.length} iscrizioni visibili`}
              style={{ padding: "6px 12px", background: filtered.length === 0 ? "#bdbdbd" : "#37474f", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: filtered.length === 0 ? "not-allowed" : "pointer", borderRadius: 3 }}>
              🖨 Stampa
            </button>
            <button onClick={exportExcel}
              data-testid="mm-isc-export-excel"
              disabled={filtered.length === 0}
              title={filtered.length === 0 ? "Nessuna riga da esportare" : `Esporta in Excel le ${filtered.length} righe filtrate`}
              style={{ padding: "6px 12px", background: filtered.length === 0 ? "#bdbdbd" : "#1b7e3c", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: filtered.length === 0 ? "not-allowed" : "pointer", borderRadius: 3 }}>
              ⬇ Esporta Excel
            </button>
          </div>
        </>
      ) : (
        <>
          <div style={{ margin: "12px 20px 8px", background: "white", border: "1px solid #ccc", padding: "10px 12px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div style={{ fontWeight: 700, fontSize: 13, color }}>{title} · {filtered.length}</div>
            <div style={{ marginLeft: "auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
              <input placeholder="🔎 Filtra tabella..." value={tableSearch} onChange={e => setTableSearch(e.target.value)}
                data-testid="mm-isc-filter"
                style={{ width: 220, padding: 7, border: "1px solid #ccc", fontSize: 12 }} />
              <button onClick={() => setWizardOpen(true)} data-testid="mm-isc-new"
                style={{ padding: "7px 16px", background: color, color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 16, fontWeight: 900, lineHeight: 1 }}>+</span> Nuova iscrizione
              </button>
              <button onClick={() => setImportOpen(true)} data-testid="mm-isc-import-fam"
                style={{ padding: "7px 16px", background: "#2e7d32", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                📥 Importa Excel (spot)
              </button>
              <button onClick={svuotaTutteFamiglia} data-testid="mm-isc-fam-delete-all"
                title="Cancella TUTTE le richieste famiglia dell'anno"
                style={{ padding: "7px 16px", background: "#c62828", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                🗑 Svuota richieste
              </button>
              <button onClick={exportExcel} data-testid="mm-isc-export-excel-fam"
                disabled={filtered.length === 0}
                title={filtered.length === 0 ? "Nessuna riga da esportare" : `Esporta in Excel le ${filtered.length} righe filtrate`}
                style={{ padding: "7px 16px", background: filtered.length === 0 ? "#bdbdbd" : "#1b7e3c", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: filtered.length === 0 ? "not-allowed" : "pointer" }}>
                ⬇ Esporta Excel
              </button>
            </div>
          </div>

          {/* 🎯 FILTRI PRINCIPALI — bianco su nero, in evidenza (solo famiglia) */}
          <div style={{ margin: "0 20px 8px", background: "#212121", padding: "10px 14px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", borderRadius: 4, boxShadow: "0 2px 8px rgba(0,0,0,0.2)" }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: "white", letterSpacing: 0.8, textTransform: "uppercase", paddingRight: 6, borderRight: "1px solid #424242" }}>
              🎯 Filtri
            </div>
            <FilterField label="ANNO">
              <select value={anno ?? ""} onChange={e => setAnno(Number(e.target.value))}
                data-testid="mm-isc-anno"
                style={{ padding: "6px 10px", border: "none", fontSize: 13, fontWeight: 700, color: "#212121", background: "white", borderRadius: 3, minWidth: 120, cursor: "pointer" }}>
                {anni.map(a => <option key={a} value={a}>{fmtAnno(a)}</option>)}
              </select>
            </FilterField>
            <FilterField label="GIORNO">
              <select value={filterGiorno} onChange={e => setFilterGiorno(e.target.value)}
                data-testid="mm-isc-filter-giorno"
                style={{ padding: "6px 10px", border: "none", fontSize: 13, fontWeight: 700, color: "#212121", background: "white", borderRadius: 3, minWidth: 130, cursor: "pointer" }}>
                <option value="">Tutti i giorni</option>
                {availableGiorni.map(wd => (
                  <option key={wd} value={String(wd)}>{["Lunedì","Martedì","Mercoledì","Giovedì","Venerdì"][wd]}</option>
                ))}
              </select>
            </FilterField>
          </div>
        </>
      )}

      {err && <div style={{ margin: "0 20px 8px", padding: 10, background: "#fff0f0", color: "#c00", fontSize: 12 }}>{err} <button onClick={() => setErr(null)} style={{ marginLeft: 10, border: "none", background: "none", color: "#c00", cursor: "pointer", fontWeight: 700 }}>✕</button></div>}

      <div style={{ margin: "0 20px 20px", background: "white", border: "1px solid #ccc" }}>
        <table className="sw-table sw-table-compact" style={{ fontSize: 11 }}>
          <thead className={kind === "extra" ? "thead-blue" : undefined} style={{ background: kind === "extra" ? "#1a237e" : "#1f3864", color: "white" }}>
            <tr>
              {kind === "extra" && <th style={{ width: 34, textAlign: "center" }} title="Posizione nell'elenco filtrato (progressiva riga)">#</th>}
              {kind === "extra" && <th style={{ width: 90, textAlign: "center", fontSize: 10 }} title="Data e ora di invio della richiesta — determina l'ordine di graduatoria">DATA/ORA</th>}
              {kind === "extra" && <th style={{ width: 55, textAlign: "center" }} title="Anno scolastico dell'iscrizione">ANNO</th>}
              <th style={{ minWidth: 120, maxWidth: 160 }}>ALUNNO</th>
              {kind === "extra" && <th style={{ width: 40, textAlign: "center" }}>SET</th>}
              {kind === "extra" && <th style={{ width: 55 }}>CLASSE</th>}
              {kind === "extra" && <th style={{ width: 35, textAlign: "center" }} title="Gruppo doposcuola primaria: 1=cl.1-2 · 2=cl.3-4-5">GRP</th>}
              {kind === "extra" && <th style={{ width: 90, textAlign: "center" }}>CORSO</th>}
              {kind === "extra" && <th style={{ minWidth: 170 }}>DESCRIZIONE</th>}
              {kind === "extra" && <th style={{ width: 80, textAlign: "right" }} title="Prezzo a listino dell'iscrizione (campo `prezzo`). 🔄 = calcolato al volo, non persistito.">PREZZO €</th>}
              {kind === "extra" && <th style={{ width: 150, textAlign: "center" }}>STATO</th>}
              {kind === "fam" && <th style={{ width: 120 }}>DAL</th>}
              {DAYS.map(d => <th key={d} style={{ textAlign: "center", width: 34, fontSize: 10 }}>{d}</th>)}
              {kind === "fam" && <th style={{ width: 120, textAlign: "center" }} title="Data recesso: fino a revoca se vuota">DATA RECESSO</th>}
              {kind === "fam" && <th style={{ minWidth: 180 }}>NOTE</th>}
              <th style={{ minWidth: 320, textAlign: "center" }}>AZIONI</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={14} style={{ padding: 20, textAlign: "center", color: "#888" }}>Caricamento...</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={14} style={{ padding: 20, textAlign: "center", color: "#888" }}>
                {kind === "extra" && !filterCorso && filterAttesa === "all" && filterGiorno === "" && filterGruppo === "" && !tableSearch.trim()
                  ? <><b>Seleziona almeno un filtro</b> (corso, giorno, gruppo, stato o ricerca) per visualizzare le iscrizioni.</>
                  : "Nessuna iscrizione trovata con i filtri attivi."}
              </td></tr>
            )}
            {!loading && filtered.map((it, i) => {
              const isRecesso = !!(it.data_recesso || "").trim();
              const isWait = kind === "extra" ? (waitlistByIdComputed[it._id] ?? !!it.in_lista_attesa) : !!it.in_lista_attesa;
              // 🆕 Suggerimento "Verificato pagato": movimenti pagati e importo ≈ prezzo iscrizione
              // (l'utente deve andare a marcare come PAGATA). Non applica se già PAGATA/RECESSO.
              const mcRow = kind === "extra" ? movimentiCounts[it._id] : null;
              const statoRow = it.stato_iscrizione || (isWait ? "waiting" : "pending");
              const paidTotal = mcRow && typeof mcRow === "object" ? Number(mcRow.paid_total || 0) : 0;
              const prezzoNum = Number(it.prezzo || 0);
              const suggestVerified = kind === "extra"
                && !isRecesso
                && statoRow !== "pagata"
                && mcRow && typeof mcRow === "object"
                && mcRow.paid > 0
                && prezzoNum > 0
                && Math.abs(paidTotal - prezzoNum) < 0.01;
              const rowBg = isRecesso ? "#ffebee" : (suggestVerified ? "#c8e6c9" : (isWait ? "#fff8e1" : "transparent"));
              return (
              <tr key={it._id} data-testid={`mm-isc-row-${i}`}
                title={suggestVerified ? "💡 Movimenti pagati e importo = prezzo iscrizione. Da marcare come VERIFICATO PAGATO (✓💳)." : undefined}
                style={{ background: rowBg, textDecoration: isRecesso ? "line-through" : "none", color: isRecesso ? "#c62828" : undefined }}>
                {kind === "extra" && (
                  <td data-testid={`mm-isc-pos-${i}`} style={{ textAlign: "center", fontFamily: "Consolas, monospace", fontWeight: 800, color: isRecesso ? "#c62828" : "#546e7a", background: "#eceff1" }}>{isRecesso ? "—" : rowNumbers[it._id]}</td>
                )}
                {kind === "extra" && (() => {
                  const ca = it.created_at || "";
                  let dateLabel = "—";
                  let timeLabel = "";
                  try {
                    if (ca) {
                      const d = new Date(ca);
                      dateLabel = d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
                      timeLabel = d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
                    }
                  } catch { /* ignore */ }
                  return (
                    <td style={{ textAlign: "center", fontFamily: "Consolas, monospace", color: "#37474f", whiteSpace: "nowrap" }} title={ca}>
                      <div style={{ fontSize: 11, fontWeight: 700 }}>{dateLabel}</div>
                      {timeLabel && <div style={{ fontSize: 10, color: "#777" }}>🕒 {timeLabel}</div>}
                    </td>
                  );
                })()}
                {kind === "extra" && <td style={{ textAlign: "center", fontFamily: "Consolas, monospace", fontWeight: 700, color: "#0d47a1", background: "#e3f2fd" }} title={`Anno scolastico ${fmtAnno(it.anno)}`}>{fmtAnno(it.anno)}</td>}
                <td style={{ fontWeight: 600 }}
                  title={
                    (kind === "extra"
                      ? `📞 ${it.telefono || "—"}\n✉️ ${it.email || "—"}${it.email_canossa ? `\n🎓 ${it.email_canossa}` : ""}`
                      : "")
                  }>
                  {it.cognome} {it.nome} <span style={{ color: "#999", fontSize: 10, fontWeight: 400 }}>· {it.alunno_cod}</span>
                  {kind === "extra" && (it.telefono || it.email) && (
                    <span data-testid={`mm-isc-contact-${i}`}
                      style={{ marginLeft: 6, fontSize: 9, color: "#666", cursor: "help", letterSpacing: 0.3 }}
                      title={`📞 ${it.telefono || "—"}\n✉️ ${it.email || "—"}${it.email_canossa ? `\n🎓 ${it.email_canossa}` : ""}`}>
                      ℹ️
                    </span>
                  )}
                  {isWait && (
                    <span title="Iscrizione in lista d'attesa — click per promuovere"
                      onClick={async () => {
                        if (!window.confirm(`Promuovere ${it.cognome} ${it.nome} da lista d'attesa a iscritto attivo?`)) return;
                        try { await api.put(`/mensa-matrice/extra-iscrizioni/${it._id}`, { in_lista_attesa: false }); await carica(); }
                        catch (e) { setErr(e.response?.data?.detail || "Errore"); }
                      }}
                      data-testid={`mm-isc-attesa-${i}`}
                      style={{ marginLeft: 6, padding: "1px 6px", background: "#ef6c00", color: "white", fontSize: 9, fontWeight: 700, cursor: "pointer" }}>⏳ LISTA ATTESA</span>
                  )}
                </td>
                {kind === "extra" && <td style={{ textAlign: "center", fontWeight: 700, fontFamily: "Consolas, monospace", color: it.settore ? "#0d47a1" : "#ccc" }}>{it.settore || "—"}</td>}
                {kind === "extra" && <td style={{ fontWeight: 600, fontFamily: "Consolas, monospace" }}>{it.classe || <span style={{ color: "#ccc" }}>—</span>}</td>}
                {kind === "extra" && (() => {
                  const g = gruppoVisualOf(it);
                  return <td style={{ textAlign: "center", fontWeight: 800, fontFamily: "Consolas, monospace", background: g === 1 ? "#fff3e0" : g === 2 ? "#e8eaf6" : "transparent", color: g === 1 ? "#e65100" : g === 2 ? "#1a237e" : "#ccc" }} title={g === 1 ? "Gruppo 1 (classi 1-2)" : g === 2 ? "Gruppo 2 (classi 3-4-5)" : "Gruppo non applicabile alla classe"}>{g || "—"}</td>;
                })()}
                {kind === "extra" && <td style={{ fontFamily: "Consolas, monospace", fontWeight: 700, color: "#0d47a1", textAlign: "center" }}>{it.corso_codice || ""}</td>}
                {kind === "extra" && <td style={{ color, fontWeight: 600 }}>{it.corso_descrizione || it.attivita || ""}</td>}
                {/* 🆕 Layout richiesto utente 2026-02-25:
                    - Colonna PREZZO €: valore listino + DROPDOWN STATO sotto
                    - Colonna STATO: bottone CONTABILE (verde se pagata) + BOTTONE MOVIMENTI sotto
                */}
                {kind === "extra" && (() => {
                  const stato = it.stato_iscrizione || (isRecesso ? "recesso" : (isWait ? "waiting" : "pending"));
                  const map = {
                    pending:                { bg: "#9e9e9e", color: "white", txt: "● INSERITA" },
                    waiting:                { bg: "#ef6c00", color: "white", txt: "⏳ LISTA D'ATTESA" },
                    in_attesa_pagamento:    { bg: "#ba68c8", color: "white", txt: "💳 INVIATO LINK PAG." },
                    accettata_mail_inviata: { bg: "#ba68c8", color: "white", txt: "💳 INVIATO LINK PAG." },
                    confirmed:              { bg: "#ba68c8", color: "white", txt: "💳 INVIATO LINK PAG." },
                    confermata_genitore:    { bg: "#ba68c8", color: "white", txt: "💳 INVIATO LINK PAG." },
                    pagata_da_verificare:   { bg: "#03a9f4", color: "white", txt: "🔍 DA VERIFICARE" },
                    pagata:                 { bg: "#2e7d32", color: "white", txt: "✓ PAGATA" },
                    scaduta:                { bg: "#212121", color: "white", txt: "⏰ SCADUTA" },
                    recesso:                { bg: "#d32f2f", color: "white", txt: "⊘ RECESSO" },
                    rejected:               { bg: "#ba68c8", color: "white", txt: "💳 INVIATO LINK PAG." },
                    chiusa:                 { bg: "#ffffff", color: "#37474f", txt: "🔒 CHIUSA", border: "1px solid #37474f" },
                  };
                  const cfg = map[stato] || map.pending;
                  const lastEmail = it.last_email_at ? new Date(it.last_email_at).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit" }) : null;
                  const cambiaStato = async (nuovo) => {
                    if (nuovo === stato) return;
                    if (!window.confirm(`Cambiare lo stato di ${it.cognome} ${it.nome} da "${cfg.txt.replace(/^[^A-Z]+ /, "")}" a "${(map[nuovo] || {}).txt || nuovo}"?`)) return;
                    try {
                      const payload = { stato_iscrizione: nuovo };
                      if (nuovo === "recesso") {
                        const today = new Date().toISOString().slice(0, 10);
                        await api.put(`/mensa-matrice/extra-iscrizioni/${it._id}`, { data_recesso: today });
                      } else {
                        if (stato === "recesso") {
                          await api.put(`/mensa-matrice/extra-iscrizioni/${it._id}`, { data_recesso: "" });
                        }
                        await api.put(`/mensa-matrice/extra-iscrizioni/${it._id}`, payload);
                      }
                      await carica();
                    } catch (e) { setErr(e.response?.data?.detail || "Errore cambio stato"); }
                  };
                  // Prezzo
                  const pz = it.prezzo;
                  let prezzoLabel = "—";
                  let prezzoColor = "#bbb";
                  if (pz != null && pz !== "" && Number(pz) !== 0) {
                    const n = Number(pz);
                    prezzoLabel = Number.isFinite(n) ? n.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : String(pz);
                    prezzoColor = "#0d47a1";
                  }
                  // Movimenti collegati
                  const mc = movimentiCounts[it._id];
                  const total = mc && typeof mc === "object" ? mc.total : (typeof mc === "number" ? mc : 0);
                  const paid = mc && typeof mc === "object" ? mc.paid : 0;
                  let movBg = "#9e9e9e";
                  let movTip = "Nessun movimento contabile collegato (verifica causale corso)";
                  if (total > 0 && paid === 0) { movBg = "#c62828"; movTip = `⚠ ${total} movimento/i collegato/i · NESSUNO ancora pagato`; }
                  else if (total > 0 && paid > 0) { movBg = "#2e7d32"; movTip = `${total} movimento/i collegato/i · ${paid} PAGATO/I`; }
                  const isPagata = stato === "pagata";
                  const contabileBg = isPagata ? "#2e7d32" : "#03a9f4";
                  return (
                    <>
                      {/* PREZZO € — valore + dropdown STATO sotto */}
                      <td style={{ textAlign: "right", fontFamily: "Consolas, monospace", fontWeight: 700, color: prezzoColor, width: 140, minWidth: 140 }}>
                        {/* 🆕 Feb 2026: cella "prezzo" resa con la stessa dimensione dei bottoni sotto/adiacenti (2×2 uniformi) */}
                        <div style={{ display: "block", padding: "4px 6px", width: "100%", border: "1px solid transparent", background: "transparent", color: prezzoColor, fontSize: 12, fontWeight: 800, borderRadius: 3, textAlign: "right", boxSizing: "border-box", marginBottom: 3, lineHeight: 1.2, minHeight: 26 }}>
                          {prezzoLabel}
                          {it.prezzo_auto && prezzoLabel !== "—" && (
                            <span title="Prezzo calcolato al volo dal listino corso (non persistito). Usa 'Ricalcola prezzi' per salvarlo."
                              style={{ marginLeft: 4, fontSize: 9, color: "#1565c0", cursor: "help" }}>🔄</span>
                          )}
                        </div>
                        <select value={stato} onChange={(e) => cambiaStato(e.target.value)}
                          data-testid={`mm-isc-stato-select-${i}`}
                          title={`Stato: ${cfg.txt} — clicca per modificare${lastEmail ? " · Ultima email " + lastEmail : ""}`}
                          style={{ display: "block", fontSize: 11, padding: "4px 6px", width: "100%", border: cfg.border || `1px solid ${cfg.bg}`, background: cfg.bg, color: cfg.color, cursor: "pointer", fontWeight: 800, borderRadius: 3, textAlignLast: "center", boxSizing: "border-box", minHeight: 26, lineHeight: 1.2 }}>
                          <option value="pending" style={{ background: "white", color: "#333" }}>● INSERITA</option>
                          <option value="waiting" style={{ background: "white", color: "#333" }}>⏳ LISTA D&apos;ATTESA</option>
                          <option value="in_attesa_pagamento" style={{ background: "white", color: "#333" }}>💳 INVIATO LINK PAG.</option>
                          <option value="pagata_da_verificare" style={{ background: "white", color: "#333" }}>🔍 DA VERIFICARE</option>
                          <option value="pagata" style={{ background: "white", color: "#333" }}>✓ PAGATA</option>
                          <option value="scaduta" style={{ background: "white", color: "#333" }}>⏰ SCADUTA</option>
                          <option value="recesso" style={{ background: "white", color: "#333" }}>⊘ RECESSO</option>
                          <option value="chiusa" style={{ background: "white", color: "#333" }}>🔒 CHIUSA</option>
                        </select>
                      </td>
                      {/* STATO — bottone CONTABILE (verde se pagata) + bottone MOV sotto */}
                      <td style={{ textAlign: "center", width: 140, minWidth: 140 }} title={lastEmail ? `Ultima email: ${it.last_email_template || ""} il ${lastEmail}` : ""}>
                        {it.has_contabile ? (
                          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/mensa-matrice/extra-iscrizioni/${it._id}/contabile?inline=1`}
                            target="_blank" rel="noopener noreferrer"
                            data-testid={`mm-isc-contabile-${i}`}
                            title={`Visualizza contabile: ${it.contabile_filename || ""}${it.contabile_uploaded_at ? "\nCaricata il " + new Date(it.contabile_uploaded_at).toLocaleString("it-IT") : ""}${isPagata ? "\n✓ Iscrizione PAGATA" : ""}`}
                            style={{ display: "block", marginBottom: 3, fontSize: 11, padding: "4px 6px", width: "100%", border: `1px solid ${contabileBg}`, background: contabileBg, color: "white", cursor: "pointer", fontWeight: 800, borderRadius: 3, textDecoration: "none", textAlign: "center", boxSizing: "border-box", minHeight: 26, lineHeight: 1.2 }}>
                            Contabile{it.contabile_uploaded_at ? ` ${new Date(it.contabile_uploaded_at).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit" })}` : ""}
                          </a>
                        ) : (
                          <div style={{ display: "block", marginBottom: 3, fontSize: 10, padding: "4px 6px", width: "100%", border: "1px dashed #bdbdbd", background: "#fafafa", color: "#999", fontWeight: 600, borderRadius: 3, textAlign: "center", boxSizing: "border-box", minHeight: 26, lineHeight: 1.2 }}>
                            no contab.
                          </div>
                        )}
                        {mc !== undefined ? (
                          <button data-testid={`mm-isc-mov-${i}`}
                            onClick={() => apriMovimenti(it)}
                            title={movTip}
                            style={{ display: "block", padding: "4px 6px", background: movBg, color: "white", border: `1px solid ${movBg}`, fontSize: 11, fontWeight: 800, cursor: "pointer", borderRadius: 3, width: "100%", letterSpacing: 0.3, lineHeight: 1.2, boxSizing: "border-box", minHeight: 26 }}>
                            {total} mov{total > 0 ? ` · ${paid} ✓` : ""}
                          </button>
                        ) : (
                          /* placeholder invisibile per mantenere altezza uniforme */
                          <div style={{ display: "block", padding: "4px 6px", width: "100%", minHeight: 26, boxSizing: "border-box" }} />
                        )}
                      </td>
                    </>
                  );
                })()}
                {kind === "fam" && <td style={{ fontSize: 11, fontFamily: "Consolas, monospace", color: "#555" }}>{it.dal || <span style={{ color: "#c62828" }}>—</span>}</td>}
                {[0, 1, 2, 3, 4].map(wd => {
                  const isInDay = !!it.giorni?.includes(wd);
                  // Per "extra": mostra la POSIZIONE dell'alunno tra gli iscritti di quel giorno
                  // (ordinati per data/ora iscrizione, recessi esclusi).
                  // Le righe oltre il max_per_slot (o max_iscritti) hanno sfondo ROSSO = fuori graduatoria.
                  // Per "fam": mantieni la spunta classica.
                  const rank = (kind === "extra" && !isRecesso) ? giornoRanks[wd]?.[it._id] : null;
                  const slotMax = (kind === "extra" && isInDay) ? slotMaxFor(it, wd) : 0;
                  const isOverMax = !!(rank && slotMax > 0 && rank > slotMax);
                  let bg = "transparent";
                  if (isInDay) bg = isOverMax ? "#ef5350" : (kind === "extra" ? "#66bb6a" : "#81c784");
                  return (
                    <td key={wd} style={{ textAlign: "center", background: bg }}
                        title={
                          !isInDay ? `Non iscritto a ${DAYS[wd]}` :
                          isOverMax ? `⚠ Posizione #${rank} su max ${slotMax} per ${DAYS[wd]} — FUORI GRADUATORIA (lista d'attesa)` :
                          rank ? `Posizione #${rank}${slotMax > 0 ? ` su ${slotMax}` : ""} per ${DAYS[wd]}` :
                          `Iscritto a ${DAYS[wd]}`
                        }>
                      {isInDay
                        ? (rank
                            ? <span style={{ color: "white", fontWeight: 800, fontSize: 17 }}>{rank}</span>
                            : <span style={{ color: "white", fontWeight: 800, fontSize: 17 }}>✓</span>)
                        : <span style={{ color: "#ccc" }}>x</span>}
                    </td>
                  );
                })}
                {(kind === "fam") && (
                  <td style={{ textAlign: "center" }}>
                    <input type="date" defaultValue={it.data_recesso || ""}
                      onBlur={async (e) => {
                        const nv = e.target.value || "";
                        if (nv === (it.data_recesso || "")) return;
                        try {
                          await api.put(`/mensa-matrice/${prefix}/${it._id}`, { data_recesso: nv });
                          await carica();
                        } catch (ex) { setErr(ex.response?.data?.detail || "Errore salvataggio data recesso"); }
                      }}
                      title={kind === "fam" ? "Data recesso: lascia vuota per 'fino a revoca'" : "Data recesso: oltre questa data l'iscrizione viene esclusa dalla generazione mensa"}
                      data-testid={`mm-isc-recesso-${i}`}
                      style={{ width: "100%", padding: "3px 4px", border: it.data_recesso ? "1px solid #c62828" : "1px solid #ccc", background: it.data_recesso ? "#ffebee" : "white", fontSize: 10, fontFamily: "Consolas, monospace", color: it.data_recesso ? "#c62828" : "#333", fontWeight: it.data_recesso ? 700 : 400 }} />
                  </td>
                )}
                {kind === "fam" && <td style={{ fontSize: 10, color: "#666" }}>{it.note}</td>}
                <td style={{ textAlign: "center" }}>
                  {kind === "extra" && (() => {
                    const stato = it.stato_iscrizione || (it.in_lista_attesa ? "waiting" : "pending");
                    const setStato = async (newStato, label) => {
                      if (!window.confirm(`${label} l'iscrizione di ${it.cognome} ${it.nome} a "${it.corso_descrizione || it.corso_codice}"?`)) return;
                      try { await api.put(`/mensa-matrice/extra-iscrizioni/${it._id}`, { stato_iscrizione: newStato }); await carica(); }
                      catch (e) { setErr(e.response?.data?.detail || "Errore"); }
                    };
                    const accettaEInvia = () => setMailConfermaIscr(it);
                    const sollecitoPagamento = () => {
                      // Apre il modal di invio email con preselezione di un template "sollecito"
                      apriInvioEmail({ ...it, _preselect_template: "sollecito_pagamento" });
                    };
                    const pagataSenzaMail = async () => {
                      if (!window.confirm(`Marca PAGATA (senza inviare alcuna mail) l'iscrizione di ${it.cognome} ${it.nome} a "${it.corso_descrizione || it.corso_codice}"?`)) return;
                      try { await api.post(`/mensa-matrice/extra-iscrizioni/${it._id}/conferma-pagamento?force=1`); await carica(); }
                      catch (e) { setErr(e.response?.data?.detail || "Errore"); }
                    };
                    const uploadContabile = async () => {
                      const input = document.createElement("input");
                      input.type = "file";
                      input.accept = "application/pdf,image/jpeg,image/jpg,image/png,image/webp";
                      input.style.display = "none";
                      document.body.appendChild(input);
                      const f = await new Promise(r => { input.onchange = () => r(input.files && input.files[0] ? input.files[0] : null); input.click(); setTimeout(() => r(null), 60000); });
                      document.body.removeChild(input);
                      if (!f) return;
                      const warn = it.has_contabile ? `⚠ Una contabile è GIÀ caricata. Verrà SOSTITUITA.\n\n` : "";
                      if (!window.confirm(`📤 Carica contabile per ${it.cognome} ${it.nome}\n\n${warn}File: ${f.name}\n\nLo stato passerà a CONFERMATA PAGATA.\n\nConfermi?`)) return;
                      try {
                        const fd = new FormData();
                        fd.append("file", f);
                        await api.post(`/mensa-matrice/extra-iscrizioni/${it._id}/contabile`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                        await carica();
                      } catch (e) { setErr(e.response?.data?.detail || "Errore upload contabile"); }
                    };
                    const visualizzaContabile = () => {
                      const API = process.env.REACT_APP_BACKEND_URL;
                      window.open(`${API}/api/mensa-matrice/extra-iscrizioni/${it._id}/contabile?inline=1`, "_blank");
                    };
                    const isFinal = (stato === "pagata" || stato === "recesso");
                    const canScaduta = stato === "in_attesa_pagamento" || stato === "accettata_mail_inviata";
                    // 🆕 Richiesta utente 2026-02-25: quando lo stato è "pagata", TUTTI i bottoni
                    // attivi diventano verdi (unificazione visiva). Le dinamiche restano invariate.
                    const isPaid = stato === "pagata";
                    const clr = (defaultColor) => isPaid ? "#2e7d32" : defaultColor;
                    // Helper stile bottone: v=true → colorato attivo, v=false → placeholder BIANCO con SIGLA visibile in grigio chiaro
                    // 🆕 Dimensioni FISSE: attivo e placeholder hanno lo stesso ingombro (border 2px sempre, larghezza/altezza fissa)
                    // 🆕 Su placeholder la sigla resta VISIBILE ma in grigio chiaro (richiesta utente 2026-02-25)
                    const btnStyle = (bg, v = true) => ({
                      background: v ? bg : "#ffffff",
                      color: v ? "white" : "#b0bec5",
                      border: v ? "2px solid " + bg : "2px solid #cfd8dc",
                      padding: 0,
                      fontSize: 11, fontWeight: 700,
                      cursor: v ? "pointer" : "default",
                      borderRadius: 3,
                      pointerEvents: v ? "auto" : "none",
                      width: 30,
                      height: 24,
                      boxSizing: "border-box",
                      lineHeight: 1,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flex: "0 0 30px",
                    });
                    return (
                      <span style={{ display: "inline-flex", gap: 4, flexWrap: "nowrap", justifyContent: "center" }}>
                        {/* 1) Invia mail conferma (verde) — visibile solo per pending/waiting */}
                        <button onClick={accettaEInvia} data-testid={`mm-isc-accetta-mail-${i}`}
                          title="Invia mail di conferma con link pagamento al genitore"
                          style={btnStyle(clr("#2e7d32"), stato === "pending" || stato === "waiting")}>M</button>
                        {/* 1b) Reinvia mail (blu) — visibile per stati intermedi */}
                        <button onClick={accettaEInvia} data-testid={`mm-isc-rinvia-mail-${i}`}
                          title="Re-invia mail conferma con link pagamento"
                          style={btnStyle(clr("#1565c0"), stato === "accettata_mail_inviata" || stato === "in_attesa_pagamento" || stato === "chiusa")}>M+</button>
                        {/* 2) Sposta in lista d'attesa */}
                        <button onClick={() => setStato("waiting", "Mettere in lista d'attesa")} data-testid={`mm-isc-attesa-btn-${i}`}
                          title="Sposta in lista d'attesa"
                          style={btnStyle(clr("#ef6c00"), stato !== "waiting" && !isFinal && stato !== "pagata_da_verificare")}>LA</button>
                        {/* 3) Sollecito pagamento */}
                        <button onClick={sollecitoPagamento} data-testid={`mm-isc-sollecito-${i}`}
                          title={it.mail_sollecito_inviato_at ? `Sollecito già inviato il ${new Date(it.mail_sollecito_inviato_at).toLocaleString("it-IT")} — reinvia` : "Invia sollecito di pagamento (editor con modello 'sollecito_pagamento')"}
                          style={{
                            // 🆕 Feb 2026: SO sempre attivo se lo stato NON è "pagata" (né chiuso definitivamente)
                            ...btnStyle(clr("#ad1457"), stato !== "pagata" && stato !== "recesso"),
                            border: it.mail_sollecito_inviato_at ? "2px solid #ad1457" : undefined,
                            background: it.mail_sollecito_inviato_at ? "white" : undefined,
                            color: it.mail_sollecito_inviato_at ? "#ad1457" : undefined,
                          }}>SO</button>
                        {/* 4) Marca pagata SENZA mail (con contabile) */}
                        <button onClick={pagataSenzaMail} data-testid={`mm-isc-paga-senza-mail-${i}`}
                          title="Marca PAGATA e CONFERMATA (senza mail) — richiede contabile"
                          style={btnStyle(clr("#1b5e20"), it.has_contabile && stato !== "pagata")}>P</button>
                        {/* 5+6) Carica / Ricarica contabile */}
                        <button onClick={uploadContabile} data-testid={`mm-isc-upload-contabile-${i}`}
                          title={it.has_contabile ? "RICARICA contabile (sostituisce)" : "Carica contabile bonifico"}
                          style={btnStyle(clr(it.has_contabile ? "#00838f" : "#0277bd"), !isFinal && stato !== "scaduta")}>UP</button>
                        {/* 7) Visualizza contabile */}
                        <button onClick={visualizzaContabile} data-testid={`mm-isc-view-contabile-${i}`}
                          title={`Visualizza contabile bonifico${it.contabile_filename ? ` (${it.contabile_filename})` : ""}`}
                          style={btnStyle(clr("#0288d1"), !!it.has_contabile)}>VD</button>
                        {/* 7b) Verificato pagato (force) */}
                        <button onClick={pagataSenzaMail} data-testid={`mm-isc-verif-pag-${i}`}
                          title="Marca come VERIFICATO PAGATO (stato → PAGATA) — senza mail"
                          style={btnStyle(clr("#388e3c"), stato !== "pagata" && stato !== "recesso" && stato !== "scaduta")}>VP</button>
                        {/* 8) Marca scaduta */}
                        <button onClick={() => setStato("scaduta", "Marcare come SCADUTA")} data-testid={`mm-isc-scaduta-${i}`}
                          title="Marca come SCADUTA (>24h senza conferma)"
                          style={btnStyle(clr("#5d4037"), canScaduta)}>SC</button>
                        {/* 9) Storico iscrizioni (sempre) */}
                        <button onClick={() => setLogAlunno(it.alunno_cod)} data-testid={`mm-isc-log-${i}`}
                          title={`Storico iscrizioni extra di ${it.cognome} ${it.nome}`}
                          style={btnStyle(clr("#37474f"), true)}>ST</button>
                        {/* 9b) 🆕 Data recesso (popup) — attivo se non è già recesso; se recesso, mostra data + permette rimozione */}
                        <button onClick={() => setRecessoModal({ it, value: it.data_recesso || "" })} data-testid={`mm-isc-recesso-btn-${i}`}
                          title={isRecesso ? `Recesso dal ${it.data_recesso} — click per modificare/rimuovere` : "Imposta data recesso"}
                          style={btnStyle(clr(isRecesso ? "#c62828" : "#78909c"), true)}>R</button>
                        {/* 10) Modifica (sempre) */}
                        <button onClick={() => setEditing({ ...it, giorni: [...(it.giorni || [])] })} data-testid={`mm-isc-edit-${i}`}
                          title="Modifica iscrizione"
                          style={btnStyle(clr("#1565c0"), true)}>E</button>
                        {/* 11) Elimina (sempre) */}
                        <button onClick={() => elimina(it)} data-testid={`mm-isc-del-${i}`}
                          title="Elimina iscrizione (irreversibile)"
                          style={btnStyle(clr("#c62828"), true)}>X</button>
                      </span>
                    );
                  })()}
                  {kind === "fam" && (
                    <>
                      <button onClick={() => setEditing({ ...it, giorni: [...(it.giorni || [])] })} data-testid={`mm-isc-edit-${i}`}
                        style={{ background: "#1565c0", color: "white", border: "none", padding: "3px 8px", fontSize: 10, fontWeight: 700, cursor: "pointer", marginRight: 4 }}>✏</button>
                      <button onClick={() => elimina(it)} data-testid={`mm-isc-del-${i}`}
                        style={{ background: "#c62828", color: "white", border: "none", padding: "3px 8px", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>🗑</button>
                    </>
                  )}
                  {/* 🆕 Feb 2026: badge "SO inviato il ..." */}
                  {it.mail_sollecito_inviato_at && (
                    <div data-testid={`mm-isc-sollecito-info-${i}`}
                      title={`Sollecito inviato da ${it.mail_sollecito_inviato_by || "?"} a ${it.mail_sollecito_inviato_to || ""}`}
                      style={{
                        marginTop: 4, fontSize: 9, color: "#ad1457",
                        fontWeight: 700, letterSpacing: 0.2,
                        background: "#fce4ec", border: "1px solid #f8bbd0",
                        padding: "1px 4px", borderRadius: 3,
                        display: "inline-block",
                      }}>
                      📣 SO: {new Date(it.mail_sollecito_inviato_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })}
                    </div>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <IscrizioneEditModal editing={editing} setEditing={(v) => { if (v == null) setEditErr(null); setEditing(v); }} kind={kind} onSave={salva} err={editErr} />
      )}

      {wizardOpen && !editing && (
        <NuovaIscrizioneWizard
          kind={kind}
          initialAnno={anno}
          anni={anni}
          onClose={() => setWizardOpen(false)}
          onPicked={(payload) => { setWizardOpen(false); setEditing(payload); }}
          onError={(m) => setErr(m)}
        />
      )}

      {logAlunno != null && (
        <AlunnoLogModal alunnoCod={logAlunno} onClose={() => setLogAlunno(null)} />
      )}

      {mailConfermaIscr && (
        <MailConfermaModal iscrizione={mailConfermaIscr}
          onClose={() => setMailConfermaIscr(null)}
          onSent={() => { setMailConfermaIscr(null); carica(); }} />
      )}

      {movimentiModal && (
        <MovimentiCollegatiModal data={movimentiModal} onClose={() => setMovimentiModal(null)} />
      )}

      {/* 🆕 Popup dedicato per impostare/rimuovere data recesso */}
      {recessoModal && (
        <div onClick={() => setRecessoModal(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 750, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()} data-testid="mm-recesso-modal"
            style={{ background: "white", width: "100%", maxWidth: 420, borderRadius: 6, overflow: "hidden" }}>
            <div style={{ background: "#c62828", color: "white", padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <strong>⊘ Data recesso · {recessoModal.it.cognome} {recessoModal.it.nome}</strong>
              <button onClick={() => setRecessoModal(null)} data-testid="mm-recesso-close" style={{ background: "none", border: "none", color: "white", cursor: "pointer", fontSize: 18 }}>✕</button>
            </div>
            <div style={{ padding: 18 }}>
              <div style={{ fontSize: 11, color: "#666", marginBottom: 10 }}>
                Corso: <b>{recessoModal.it.corso_descrizione || recessoModal.it.corso_codice || "—"}</b><br/>
                Oltre questa data l'iscrizione viene esclusa dalla generazione mensa e dalla graduatoria.
              </div>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 4 }}>DATA RECESSO</label>
              <input type="date" value={recessoModal.value || ""}
                onChange={e => setRecessoModal({ ...recessoModal, value: e.target.value })}
                data-testid="mm-recesso-input"
                style={{ width: "100%", padding: 8, border: recessoModal.value ? "2px solid #c62828" : "1px solid #ccc", background: recessoModal.value ? "#ffebee" : "white", fontSize: 13, fontFamily: "Consolas, monospace", color: recessoModal.value ? "#c62828" : "#333", fontWeight: 700, borderRadius: 3 }} />
              <div style={{ marginTop: 6 }}>
                <button onClick={() => setRecessoModal({ ...recessoModal, value: new Date().toISOString().slice(0, 10) })}
                  data-testid="mm-recesso-today"
                  style={{ padding: "4px 10px", background: "#eeeeee", border: "1px solid #bdbdbd", fontSize: 11, cursor: "pointer", marginRight: 6 }}>Oggi</button>
                {recessoModal.value && (
                  <button onClick={() => setRecessoModal({ ...recessoModal, value: "" })}
                    data-testid="mm-recesso-clear"
                    style={{ padding: "4px 10px", background: "#fff3e0", border: "1px solid #ffb74d", fontSize: 11, cursor: "pointer", color: "#bf360c" }}>Pulisci (rimuovi recesso)</button>
                )}
              </div>
            </div>
            <div style={{ padding: "12px 18px", background: "#f5f5f5", display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button onClick={() => setRecessoModal(null)} data-testid="mm-recesso-cancel"
                style={{ padding: "8px 16px", background: "white", border: "1px solid #ccc", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Annulla</button>
              <button onClick={async () => {
                const it = recessoModal.it;
                const nv = recessoModal.value || "";
                try {
                  await api.put(`/mensa-matrice/${prefix}/${it._id}`, { data_recesso: nv });
                  setRecessoModal(null);
                  await carica();
                } catch (ex) { setErr(ex.response?.data?.detail || "Errore salvataggio data recesso"); }
              }}
                data-testid="mm-recesso-save"
                style={{ padding: "8px 16px", background: "#c62828", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Salva</button>
            </div>
          </div>
        </div>
      )}

      {importOpen && kind === "extra" && (
        <ImportIscrizioniModal
          anno={anno}
          onClose={() => setImportOpen(false)}
          onDone={async () => { setImportOpen(false); await carica(); }}
        />
      )}

      {importOpen && kind === "fam" && (
        <ImportFamigliaModal
          anno={anno}
          onClose={() => setImportOpen(false)}
          onDone={async () => { setImportOpen(false); await carica(); }}
        />
      )}

      {/* Modal invio email */}
      {sendModal && (
        <div onClick={() => setSendModal(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()} data-testid="mm-send-email-modal"
            style={{ background: "white", width: "100%", maxWidth: 720, borderRadius: 6, overflow: "hidden", maxHeight: "92vh", display: "flex", flexDirection: "column" }}>
            <div style={{ background: "#6a1b9a", color: "white", padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <strong>📧 Invia email · {sendModal.iscrizione.cognome} {sendModal.iscrizione.nome}</strong>
              <button onClick={() => setSendModal(null)} data-testid="mm-send-email-close" style={{ background: "none", border: "none", color: "white", cursor: "pointer", fontSize: 18 }}>✕</button>
            </div>
            <div style={{ padding: 18, overflowY: "auto" }}>
              <div style={{ marginBottom: 12, padding: 10, background: emailCfgState.resend_configured ? "#e8f5e9" : "#fff3e0", border: `1px solid ${emailCfgState.resend_configured ? "#81c784" : "#ffb74d"}`, borderRadius: 3, fontSize: 11, color: emailCfgState.resend_configured ? "#1b5e20" : "#bf360c" }}>
                {emailCfgState.resend_configured
                  ? <>✓ <b>Resend attivo</b> · L'email verrà inviata realmente.</>
                  : <>⚠ <b>Resend non configurato</b> · L'email verrà solo registrata in outbox (mock).</>}
              </div>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>MODELLO</label>
              <select value={sendModal.selectedTpl}
                onChange={async (e) => {
                  const tid = e.target.value;
                  setSendModal({ ...sendModal, selectedTpl: tid });
                  await caricaAnteprima(tid, sendModal.iscrizione._id);
                }}
                data-testid="mm-send-tpl-select"
                style={{ width: "100%", padding: 8, border: "2px solid #6a1b9a", borderRadius: 3, fontSize: 12, fontWeight: 600, marginBottom: 12 }}>
                {sendTemplates.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>

              <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>DESTINATARIO *</label>
              <input value={sendModal.toOverride} onChange={e => setSendModal({ ...sendModal, toOverride: e.target.value })}
                data-testid="mm-send-to" placeholder="email@destinatario.it"
                style={{ width: "100%", padding: 8, border: "1px solid #ccc", borderRadius: 3, fontSize: 12, fontFamily: "Consolas, monospace", marginBottom: 12 }} />

              <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>OGGETTO *</label>
              <input value={sendModal.subject} onChange={e => setSendModal({ ...sendModal, subject: e.target.value })}
                data-testid="mm-send-subject"
                style={{ width: "100%", padding: 8, border: "1px solid #ccc", borderRadius: 3, fontSize: 12, fontWeight: 600, marginBottom: 12 }} />

              <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>CORPO (anteprima con sostituzioni applicate)</label>
              <textarea value={sendModal.body} onChange={e => setSendModal({ ...sendModal, body: e.target.value })} rows={14}
                data-testid="mm-send-body"
                style={{ width: "100%", padding: 10, border: "1px solid #ccc", borderRadius: 3, fontSize: 12, fontFamily: "Consolas, monospace", resize: "vertical", lineHeight: 1.5 }} />
              {sendModal.anteprima?.missing_placeholders?.length > 0 && (
                <div style={{ marginTop: 8, padding: 8, background: "#fff0f0", color: "#c00", fontSize: 11, border: "1px solid #fcc", borderRadius: 3 }}>
                  ⚠ Placeholder non risolti: {sendModal.anteprima.missing_placeholders.join(", ")}
                </div>
              )}
            </div>
            <div style={{ padding: "12px 20px", borderTop: "1px solid #eee", display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => setSendModal(null)} data-testid="mm-send-cancel"
                style={{ background: "white", color: "#666", border: "1px solid #ccc", padding: "8px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
                Annulla
              </button>
              <button onClick={inviaEmail} disabled={sendModal.sending || !sendModal.toOverride || !sendModal.subject} data-testid="mm-send-confirm"
                style={{ background: (sendModal.sending || !sendModal.toOverride || !sendModal.subject) ? "#bbb" : "#6a1b9a", color: "white", border: "none", padding: "8px 20px", fontSize: 12, fontWeight: 700, cursor: sendModal.sending ? "wait" : "pointer", borderRadius: 3 }}>
                {sendModal.sending ? "Invio..." : "📧 Invia email"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ImportIscrizioniModal({ anno, onClose, onDone }) {
  const [corsi, setCorsi] = useState([]);
  const [corsoCod, setCorsoCod] = useState("");
  const [giorni, setGiorni] = useState([]); // array di 0..4
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [report, setReport] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/mensa-matrice/corsi", { params: { anno } });
        setCorsi(r.data.items || []);
      } catch (e) { setErr(e.response?.data?.detail || "Errore caricamento corsi"); }
    })();
  }, [anno]);
  const corso = corsi.find(c => c.codice === corsoCod);
  const ammessi = corso?.giorni_ammessi || [];
  const giornoFisso = corso?.giorno_fisso;
  const hasFixedDay = giornoFisso != null || ammessi.length === 1;
  useEffect(() => {
    if (!corso) { setGiorni([]); return; }
    if (giornoFisso != null) setGiorni([giornoFisso]);
    else if (ammessi.length === 1) setGiorni([...ammessi]);
    else setGiorni([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corsoCod]);
  const toggleGiorno = (wd) => {
    setGiorni(g => g.includes(wd) ? g.filter(x => x !== wd) : [...g, wd].sort());
  };
  const DAY_LABELS = ["Lun", "Mar", "Mer", "Gio", "Ven"];
  const upload = async () => {
    if (!corsoCod) { setErr("Seleziona un corso"); return; }
    if (!file) { setErr("Seleziona un file .xlsx"); return; }
    if (giorni.length === 0) { setErr("Seleziona almeno un giorno"); return; }
    setBusy(true); setErr(null); setReport(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const params = { corso_codice: corsoCod, anno, giorni: giorni.join(",") };
      const r = await api.post("/mensa-matrice/extra-iscrizioni/import-xlsx", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        params,
      });
      setReport(r.data);
    } catch (e) { setErr(e.response?.data?.detail || "Errore import"); }
    finally { setBusy(false); }
  };
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} data-testid="mm-isc-import-modal"
        style={{ background: "white", width: "100%", maxWidth: 760, borderRadius: 4, overflow: "hidden", maxHeight: "92vh", display: "flex", flexDirection: "column" }}>
        <div style={{ background: "#2e7d32", color: "white", padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>📥 Import massivo iscrizioni extracurriculari</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "white", fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ padding: 18, overflowY: "auto", flex: 1 }}>
          <div style={{ fontSize: 12, color: "#555", marginBottom: 12, padding: 10, background: "#f1f8e9", border: "1px solid #c5e1a5" }}>
            Carica un file <b>.xlsx</b> con una sola colonna di codici alunno (header: <code>CodiceAlunno</code>, <code>Cod</code> o simili).
            Se un alunno è <b>già iscritto</b> al corso, il giorno selezionato viene <b>aggiunto</b> alla sua iscrizione (merge).
            Altrimenti viene creata una nuova iscrizione con i giorni indicati.
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 4 }}>CORSO *</label>
            <select value={corsoCod} onChange={e => setCorsoCod(e.target.value)} data-testid="mm-isc-import-corso"
              style={{ width: "100%", padding: 8, border: "1px solid #ccc", fontSize: 13 }}>
              <option value="">— Seleziona corso —</option>
              {corsi.map(c => (
                <option key={c.codice} value={c.codice}>
                  {c.codice} · {c.descrizione} · giorni ammessi: {(c.giorni_ammessi || []).map(g => DAY_LABELS[g]).join("/")}
                </option>
              ))}
            </select>
          </div>

          {corso && (
            <div style={{ marginBottom: 12, padding: 10, background: hasFixedDay ? "#e3f2fd" : "#fff8e1", border: `1px solid ${hasFixedDay ? "#90caf9" : "#ffd54f"}` }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#555", marginBottom: 6 }}>
                GIORNI DA APPLICARE *
                {hasFixedDay && <span style={{ color: "#1565c0", marginLeft: 8, fontWeight: 400 }}>(fisso per questo corso)</span>}
                {!hasFixedDay && <span style={{ color: "#ef6c00", marginLeft: 8, fontWeight: 400 }}>(seleziona 1 o più giorni tra quelli ammessi)</span>}
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                {DAY_LABELS.map((lbl, wd) => {
                  const allowed = ammessi.includes(wd);
                  const checked = giorni.includes(wd);
                  return (
                    <label key={wd} data-testid={`mm-isc-import-day-${wd}`}
                      style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 10px",
                        background: checked ? "#2e7d32" : allowed ? "white" : "#f0f0f0",
                        color: checked ? "white" : allowed ? "#333" : "#ccc",
                        border: `1px solid ${checked ? "#2e7d32" : allowed ? "#ccc" : "#e0e0e0"}`,
                        fontSize: 12, fontWeight: 700,
                        cursor: (!allowed || hasFixedDay) ? "not-allowed" : "pointer",
                        opacity: !allowed ? 0.5 : 1 }}>
                      <input type="checkbox" checked={checked} disabled={!allowed || hasFixedDay}
                        onChange={() => allowed && !hasFixedDay && toggleGiorno(wd)}
                        style={{ cursor: (!allowed || hasFixedDay) ? "not-allowed" : "pointer" }} />
                      {lbl}
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 4 }}>FILE EXCEL *</label>
            <input type="file" accept=".xlsx" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="mm-isc-import-file"
              style={{ width: "100%", padding: 8, border: "1px solid #ccc", fontSize: 12 }} />
          </div>
          {err && <div style={{ padding: 10, background: "#fff0f0", border: "1px solid #fcc", color: "#c00", fontSize: 12, marginBottom: 10 }}>{err}</div>}
          {report && (
            <div style={{ padding: 12, background: "#e8f5e9", border: "1px solid #a5d6a7", fontSize: 12, marginTop: 10 }}>
              <b>Esito</b> — giorni applicati: <b>{(report.corso?.giorni_applicati || []).map(g => DAY_LABELS[g]).join(", ")}</b><br />
              Nuove iscrizioni: <b style={{ color: "#2e7d32" }}>{report.inserite}</b> ·
              Giorno aggiunto a iscrizioni esistenti: <b style={{ color: "#1565c0" }}>{report.giorno_aggiunto}</b> ·
              Già presenti (invariate): <b>{report.gia_presenti}</b>
              {report.in_lista_attesa > 0 && <> · lista attesa: <b style={{ color: "#ef6c00" }}>{report.in_lista_attesa}</b></>}
              <div style={{ marginTop: 4, color: "#666" }}>Righe totali nel file: {report.totale_righe}</div>
              {(report.alunni_non_trovati || []).length > 0 && (
                <div style={{ marginTop: 8, color: "#c62828" }}>
                  ⚠ Alunni non trovati ({report.alunni_non_trovati.length}): {report.alunni_non_trovati.slice(0, 20).join(", ")}{report.alunni_non_trovati.length > 20 ? " ..." : ""}
                </div>
              )}
              {(report.settore_incompatibile || []).length > 0 && (
                <div style={{ marginTop: 8, color: "#c62828" }}>
                  ⚠ Settore incompatibile ({report.settore_incompatibile.length}): {report.settore_incompatibile.slice(0, 10).map(x => `${x.cod}(${x.settore_alu})`).join(", ")}
                </div>
              )}
              {(report.errori || []).length > 0 && (
                <div style={{ marginTop: 8, color: "#c62828" }}>
                  ⚠ Altri errori ({report.errori.length}): {report.errori.slice(0, 5).map(e => `${e.cod}: ${e.err}`).join(" · ")}
                </div>
              )}
            </div>
          )}
        </div>
        <div style={{ padding: "10px 18px", borderTop: "1px solid #eee", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={{ padding: "7px 14px", background: "white", color: "#666", border: "1px solid #ccc", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            {report ? "Chiudi" : "Annulla"}
          </button>
          {!report && (
            <button onClick={upload} disabled={busy || !corsoCod || !file || giorni.length === 0} data-testid="mm-isc-import-upload"
              style={{ padding: "7px 18px", background: "#2e7d32", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: busy ? "wait" : "pointer", opacity: (busy || !corsoCod || !file || giorni.length === 0) ? 0.6 : 1 }}>
              {busy ? "Import..." : "📥 Importa"}
            </button>
          )}
          {report && (
            <button onClick={onDone} style={{ padding: "7px 18px", background: "#2e7d32", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
              ✓ Fine — Aggiorna lista
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ImportFamigliaModal({ anno, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [report, setReport] = useState(null);
  const upload = async () => {
    if (!file) { setErr("Seleziona un file .xlsx"); return; }
    setBusy(true); setErr(null); setReport(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/mensa-matrice/famiglia-ricorrenti/import-xlsx", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        params: { anno },
      });
      setReport(r.data);
    } catch (e) { setErr(e.response?.data?.detail || "Errore import"); }
    finally { setBusy(false); }
  };
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} data-testid="mm-isc-import-fam-modal"
        style={{ background: "white", width: "100%", maxWidth: 700, borderRadius: 4, overflow: "hidden", maxHeight: "92vh", display: "flex", flexDirection: "column" }}>
        <div style={{ background: "#2e7d32", color: "white", padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>📥 Import richieste famiglie (spot)</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "white", fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ padding: 18, overflowY: "auto", flex: 1 }}>
          <div style={{ fontSize: 12, color: "#555", marginBottom: 12, padding: 10, background: "#f1f8e9", border: "1px solid #c5e1a5" }}>
            Formato atteso: <b>.xlsx</b> con colonne <code>Codice Fiscale</code> + <code>G1..G5</code> (valore 1 nelle colonne corrispondenti ai giorni prenotati; G1=Lun, G5=Ven).
            Per ogni CF con almeno 1 giorno flaggato viene creata una richiesta spot nell'anno <b>{anno}</b>.
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 4 }}>FILE EXCEL *</label>
            <input type="file" accept=".xlsx" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="mm-isc-import-fam-file"
              style={{ width: "100%", padding: 8, border: "1px solid #ccc", fontSize: 12 }} />
          </div>
          {err && <div style={{ padding: 10, background: "#fff0f0", border: "1px solid #fcc", color: "#c00", fontSize: 12, marginBottom: 10 }}>{err}</div>}
          {report && (
            <div style={{ padding: 12, background: "#e8f5e9", border: "1px solid #a5d6a7", fontSize: 12, marginTop: 10 }}>
              <b>Esito:</b> inserite <b style={{ color: "#2e7d32" }}>{report.inserite}</b> su {report.totale_righe} righe valide.
              {(report.cf_non_trovati || []).length > 0 && (
                <div style={{ marginTop: 8, color: "#c62828" }}>
                  ⚠ CF non trovati ({report.cf_non_trovati.length}): {report.cf_non_trovati.slice(0, 10).join(", ")}{report.cf_non_trovati.length > 10 ? " ..." : ""}
                </div>
              )}
            </div>
          )}
        </div>
        <div style={{ padding: "10px 18px", borderTop: "1px solid #eee", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={{ padding: "7px 14px", background: "white", color: "#666", border: "1px solid #ccc", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            {report ? "Chiudi" : "Annulla"}
          </button>
          {!report && (
            <button onClick={upload} disabled={busy || !file} data-testid="mm-isc-import-fam-upload"
              style={{ padding: "7px 18px", background: "#2e7d32", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: busy ? "wait" : "pointer", opacity: (busy || !file) ? 0.6 : 1 }}>
              {busy ? "Import..." : "📥 Importa"}
            </button>
          )}
          {report && (
            <button onClick={onDone} style={{ padding: "7px 18px", background: "#2e7d32", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
              ✓ Fine — Aggiorna lista
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function NuovaIscrizioneWizard({ kind, initialAnno, anni, onClose, onPicked, onError }) {
  const color = kind === "extra" ? "#ef6c00" : "#8e24aa";
  const [step, setStep] = useState(1);
  const [annoW, setAnnoW] = useState(initialAnno ?? (anni?.[0] ?? null));
  const [search, setSearch] = useState("");
  const [alunni, setAlunni] = useState([]);
  const [searching, setSearching] = useState(false);
  const [alu, setAlu] = useState(null); // {Cod, Cog, Nom, settore, classe, ciclo, cicloLabel}
  const [corsi, setCorsi] = useState([]);
  const [loadingCorsi, setLoadingCorsi] = useState(false);
  const [wizErr, setWizErr] = useState(null);

  // 🆕 Helper: calcola il ciclo (label breve) da settore+classe (regole di curriculum)
  // A → INFANZIA
  // E/F + n (1..5) → PRIMARIA n
  // M + n (1..3) → SECONDARIA n
  // Se manca la classe, ritorna il ciclo base ("PRIMARIA" / "SECONDARIA").
  const computeCiclo = (settore, classe) => {
    const s = (settore || "").trim().toUpperCase();
    const cs = String(classe || "").trim();
    const m = cs.match(/(\d+)/);
    const n = m ? parseInt(m[1], 10) : null;
    if (s === "A") return { code: "infanzia", label: "INFANZIA", num: null };
    if (s === "E" || s === "F") {
      if (n && n >= 1 && n <= 5) return { code: `primaria_${n}`, label: `PRIMARIA ${n}`, num: n };
      return { code: "primaria", label: "PRIMARIA", num: null };
    }
    if (s === "M") {
      if (n && n >= 1 && n <= 3) return { code: `secondaria_${n}`, label: `SECONDARIA ${n}`, num: n };
      return { code: "secondaria", label: "SECONDARIA", num: null };
    }
    return { code: "", label: "?", num: null };
  };

  // Se al momento dell'apertura del wizard `initialAnno` era null (race con caricaAnni),
  // appena `initialAnno` o `anni` diventano disponibili allineiamo `annoW`.
  useEffect(() => {
    if (annoW != null) return;
    if (initialAnno != null) { setAnnoW(initialAnno); return; }
    if (Array.isArray(anni) && anni.length > 0) setAnnoW(anni[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAnno, anni]);

  // Step 1: ricerca alunni live quando cambia search
  useEffect(() => {
    if (step !== 1 || !annoW) { setAlunni([]); return; }
    const q = search.trim();
    if (q.length < 2) { setAlunni([]); return; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/alunni", { params: { search: q, anno: annoW, limit: 25 } });
        if (!cancelled) setAlunni(r.data.data || r.data.items || []);
      } catch { if (!cancelled) setAlunni([]); }
      finally { if (!cancelled) setSearching(false); }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [step, search, annoW]);

  const confermaAlunno = async (a) => {
    setWizErr(null);
    let ciclo = computeCiclo(a.settore, a.classe);
    // 🆕 Se il calcolo settore+classe non produce un ciclo valido (settore non didattico o vuoto),
    // interroga il curriculum: potrebbe esserci un override manuale (INFANZIA/PRIMARIA/SECONDARIA)
    // che rende comunque l'iscrizione possibile. Anche in caso di errore, procediamo permissivi.
    if (!ciclo.code || ciclo.code === "primaria" || ciclo.code === "secondaria") {
      try {
        const rc = await api.get(`/scuola-curriculum/ciclo/${a.Cod}`, { params: { anno: annoW } });
        const d = rc.data || {};
        if (d.ciclo) {
          ciclo = { code: d.ciclo, label: d.label || d.ciclo.toUpperCase(), num: d.num ?? null };
          // Applica anche settore/classe effettive
          if (d.settore) a = { ...a, settore: d.settore };
          if (d.classe) a = { ...a, classe: d.classe };
        }
      } catch { /* ignore: proseguiamo con quanto abbiamo */ }
    }
    setAlu({ Cod: a.Cod, Cog: a.Cog, Nom: a.Nom, settore: a.settore, classe: a.classe, anno: annoW, ciclo: ciclo.code, cicloLabel: ciclo.label, cicloNum: ciclo.num });
    if (kind === "fam") {
      onPicked({
        _isNew: true,
        alunno_cod: a.Cod,
        cognome: a.Cog, nome: a.Nom, classe: a.classe, settore: a.settore,
        anno: annoW,
        giorni: [],
        note: "",
      });
      return;
    }
    // 🆕 Per extra: non blocchiamo più per "settore non didattico".
    // Usiamo il settore effettivo derivato dal ciclo:
    //   INFANZIA → A, PRIMARIA → E (default), SECONDARIA → M
    let settore = (a.settore || "").toUpperCase();
    if (!["A", "E", "F", "M"].includes(settore)) {
      if (ciclo.code === "infanzia") settore = "A";
      else if (ciclo.code.startsWith("primaria")) settore = "E";
      else if (ciclo.code.startsWith("secondaria")) settore = "M";
      else settore = ""; // nessun filtro settore
    }
    setLoadingCorsi(true);
    try {
      const params = { anno: annoW };
      if (settore) params.settore = settore;
      const r = await api.get("/mensa-matrice/corsi", { params });
      const items0 = r.data.items || [];
      const n = ciclo.num;
      const items = items0.filter(c => {
        const ammesse = Array.isArray(c.classi_ammesse) ? c.classi_ammesse : [];
        if (ammesse.length === 0) return true;
        if (n == null) return true;
        return ammesse.includes(n);
      });
      if (items.length === 0) {
        const m = `Nessun corso attivo per il ciclo ${ciclo.label || "(sconosciuto)"} anno ${fmtAnno(annoW)}. Verifica in 📚 Corsi le classi ammesse (di ${items0.length} corsi del settore ${settore || "?"} nessuno è aperto a questa classe).`;
        setWizErr(m); onError(m);
        setLoadingCorsi(false);
        setAlu(null);
        return;
      }
      setCorsi(items);
      setStep(2);
    } catch (e) {
      const m = e.response?.data?.detail || "Errore caricamento corsi";
      setWizErr(m); onError(m);
    } finally { setLoadingCorsi(false); }
  };

  const confermaCorso = (c) => {
    onPicked({
      _isNew: true,
      alunno_cod: alu.Cod,
      cognome: alu.Cog, nome: alu.Nom,
      classe: alu.classe, settore: (alu.settore || "").toUpperCase(),
      anno: annoW,
      corsi_disponibili: corsi,
      corso_codice: c.codice,
      giorni: c.giorno_fisso != null ? [c.giorno_fisso] : [],
    });
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 600, display: "flex", alignItems: "center", justifyContent: "center", padding: 18 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "white", width: 720, maxHeight: "92vh", display: "flex", flexDirection: "column", borderRadius: 4, overflow: "hidden" }}>
        <div style={{ background: color, color: "white", padding: "12px 18px", fontWeight: 700, fontSize: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>➕ Nuova {kind === "extra" ? "Iscrizione Extra" : "Richiesta Famiglia"} · Step {step}/{kind === "extra" ? 2 : 1}</span>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "white", fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>

        {wizErr && (
          <div data-testid="mm-wz-err"
            style={{ margin: "10px 18px 0", padding: "8px 12px", background: "#fff0f0", border: "1px solid #fcc", color: "#c00", fontSize: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>{wizErr}</span>
            <button onClick={() => setWizErr(null)} style={{ background: "none", border: "none", color: "#c00", cursor: "pointer", fontWeight: 700 }}>✕</button>
          </div>
        )}

        {/* STEP 1: Anno + Alunno */}
        {step === 1 && (
          <div style={{ padding: 18, overflowY: "auto" }}>
            <div style={{ display: "flex", gap: 10, marginBottom: 14, alignItems: "flex-end" }}>
              <div>
                <label style={{ fontSize: 10, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>ANNO SCOLASTICO</label>
                <select value={annoW ?? ""} onChange={e => setAnnoW(Number(e.target.value))}
                  data-testid="mm-wz-anno"
                  style={{ padding: 7, border: `2px solid ${color}`, fontSize: 13, fontWeight: 700, color }}>
                  {anni.map(a => <option key={a} value={a}>{fmtAnno(a)}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 10, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>CERCA ALUNNO (cognome o nome, min 2 caratteri)</label>
                <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
                  data-testid="mm-wz-search"
                  placeholder="Es. Rossi, Maria..."
                  style={{ width: "100%", padding: 8, border: "1px solid #ccc", fontSize: 13 }} />
              </div>
            </div>
            <div style={{ border: "1px solid #eee", maxHeight: 360, overflowY: "auto", background: "#fafafa" }}>
              {search.trim().length < 2 && (
                <div style={{ padding: 30, textAlign: "center", color: "#888", fontSize: 12 }}>
                  Inserisci almeno 2 caratteri per cercare.
                </div>
              )}
              {search.trim().length >= 2 && searching && (
                <div style={{ padding: 20, textAlign: "center", color: "#888", fontSize: 12 }}>Ricerca in corso…</div>
              )}
              {search.trim().length >= 2 && !searching && alunni.length === 0 && (
                <div style={{ padding: 20, textAlign: "center", color: "#888", fontSize: 12 }}>Nessun alunno trovato nell'anno {fmtAnno(annoW)}.</div>
              )}
              {!searching && alunni.map(a => {
                const sett = (a.settore || "").toUpperCase();
                const cic = computeCiclo(a.settore, a.classe);
                // 🆕 Nessun blocco: tutti gli alunni sono selezionabili. Se il ciclo non è calcolabile
                // dai raw settore/classe (es. settore tecnico o vuoto), il ciclo effettivo viene
                // recuperato al momento della conferma via `/scuola-curriculum/ciclo/{cod}`.
                const cicloLabel = cic.label && cic.label !== "?" ? cic.label : "→ verifica";
                const noCicloDaSet = !["A", "E", "F", "M"].includes(sett);
                return (
                  <div key={a.Cod} onClick={() => confermaAlunno(a)}
                    data-testid={`mm-wz-alu-${a.Cod}`}
                    style={{ padding: "8px 12px", borderBottom: "1px solid #eee", fontSize: 12,
                      cursor: "pointer",
                      display: "flex", alignItems: "center", gap: 10,
                      background: "white",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = "#fff3e0"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "white"; }}>
                    <div title={`Settore ${sett || "?"} · Classe ${a.classe || "?"}`}
                      style={{ minWidth: 120, textAlign: "center", fontWeight: 700, color: noCicloDaSet ? "#666" : color, background: noCicloDaSet ? "#f5f5f5" : "#fff3e0", padding: "4px 8px", fontSize: 11, letterSpacing: 0.3, border: noCicloDaSet ? "1px dashed #bdbdbd" : "none" }}>
                      {cicloLabel}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700 }}>{a.Cog} {a.Nom}</div>
                      <div style={{ fontSize: 10, color: "#888" }}>Cod {a.Cod}{a.CodFis ? ` · ${a.CodFis}` : ""}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* STEP 2: Corso (solo extra) */}
        {step === 2 && (
          <div style={{ padding: 18, overflowY: "auto" }}>
            <div style={{ padding: 10, background: "#fff3e0", marginBottom: 14, fontSize: 12 }}>
              Alunno: <b>{alu.Cog} {alu.Nom}</b> · Ciclo <b style={{ color: "#c62828" }}>{alu.cicloLabel || "?"}</b> · Anno <b>{fmtAnno(annoW)}</b>
              <button onClick={() => { setStep(1); setAlu(null); }} style={{ marginLeft: 10, background: "none", border: "none", color: "#1565c0", fontSize: 11, cursor: "pointer", textDecoration: "underline" }}>
                ← cambia alunno
              </button>
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#666", marginBottom: 6 }}>SELEZIONA UN CORSO ATTIVO PER {alu.cicloLabel || "IL CICLO"} ({corsi.length} disponibili)</div>
            {loadingCorsi && <div style={{ padding: 20, textAlign: "center", color: "#888" }}>Caricamento…</div>}
            <div style={{ border: "1px solid #eee", maxHeight: 420, overflowY: "auto", background: "#fafafa" }}>
              {corsi.map((c, i) => {
                const set = Array.isArray(c.settori) && c.settori.length ? c.settori.join("/") : (c.settore || "");
                const fisso = c.giorno_fisso != null ? DAYS[c.giorno_fisso] : null;
                const gg = c.giorno_fisso != null ? [c.giorno_fisso] : (c.giorni_ammessi || []);
                return (
                  <div key={c.codice} onClick={() => confermaCorso(c)}
                    data-testid={`mm-wz-corso-${i}`}
                    style={{ padding: "10px 12px", borderBottom: "1px solid #eee", cursor: "pointer", background: "white", fontSize: 12,
                      display: "grid", gridTemplateColumns: "100px 1fr 110px 160px", alignItems: "center", gap: 10 }}
                    onMouseEnter={e => e.currentTarget.style.background = "#fff3e0"}
                    onMouseLeave={e => e.currentTarget.style.background = "white"}>
                    <div style={{ fontFamily: "Consolas, monospace", fontWeight: 800, color: "#0d47a1" }}>{c.codice}</div>
                    <div>
                      <div style={{ fontWeight: 700 }}>{c.descrizione}</div>
                      <div style={{ fontSize: 10, color: "#888" }}>Settori {set}</div>
                    </div>
                    <div style={{ fontSize: 10, textAlign: "center" }}>
                      {fisso ? (
                        <span style={{ background: "#e8f5e9", color: "#2e7d32", padding: "2px 6px", fontWeight: 700, borderRadius: 2 }}>📌 {fisso}</span>
                      ) : (
                        <span style={{ color: "#666" }}>{gg.map(g => DAYS[g]).join("/") || "—"}</span>
                      )}
                    </div>
                    <div style={{ fontSize: 10, fontFamily: "Consolas, monospace", textAlign: "right" }}>
                      {Object.entries(c.prezzi || {}).sort((a,b) => Number(a[0]) - Number(b[0])).map(([n, p]) => (
                        <span key={n} style={{ marginLeft: 4, background: "#f3e5f5", color: "#4a148c", padding: "1px 5px", fontWeight: 700, borderRadius: 2 }}>
                          {n}g:€{Number(p).toFixed(0)}
                        </span>
                      ))}
                      {(!c.prezzi || Object.keys(c.prezzi).length === 0) && <span style={{ color: "#c62828" }}>prezzi non impostati</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div style={{ padding: "10px 18px", borderTop: "1px solid #eee", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 11, color: "#888" }}>
            {step === 1 ? "Step 1 · Anno e Alunno" : "Step 2 · Scegli Corso"}
          </div>
          <button onClick={onClose} style={{ padding: "6px 14px", background: "white", color: "#666", border: "1px solid #ccc", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Annulla</button>
        </div>
      </div>
    </div>
  );
}

function IscrizioneEditModal({ editing, setEditing, kind, onSave, err }) {
  const title = kind === "extra" ? "Iscrizione Extracurriculare" : "Richiesta Famiglia Spot";
  const color = kind === "extra" ? "#ef6c00" : "#8e24aa";

  // Giorni in cui l'alunno è già in mensa (per kind="fam")
  const [giorniOccupati, setGiorniOccupati] = useState([]);
  const [dettagliOcc, setDettagliOcc] = useState([]);
  const [loadingOcc, setLoadingOcc] = useState(false);
  useEffect(() => {
    if (kind !== "fam" || !editing?.alunno_cod || !editing?.anno) return;
    let cancelled = false;
    setLoadingOcc(true);
    (async () => {
      try {
        const r = await api.get(`/mensa-matrice/giorni-occupati/${editing.alunno_cod}`, { params: { anno: editing.anno } });
        if (!cancelled) {
          // Escludi i giorni di QUESTA iscrizione (se in modifica) dai "già occupati",
          // altrimenti non sarebbero deselezionabili
          const myGiorni = editing._isNew ? [] : (editing.giorni || []);
          const occ = (r.data.giorni || []).filter(g => !myGiorni.includes(g));
          setGiorniOccupati(occ);
          setDettagliOcc((r.data.details || []).filter(d => !myGiorni.includes(d.wd)));
        }
      } catch { if (!cancelled) { setGiorniOccupati([]); setDettagliOcc([]); } }
      finally { if (!cancelled) setLoadingOcc(false); }
    })();
    return () => { cancelled = true; };
  }, [kind, editing?.alunno_cod, editing?.anno]);

  // Default dal = oggi per nuove richieste famiglia
  useEffect(() => {
    if (kind === "fam" && editing?._isNew && !editing?.dal) {
      const t = new Date();
      const dal = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
      setEditing(e => e && e._isNew ? { ...e, dal } : e);
    }
  }, [kind, editing?._isNew]); // eslint-disable-line

  const corsoAttivo = useMemo(() => {
    if (kind !== "extra" || !editing?.corsi_disponibili) return null;
    return editing.corsi_disponibili.find(c => c.codice === editing.corso_codice) || editing.corsi_disponibili[0];
  }, [editing, kind]);
  const settoreAlu = (editing?.settore || "").toUpperCase();
  const giorniCorso = corsoAttivo?.giorni_ammessi || (kind === "extra" ? [] : [0, 1, 2, 3, 4]);
  const giornoFisso = corsoAttivo?.giorno_fisso;
  const isAllDays = giorniCorso.length >= 5 && [0, 1, 2, 3, 4].every(d => giorniCorso.includes(d));
  const giorniConsentitiPerSettore = (kind === "extra" && ["E", "F"].includes(settoreAlu) && !isAllDays)
    ? giorniCorso.filter(g => g === 1 || g === 3)
    : giorniCorso;
  const giorniAmmessi = giornoFisso != null ? [giornoFisso] : giorniConsentitiPerSettore;

  const efRule = kind === "extra" && ["E", "F"].includes(settoreAlu) && giornoFisso == null && !isAllDays;

  const toggleDay = (wd) => {
    if (kind === "fam" && giorniOccupati.includes(wd)) return; // blocca giorni già in mensa
    if (kind === "extra" && !giorniAmmessi.includes(wd)) return;
    if (giornoFisso != null) {
      setEditing({ ...editing, giorni: [giornoFisso] });
      return;
    }
    let next = editing.giorni.includes(wd) ? editing.giorni.filter(d => d !== wd) : [...editing.giorni, wd].sort();
    if (efRule) {
      if (next.length > 2) return;
      if (next.length === 2 && !(next[0] === 1 && next[1] === 3)) {
        next = [1, 3];
      }
    }
    setEditing({ ...editing, giorni: next });
  };
  const setCorso = (codice) => {
    const corso = editing.corsi_disponibili.find(c => c.codice === codice);
    let validList = corso ? (corso.giorno_fisso != null ? [corso.giorno_fisso] : corso.giorni_ammessi) : [];
    const corsoIsAll = corso && corso.giorni_ammessi && corso.giorni_ammessi.length >= 5 && [0, 1, 2, 3, 4].every(d => corso.giorni_ammessi.includes(d));
    if (corso && ["E", "F"].includes(settoreAlu) && corso.giorno_fisso == null && !corsoIsAll) {
      validList = validList.filter(g => g === 1 || g === 3);
    }
    const valid = editing.giorni.filter(g => validList.includes(g));
    // Se giorno fisso → preseleziona
    const nuoviGiorni = corso?.giorno_fisso != null ? [corso.giorno_fisso] : valid;
    setEditing({ ...editing, corso_codice: codice, giorni: nuoviGiorni });
  };

  // Prezzo stimato (extracurriculare) in base ai giorni selezionati.
  // Cerca prima nelle `tariffe` (nuovo schema, array di varianti), poi nel dict `prezzi` (legacy).
  const prezzoStimato = useMemo(() => {
    if (kind !== "extra" || !corsoAttivo) return null;
    const n = editing.giorni.length;
    if (n === 0) return null;
    // 1) Cerca tariffa con nr_giorni == n
    const tariffe = Array.isArray(corsoAttivo.tariffe) ? corsoAttivo.tariffe : [];
    const t = tariffe.find(x => Number(x.nr_giorni || 0) === n);
    if (t && t.importo != null) return Number(t.importo);
    // 2) Se c'è una sola tariffa "FISSA" e il corso ha 1 solo nr_giorni, usa quella
    if (tariffe.length === 1 && tariffe[0].importo != null) return Number(tariffe[0].importo);
    // 3) Fallback legacy `prezzi`
    const p = corsoAttivo.prezzi?.[String(n)];
    return p != null ? Number(p) : null;
  }, [corsoAttivo, editing.giorni, kind]);

  return (
    <div onClick={() => setEditing(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 600, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "white", width: 580, borderRadius: 4, overflow: "hidden" }}>
        <div style={{ background: color, color: "white", padding: "12px 18px", fontWeight: 700, fontSize: 14, display: "flex", justifyContent: "space-between" }}>
          {editing._isNew ? `➕ Nuova ${title}` : `✏ Modifica ${title}`}
          <button onClick={() => setEditing(null)} style={{ background: "none", border: "none", color: "white", fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>
        {err && (
          <div data-testid="mm-isc-err" style={{ margin: "10px 18px 0", padding: "8px 12px", background: "#fff0f0", border: "1px solid #fcc", color: "#c00", fontSize: 12, fontWeight: 600 }}>
            ⚠ {err}
          </div>
        )}
        <div style={{ padding: 18 }}>
          <div style={{ fontSize: 12, marginBottom: 14 }}>
            Alunno: <b>{editing.cognome} {editing.nome}</b>{editing.classe ? <> · classe {editing.classe}</> : null}{editing.settore ? <> · settore {editing.settore}</> : null}
          </div>
          {kind === "extra" && editing._isNew && (
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 10, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>CORSO *</label>
              <select value={editing.corso_codice || ""} onChange={e => setCorso(e.target.value)}
                data-testid="mm-isc-corso"
                style={{ width: "100%", padding: 7, border: "1px solid #ccc", fontSize: 12 }}>
                {editing.corsi_disponibili.map(c => {
                  const set = Array.isArray(c.settori) && c.settori.length ? c.settori.join("/") : (c.settore || "");
                  const fisso = c.giorno_fisso != null ? ` · ${DAYS[c.giorno_fisso]}` : "";
                  return <option key={c.codice} value={c.codice}>{c.codice} — {c.descrizione} [{set}{fisso}]</option>;
                })}
              </select>
            </div>
          )}
          {kind === "extra" && !editing._isNew && (
            <div style={{ marginBottom: 14, padding: "6px 10px", background: "#fff3e0", fontSize: 12 }}>
              Corso: <b>{editing.corso_codice}</b> — {editing.corso_descrizione}
            </div>
          )}
          {kind === "extra" && giornoFisso != null && (
            <div style={{ marginBottom: 10, padding: "6px 10px", background: "#e8f5e9", fontSize: 11, color: "#2e7d32", fontWeight: 600 }}>
              ℹ Corso a giorno fisso: <b>{DAY_NAMES[giornoFisso]}</b>
            </div>
          )}
          {efRule && (
            <div style={{ marginBottom: 10, padding: "6px 10px", background: "#fff8e1", fontSize: 11, color: "#7a5a00" }}>
              ℹ Settori E/F: puoi scegliere <b>1 giorno</b> (MAR o GIO) oppure <b>2 giorni</b> (MAR+GIO).
            </div>
          )}
          {kind === "fam" && (
            <div style={{ marginBottom: 10, padding: "6px 10px", background: loadingOcc ? "#eceff1" : (dettagliOcc.length ? "#e3f2fd" : "#e8f5e9"), fontSize: 11, color: "#0d47a1" }}>
              {loadingOcc ? "⏳ Carico giorni già in mensa..." : (
                dettagliOcc.length ? (
                  <>📋 Giorni <b>già in mensa</b> (non selezionabili):{" "}
                    {dettagliOcc.map((d, i) => (
                      <span key={i} style={{ marginRight: 8 }}>{d.fonte === "scuola" ? "🏫" : "✨"} {d.giorni.map(g => DAYS[g]).join(", ")} <span style={{ color: "#666" }}>({d.descrizione})</span></span>
                    ))}
                  </>
                ) : "ℹ Nessun giorno di mensa già previsto per questo alunno."
              )}
            </div>
          )}
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 10, fontWeight: 700, color: "#666", display: "block", marginBottom: 5 }}>
              GIORNI {kind === "extra" && <span style={{ color: "#ef6c00", fontWeight: 400 }}>(solo quelli ammessi dal corso e dal settore)</span>}
              {kind === "fam" && <span style={{ color: "#8e24aa", fontWeight: 400 }}>(seleziona giorni <b>aggiuntivi</b> — quelli già in mensa sono bloccati)</span>}
            </label>
            <div style={{ display: "flex", gap: 6 }}>
              {DAYS.map((d, wd) => {
                const ammesso = kind === "extra" ? giorniAmmessi.includes(wd) : !giorniOccupati.includes(wd);
                const attivo = editing.giorni.includes(wd);
                const occupato = kind === "fam" && giorniOccupati.includes(wd);
                const blocked = !ammesso;
                return (
                  <button key={wd} onClick={() => toggleDay(wd)} disabled={blocked}
                    data-testid={`mm-isc-day-${wd}`}
                    title={occupato ? "L'alunno è già in mensa questo giorno" : ""}
                    style={{
                      position: "relative",
                      padding: "8px 14px",
                      border: `2px solid ${attivo ? color : occupato ? "#1565c0" : ammesso ? "#ccc" : "#eee"}`,
                      background: attivo ? color : occupato ? "#e3f2fd" : "white",
                      color: attivo ? "white" : occupato ? "#1565c0" : ammesso ? "#666" : "#ccc",
                      cursor: blocked ? "not-allowed" : "pointer",
                      fontSize: 12, fontWeight: 700, flex: 1, opacity: blocked && !occupato ? 0.4 : 1,
                    }}>
                    {d}
                    {occupato && <div style={{ fontSize: 8, fontWeight: 400, marginTop: 2 }}>già in mensa</div>}
                  </button>
                );
              })}
            </div>
            {kind === "extra" && prezzoStimato != null && editing.giorni.length > 0 && (
              <div style={{ marginTop: 8, padding: "6px 10px", background: "#f3e5f5", fontSize: 12, fontWeight: 700, color: "#4a148c", display: "flex", justifyContent: "space-between" }}>
                <span>Prezzo stimato ({editing.giorni.length} {editing.giorni.length === 1 ? "giorno" : "giorni"}):</span>
                <span style={{ fontFamily: "Consolas, monospace" }}>€ {prezzoStimato.toFixed(2)}</span>
              </div>
            )}
            {kind === "extra" && corsoAttivo && editing.giorni.length > 0 && prezzoStimato == null && (
              <div style={{ marginTop: 8, padding: "6px 10px", background: "#ffebee", fontSize: 11, color: "#c62828" }}>
                ⚠ Prezzo non definito per {editing.giorni.length} giorni su questo corso.
              </div>
            )}
          </div>
          {kind === "fam" && (
            <>
              <div style={{ marginBottom: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <label style={{ fontSize: 10, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>DAL *</label>
                  <input type="date" value={editing.dal || ""} onChange={e => setEditing({ ...editing, dal: e.target.value })}
                    data-testid="mm-isc-dal"
                    style={{ width: "100%", padding: 7, border: "1px solid #ccc", fontSize: 12 }} />
                  <div style={{ fontSize: 9, color: "#888", marginTop: 2 }}>Data inizio richiesta spot</div>
                </div>
                <div>
                  <label style={{ fontSize: 10, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>DATA RECESSO</label>
                  <input type="date" value={editing.data_recesso || ""} onChange={e => setEditing({ ...editing, data_recesso: e.target.value })}
                    data-testid="mm-isc-recesso-modal"
                    style={{ width: "100%", padding: 7, border: editing.data_recesso ? "1px solid #c62828" : "1px solid #ccc", background: editing.data_recesso ? "#ffebee" : "white", fontSize: 12 }} />
                  <div style={{ fontSize: 9, color: "#888", marginTop: 2 }}>Lascia vuoto per "fino a revoca"</div>
                </div>
              </div>
              <div>
                <label style={{ fontSize: 10, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>NOTE</label>
                <textarea value={editing.note || ""} onChange={e => setEditing({ ...editing, note: e.target.value })}
                  rows={2} maxLength={300}
                  style={{ width: "100%", padding: 7, border: "1px solid #ccc", fontSize: 12, resize: "vertical" }} />
              </div>
            </>
          )}
        </div>
        <div style={{ padding: "10px 18px", borderTop: "1px solid #eee", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <div style={{ fontSize: 11, color: editing.giorni.length === 0 ? "#c62828" : "#888", fontWeight: editing.giorni.length === 0 ? 700 : 400 }}>
            {editing.giorni.length === 0 ? "⚠ Seleziona almeno un giorno" : `${editing.giorni.length} ${editing.giorni.length === 1 ? "giorno selezionato" : "giorni selezionati"}`}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => setEditing(null)} style={{ padding: "6px 14px", background: "white", color: "#666", border: "1px solid #ccc", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Annulla</button>
            <button onClick={onSave} data-testid="mm-isc-save" disabled={editing.giorni.length === 0}
              style={{ padding: "6px 18px", background: editing.giorni.length === 0 ? "#ccc" : color, color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: editing.giorni.length === 0 ? "not-allowed" : "pointer", opacity: editing.giorni.length === 0 ? 0.7 : 1 }}>💾 Salva</button>
          </div>
        </div>
      </div>
    </div>
  );
}


function FilterField({ label, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <span style={{ fontSize: 9, fontWeight: 800, color: "#bdbdbd", letterSpacing: 0.8 }}>{label}</span>
      {children}
    </div>
  );
}

// Apre una nuova finestra con la tabella formattata per la stampa.
// Mostra solo le righe filtrate visibili a video.
function stampaTabella({ items, kind, filterCorso, filterGiorno, filterGruppo, filterAttesa, anno, corsiLookup }) {
  const fmtAnno = (a) => a ? `${a}/${String(a + 1).slice(-2)}` : "—";
  const giorniLbl = ["Lun", "Mar", "Mer", "Gio", "Ven"];
  const corsoSel = corsiLookup?.find(c => c.codice === filterCorso);
  const giornoLbl = filterGiorno !== "" ? ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì"][Number(filterGiorno)] : null;
  const gruppoLbl = filterGruppo ? (filterGruppo === "1" ? "Gruppo 1 (cl. 1-2)" : "Gruppo 2 (cl. 3-4-5)") : null;
  const attesaLbl = filterAttesa === "attivi" ? "Solo attivi" : filterAttesa === "attesa" ? "Solo lista d'attesa" : null;

  // 🆕 Giorno/i da evidenziare: filtro giorno > giorni del corso selezionato > unione dei giorni delle iscrizioni.
  const giorniFull = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì"];
  const highlightDays = new Set();
  if (filterGiorno !== "") {
    highlightDays.add(Number(filterGiorno));
  } else if (corsoSel && ((Array.isArray(corsoSel.giorni_ammessi) && corsoSel.giorni_ammessi.length) || corsoSel.giorno_fisso != null)) {
    if (corsoSel.giorno_fisso != null) highlightDays.add(Number(corsoSel.giorno_fisso));
    (corsoSel.giorni_ammessi || []).forEach(d => highlightDays.add(Number(d)));
  } else {
    items.forEach(it => (it.giorni || []).forEach(d => highlightDays.add(Number(d))));
  }
  const highlightLbl = [...highlightDays].sort((a, b) => a - b).map(d => giorniFull[d]).filter(Boolean).join(", ");

  const filtriAttivi = [
    `Anno: ${fmtAnno(anno)}`,
    corsoSel ? `Corso: ${corsoSel.codice} — ${corsoSel.descrizione}` : null,
    giornoLbl ? `Giorno: ${giornoLbl}` : (highlightLbl ? `Giorno: ${highlightLbl}` : null),
    gruppoLbl ? `Gruppo: ${gruppoLbl}` : null,
    attesaLbl ? `Stato: ${attesaLbl}` : null,
  ].filter(Boolean);

  const esc = (s) => String(s ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  const statoLbl = {
    confirmed: "APPROVATA", waiting: "ATTESA", rejected: "RIFIUTATA",
    pending: "IN ATTESA", recesso: "RECESSO", chiusa: "CHIUSA",
    accettata_mail_inviata: "IN ATTESA PAG.", in_attesa_pagamento: "IN ATTESA PAG.", confermata_genitore: "CONFERMATA",
    scaduta: "SCADUTA", pagata_da_verificare: "DA VERIFICARE", pagata: "PAGATA",
  };

  let _num = 0;
  const rows = items.map((it) => {
    const isRecesso = !!(it.data_recesso || "").trim();
    const stato = it.stato_iscrizione || (isRecesso ? "recesso" : (it.in_lista_attesa ? "waiting" : "pending"));
    const numCell = isRecesso ? "" : String(++_num);
    const giorni = [0, 1, 2, 3, 4].map(wd => {
      const hl = highlightDays.has(wd) ? " hl" : "";
      return it.giorni?.includes(wd) ? `<td class="d${hl}">✓</td>` : `<td class="d${hl}">—</td>`;
    }).join("");
    return `<tr class="${isRecesso ? "r" : ""}">
      <td class="n">${numCell}</td>
      <td><b>${esc(it.cognome || "")} ${esc(it.nome || "")}</b><br/><span class="cod">${esc(it.alunno_cod || "")}</span></td>
      <td class="c">${esc(it.settore || "—")}</td>
      <td class="c">${esc(it.classe || "—")}</td>
      <td>${esc(it.corso_codice || "")} — ${esc(it.corso_descrizione || it.attivita || "")}</td>
      <td class="c">${esc(statoLbl[stato] || stato)}</td>
      ${giorni}
      <td class="tel">${esc(it.telefono || "")}</td>
      <td class="em">${esc(it.email || "")}</td>
    </tr>`;
  }).join("");

  const html = `<!doctype html>
<html lang="it"><head><meta charset="utf-8"/>
<title>Iscrizioni Extra · ${esc(filtriAttivi[1] || `Anno ${fmtAnno(anno)}`)}</title>
<style>
  @page { size: A4 landscape; margin: 10mm; }
  body { font-family: -apple-system, "Helvetica Neue", Arial, sans-serif; font-size: 9pt; color: #222; margin: 0; padding: 0; }
  h1 { font-size: 14pt; margin: 0 0 6px; color: #ef6c00; }
  .sub { font-size: 9pt; color: #555; margin-bottom: 10px; }
  .filt { display: inline-block; padding: 2px 8px; background: #212121; color: white; border-radius: 3px; font-weight: 700; margin-right: 5px; font-size: 8pt; }
  table { width: 100%; border-collapse: collapse; }
  thead { background: #ef6c00; color: white; }
  th, td { padding: 4px 5px; border: 1px solid #ccc; font-size: 8pt; vertical-align: top; }
  th { font-weight: 700; text-align: center; }
  td.c { text-align: center; }
  td.n { text-align: center; color: #888; }
  td.d { text-align: center; width: 22px; font-weight: 700; }
  td.d.hl { background: #fff3cd; }
  th.hlh { background: #ad1457 !important; }
  td.tel, td.em { font-family: Consolas, "Liberation Mono", monospace; font-size: 7.5pt; }
  td .cod { color: #888; font-size: 7pt; }
  tr.r { color: #c62828; text-decoration: line-through; }
  .footer { margin-top: 10px; font-size: 7pt; color: #888; text-align: right; }
  @media print {
    button { display: none; }
    .nopr { display: none; }
  }
</style>
</head>
<body>
  <div class="nopr" style="background:#212121;color:white;padding:8px 14px;display:flex;justify-content:space-between;align-items:center;margin-bottom:10px">
    <span style="font-weight:700">Anteprima stampa — clicca "Stampa" o premi Ctrl+P</span>
    <button onclick="window.print()" style="background:#ef6c00;color:white;border:none;padding:6px 16px;font-weight:700;cursor:pointer">🖨 Stampa</button>
  </div>
  <h1>🎨 Iscrizioni Extracurriculari · ${esc(corsoSel ? `${corsoSel.codice} ${corsoSel.descrizione}` : `Anno ${fmtAnno(anno)}`)}</h1>
  <div class="sub">
    ${filtriAttivi.map(f => `<span class="filt">${esc(f)}</span>`).join("")}
    <span style="margin-left:8px;color:#666">· <b>${items.length}</b> iscrizioni</span>
  </div>
  <table>
    <thead><tr>
      <th style="width:22px">#</th>
      <th>Alunno</th>
      <th style="width:30px">Set.</th>
      <th style="width:45px">Classe</th>
      <th>Corso</th>
      <th style="width:75px">Stato</th>
      ${giorniLbl.map((g, idx) => `<th class="${highlightDays.has(idx) ? "hlh" : ""}" style="width:22px">${g}</th>`).join("")}
      <th style="width:90px">Telefono</th>
      <th>Email</th>
    </tr></thead>
    <tbody>${rows || `<tr><td colspan="13" style="text-align:center;padding:20px;color:#888">Nessuna iscrizione</td></tr>`}</tbody>
  </table>
  <div class="footer">canossaHUB · Generato il ${new Date().toLocaleString("it-IT")} · ${items.length} righe</div>
</body></html>`;

  const w = openPrintWindow(html, { autoPrint: true });
  if (!w) {
    alert("Impossibile aprire la finestra di stampa. Disattiva il blocco popup per questo sito.");
    return;
  }
}

